/**
 * Translation bundles for English, Italian, and Arabic.
 *
 * Keys are organized by domain. Missing keys fall back to English.
 */

import type { Locale } from '@dentai/shared';
import { professionalPricing, professionalAnnualSavingLabel } from './commercial-pricing.ts';

interface TranslationBundle {
  [key: string]: string | TranslationBundle;
}

const en: TranslationBundle = {
  app: {
    name: 'DentAI',
    tagline: 'AI-powered dental clinic management',
  },
  landing: {
    navFeatures: 'Features',
    navHowItWorks: 'How It Works',
    navForClinics: 'For Clinics',
    ctaGetStarted: 'Get Started',
    ctaLogin: 'Log In',
    ctaTryDentai: 'Try DentAI',
    heroTitle: 'The intelligent management system for the modern dental practice',
    heroSubtitle: 'DentAI brings patients, appointments, staff, and clinic operations together in one AI-ready platform.',
    heroCtaPrimary: 'Try DentAI',
    heroCtaSecondary: 'Log In',
    heroBadge: 'AI-ready platform',
    trustTitle: 'A platform designed for modern dental practices',
    trustSubtitle: 'Everything your clinic needs, in one workspace.',
    trustItem1: 'Multi-clinic architecture with organization-level isolation',
    trustItem2: 'Role-based access control for every team member',
    trustItem3: 'Patient management with consent tracking',
    trustItem4: 'Appointment scheduling with status workflows',
    trustItem5: 'Unified conversation inbox across channels',
    trustItem6: 'Multilingual interface: Italian, English, and Arabic',
    featuresTitle: 'Everything your clinic needs',
    featuresSubtitle: 'A complete platform for managing your dental practice.',
    feature1Title: 'Patient Management',
    feature1Desc: 'Create, search, and manage patient records with consent tracking and soft-delete protection.',
    feature2Title: 'Smart Appointments',
    feature2Desc: 'Book, confirm, cancel, and monitor appointments with dentist and appointment-type management.',
    feature3Title: 'Clinic Dashboard',
    feature3Desc: "Real-time metrics: today’s appointments, patient count, dentist activity, and recent actions.",
    feature4Title: 'Staff and Roles',
    feature4Desc: 'Manage staff with role-based access, suspension, reactivation, and security protections.',
    feature5Title: 'Conversations',
    feature5Desc: 'Unified inbox for patient communication with AI handoff indicators and staff messages.',
    feature6Title: 'AI-Ready Architecture',
    feature6Desc: 'Designed to support future AI workflows, knowledge-base integration, and intelligent automation.',
    howTitle: 'How It Works',
    howStep1Title: 'Create your clinic',
    howStep1Desc: 'Register your organization and clinic in minutes. No setup fees, no installation.',
    howStep2Title: 'Organize patients and staff',
    howStep2Desc: 'Add dentists, appointment types, patients, and staff with the right roles and permissions.',
    howStep3Title: 'Manage from one workspace',
    howStep3Desc: 'Dashboard, appointments, conversations, and settings all in one place, accessible from any device.',
    clinicOpsTitle: 'One unified operating platform for your clinic',
    clinicOpsSubtitle: 'DentAI connects every part of your clinic.',
    clinicOpsPatients: 'Patients',
    clinicOpsAppointments: 'Appointments',
    clinicOpsStaff: 'Staff',
    clinicOpsConversations: 'Conversations',
    clinicOpsDashboard: 'Dashboard',
    aiTitle: 'Built for the next generation of dental care',
    aiSubtitle: 'DentAI’s architecture is designed to support intelligent workflows, future AI-assisted communication, and clinic automation.',
    aiPoint1: 'Knowledge-base integration framework',
    aiPoint2: 'Conversation handoff between AI and staff',
    aiPoint3: 'Security guardrails for input and output',
    aiPoint4: 'Audit logging for all AI actions',
    aiDisclaimer: 'AI features are under active development. The current implementation uses deterministic responses, not a production LLM.',
    multilingualTitle: 'Speak your language',
    multilingualSubtitle: 'DentAI supports three languages with full RTL support.',
    langItalian: 'Italian',
    langEnglish: 'English',
    langArabic: 'Arabic',
    rtlNote: 'Full right-to-left support for Arabic',
    finalCtaTitle: 'Bring your dental practice into the future',
    finalCtaSubtitle: 'Start managing your clinic with DentAI today.',
    finalCtaPrimary: 'Get Started',
    finalCtaSecondary: 'Log In',
    footerProduct: 'Product',
    footerFeatures: 'Features',
    footerHowItWorks: 'How It Works',
    footerAccount: 'Account',
    footerLogin: 'Log In',
    footerRegister: 'Get Started',
    footerLegal: 'Legal',
    footerPrivacy: 'Privacy Policy',
    footerTerms: 'Terms of Service',
    footerRights: 'All rights reserved.',
    footerComingSoon: 'Coming soon',
    dashMockTitle: 'Clinic Dashboard',
    dashMockPatients: 'Patients',
    dashMockToday: 'Today',
    dashMockPending: 'Pending',
    dashMockConfirmed: 'Confirmed',
  },

  nav: {
    dashboard: 'Dashboard',    overview: 'Overview',
    communications: 'Communications',
    automations: 'Automations',
    reports: 'Reports',
    appointments: 'Appointments',
    patients: 'Patients',
    conversations: 'Conversations',
    staff: 'Staff',
    settings: 'Settings',
    logout: 'Logout',
    receptionist: 'Receptionist',
    providers: 'Providers',
    appointmentTypes: 'Appt Types',
    privacy: 'Privacy',
    notifications: 'Notifications',
    knowledgeBase: 'Knowledge Base',
    aiConfig: 'AI settings',
    whatsappAgent: 'WhatsApp Agent',
    analytics: 'Analytics',
    auditLogs: 'Audit Logs',
    revenue: 'Revenue',
    communication: 'Communication Review',
    pmsImport: 'PMS Import',
    careJourneys: 'Care Journeys',
    menu: 'Open navigation',
    closeNavigation: 'Close navigation',
    language: 'Language',
    operations: 'Operations',
    growth: 'Growth',
    knowledgeCommunication: 'Knowledge & communication',
    administration: 'Administration',    primary: 'Workspace',
    workspace: 'More workspace',
    admin: 'Clinic settings',
  },
  auth: {
    login: 'Login',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    passwordHint: 'Password must be at least 12 characters',
    fullName: 'Full name',
    organizationName: 'Organization name (optional)',
    clinicName: 'Clinic name (optional)',
    clinicTimezone: 'Clinic timezone',
    plan: 'Account plan',
    freePlan: 'Free — core clinic management',
    trialPlan: '14-day trial — full Professional access',
    loginButton: 'Sign In',
    registerButton: 'Create Account',
    forgotPassword: 'Forgot password?',
    resetRequestTitle: 'Request a password reset',
    resetRequestSubtitle: 'Enter your work email. For security, the response is the same whether or not an account exists.',
    resetRequestButton: 'Request reset',
    resetRequestSuccess: 'Your request has been received. If you do not already have a secure reset token, contact your clinic administrator.',
    resetRequestError: 'We could not process the request. Please try again shortly.',
    resetTokenTitle: 'Reset your password',
    resetTokenSubtitle: 'Use the secure reset token provided through your approved clinic process.',
    resetToken: 'Reset token',
    resetTokenHint: 'Enter the complete token exactly as provided.',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordMismatch: 'The passwords do not match.',
    resetPasswordButton: 'Reset password',
    resetPasswordSuccess: 'Your password has been reset. You can now sign in.',
    resetPasswordError: 'We could not reset the password. Check the token and try again.',
    returnToLogin: 'Return to login',
    loginTitle: 'Welcome back',
    loginSubtitle: 'Sign in to keep your clinic operations moving.',
    registerTitle: 'Create your clinic workspace',
    registerSubtitle: 'Set up the essentials now. You can refine your clinic details later.',
    newToDentai: 'New to DentAI?',
    alreadyHaveAccount: 'Already have an account?',
  },
  common: {
    loading: 'Loading…',
    error: 'Something went wrong',
    retry: 'Try again',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    confirm: 'Confirm',
    search: 'Search',
    noResults: 'No results found',
    create: 'Create',
    edit: 'Edit',
    close: 'Close',
    actions: 'Actions',
    name: 'Name',
    status: 'Status',
    all: 'All',
  },
  health: {
    status: 'Status',
    version: 'Version',
    uptime: 'Uptime',
    ok: 'All systems operational',
    degraded: 'Degraded',
    down: 'Down',
  },
  patient: {
    title: 'Patients',
    searchPlaceholder: 'Search by name or email…',
    empty: 'No patients yet',
    emptyMessage: 'Create your first patient to get started.',
    createNew: 'New Patient',
    editPatient: 'Edit Patient',
    deletePatient: 'Delete Patient',
    deleteConfirm: 'Are you sure you want to delete this patient?',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    dateOfBirth: 'Date of Birth',
    notes: 'Notes',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    blocked: 'Blocked',
    created: 'Created',
    updated: 'Updated',
    deleted: 'Deleted successfully',
    saved: 'Saved successfully',
    createError: 'Failed to create patient',
    updateError: 'Failed to update patient',
    deleteError: 'Failed to delete patient',
    loadError: 'Failed to load patients',
    totalCount: '{count} patients',
  },
  appointment: {
    title: 'Appointments',
    empty: 'No appointments yet',
    emptyMessage: 'Book your first appointment to get started.',
    createNew: 'New Appointment',
    startTime: 'Start Time',
    endTime: 'End Time',
    patient: 'Patient',
    provider: 'Provider',
    type: 'Appointment Type',
    notes: 'Notes',
    status: 'Status',
    allStatuses: 'All statuses',
    pending: 'Pending',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    completed: 'Completed',
    noShow: 'No-show',
    confirm: 'Confirm',
    cancel: 'Cancel',
    cancelReason: 'Cancellation reason',
    cancelReasonRequired: 'Reason is required',
    complete: 'Mark completed',
    noShowAction: 'Mark no-show',
    createError: 'Failed to create appointment',
    requiredFields: 'Complete all required fields before saving.',
    loadError: 'Failed to load appointments',
    statusError: 'Failed to update appointment status',
    selectPatient: 'Select a patient',
    selectProvider: 'Select a provider',
    selectType: 'Select a type',
    noTypes: 'No appointment types available',
    noProviders: 'No providers available',
    noPatients: 'No patients available',
    reschedule: 'Reschedule',
    rescheduleTitle: 'Reschedule appointment',
    rescheduleDescription: 'Choose a new start and end time for this appointment.',
    rescheduleTimeRangeError: 'The end time must be after the start time.',
    rescheduleConflict: 'This provider already has an appointment during that time. Choose another time.',
    rescheduleError: 'We could not reschedule this appointment. Please try again.',
    confirmationQueueEyebrow: 'Front-desk readiness',
    confirmationQueueTitle: 'Appointment confirmation queue',
    confirmationQueueWindow: 'Pending future appointments in the next {days} days.',
    confirmationQueueError: 'Could not load confirmation work',
    noConfirmationsNeeded: 'No pending appointments need confirmation in this window.',
    needsAttentionToday: 'Needs attention today',
    upcomingConfirmations: 'Upcoming',
    confirmationStatus: 'Confirmation priority',
    attendanceEyebrow: 'Front-desk attendance',
    attendanceTitle: "Today's attendance",
    attendanceDescription: 'Record arrivals and resolve appointments after their scheduled time.',
    attendanceCheckedIn: 'Checked in',
    attendancePastUnresolved: 'Past unresolved',
    attendanceStatus: 'Attendance status',
    attendanceLoadError: 'Could not load today’s attendance',
    noAttendanceNeeded: 'No confirmed appointments need attendance action today.',
    checkIn: 'Check in',
    checkedIn: 'Checked in',
    awaitingArrival: 'Awaiting arrival',
    pastUnresolved: 'Past — resolve now',
  },
  dashboard: {
    title: 'Dashboard',
    overviewTitle: 'Your clinic, clearly organised for today',
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    newAppointment: 'New appointment',
    setupAlertTitle: 'Complete your clinic setup',
    setupAlertBody: 'Add providers and appointment types so the agenda can reflect your real day.',
    completeSetup: 'Complete setup',
    today: 'Today',
    openCalendar: 'Open calendar',
    availableSpaces: 'Pending confirmations',
    cancellationsNoShows: 'Cancellations / no-shows',
    todayAgenda: "Today's agenda",
    viewAll: 'View all',
    toManage: 'To manage',
    toManageDescription: 'The few items that deserve attention first.',
    communicationAwaiting: 'Communication awaiting review',
    nothingToManage: 'Nothing urgent to manage right now.',
    opportunities: 'Opportunities',
    noAccess: 'This area is not available for your role.',
    noOpportunities: 'No open opportunities.',
    opportunityNeedsReview: 'Opportunity needs review',
    communication: 'Communications',
    communicationDraft: 'Draft ready for review',
    noCommunications: 'No communications need attention.',
    draftStatus: 'Draft',
    lunaTitle: 'Luna, at your side',
    lunaBody: 'Use Luna for an operational suggestion. The team keeps control of every action.',
    openLuna: 'Open Luna',
    totalPatients: 'Total Patients',
    activeProviders: 'Active Providers',
    todaysAppointments: "Today's Appointments",
    clinicOverview: 'Clinic overview',
    pendingAppointments: 'Pending Appointments',
    confirmedAppointments: 'Confirmed Appointments',
    completedAppointments: 'Completed Appointments',
    time: 'Time',
    patient: 'Patient',
    provider: 'Provider',
    type: 'Type',
    status: 'Status',
    noAppointmentsToday: 'No appointments scheduled for today',
    noRecentActivity: 'No recent activity',
    recentActivity: 'Recent Activity',
    loadError: 'Failed to load dashboard',
    welcomeTitle: 'A clear view of your clinic today',
    welcomeSubtitle: 'Keep operations moving, then turn missed moments into meaningful follow-up.',
    nextStepTitle: 'Start with the essentials',
    nextStepBody: 'Add your clinical team and appointment types before scheduling your first visit.',
    setupProviders: 'Set up providers',
    setupAppointments: 'Set up appointment types',
    activityRecorded: 'An activity was recorded',
    activityLogin: 'A team member signed in',
    activityRegister: 'A workspace was created',
    activityRefresh: 'Session refreshed',
    activityRevenueScan: 'Revenue opportunities reviewed',
  },
  automation: {
    eyebrow: 'Operations', title: 'Automations', subtitle: 'Clear operational rules governed by consent, authorization, and DentAI controls.',
    controlTitle: 'Human control preserved.', controlBody: 'An active rule never bypasses consent, approval, or delivery gates.', denied: 'Only authorized clinic managers can manage automations.',
    loading: 'Loading automations', loadError: 'Unable to load automations.', empty: 'No automations configured', emptyBody: 'Automations prepare tasks and drafts under clinic rules. Staff approval remains required where configured. Ask a clinic administrator to set up a rule.',
    active: 'Active', paused: 'Paused', staffApproval: 'Staff approval', required: 'Required', governed: 'According to applicable policy and gates',
    cooldown: 'Minimum interval', none: 'None', pauseHint: 'Pausing prevents new rule executions.', pause: 'Pause', activate: 'Activate', updateError: 'Unable to update the rule. No settings were changed.',
    no_show_recovery: 'No-show recovery', cancelled_recovery: 'Cancellation recovery', reactivation: 'Patient reactivation', recall: 'Hygiene recall', review_request: 'Review request',
    appointment_no_show: 'When an appointment becomes a no-show', appointment_cancelled: 'When an appointment is cancelled', patient_inactive: 'When a patient becomes inactive', hygiene_recall_due: 'When a recall is due', appointment_completed: 'When an appointment is completed',
  },
  verification: {
    verifying: 'Verifying…', verified: 'Email verified', alreadyVerified: 'Email already verified', expired: 'Link expired', invalid: 'Invalid link', waiting: 'Check your email',
    successBody: 'You can now sign in to DentAI Italia.', expiredBody: 'Request a new verification link.', invalidBody: 'The link is invalid or has already been used.', waitingBody: 'Open the link sent to your email address. Check your spam folder too.', verifyingBody: 'We are securely verifying the link.',
    destination: 'Destination', resend: 'Send verification email again', resendIn: 'Send again in {seconds}s', resendSuccess: 'If verification is required, you will receive a new link.', resendError: 'The email could not be sent. Please try again shortly.', login: 'Go to sign in',
  },
  lunaPage: {
    eyebrow: 'Operational assistance', title: 'Luna, beside your team', subtitle: 'Luna assists in supported contexts while deterministic DentAI rules remain authoritative.',
    growth: 'Growth', growthBody: 'Open an eligible opportunity, request Luna’s analysis, and review the reasoning before a governed action.', growthAction: 'View opportunities',
    communications: 'Communications', communicationsBody: 'Use the supervised copilot in supported conversations. Every draft remains subject to consent, approval, and delivery controls.', communicationsAction: 'Open conversations',
    limits: 'Limits and responsibilities', limit1: 'Luna proposes; the team verifies and decides.', limit2: 'This page does not send communications or change appointments automatically.', limit3: 'Deterministic guardrails, consent, and DentAI permissions remain authoritative.',
    admin: 'AI administration', adminBody: 'Technical settings are separate from operational assistance.', adminAction: 'Open configuration',
  },
  billingReturn: { checkout: 'Professional checkout', verified: 'Subscription verified', verifying: 'Payment verification in progress', loading: 'Verifying subscription', activeBody: 'Professional status was confirmed by the server.', pendingBody: 'Stripe is reporting the status to the server. This page does not grant Professional access.', dashboard: 'Go to dashboard', plan: 'Check plan', cancelled: 'Checkout cancelled', cancelledBody: 'Your account and clinic remain active. This return confirms no payment or Professional access.', retry: 'Try again from Settings' },
  conversation: {
    title: 'Conversations',
    active: 'Active',
    escalated: 'Escalated',
    closed: 'Closed',
    empty: 'No conversations',
    selectConversation: 'Select a conversation',
    noMessages: 'No messages yet',
    typeMessage: 'Type a message…',
    send: 'Send',
    close: 'Close',
    escalate: 'Escalate',
    assignToMe: 'Assign to me',
    aiActive: 'AI active',
    loadError: 'Failed to load conversations',
    createInternal: 'Create internal Copilot conversation',
    createInternalButton: 'Create conversation',
    internalSubject: 'Internal Copilot review',
    selectPatient: 'Select an existing patient',
    patientLoadError: 'Unable to load patients for conversation creation',
    inbox: 'Patient inbox', unread: 'Unread', awaitingReview: 'Awaiting review', replied: 'Replied', waitingPatient: 'Waiting for patient',
    resolved: 'Resolved', inbound: 'Inbound', outbound: 'Outbound', lunaGenerated: 'Generated by Luna', humanGenerated: 'Written by staff',
    editReply: 'Edit reply', approveAndSend: 'Approve and send email', manualReply: 'Manual reply', backToInbox: 'Back to inbox',
    requiresReview: 'Requires review', lunaReplyReview: 'Luna reply · review required', providerFailure: 'Email provider failure',
    lunaFailure: 'Luna unavailable — human review required', unknownSender: 'Unknown sender', ambiguousCorrelation: 'Ambiguous conversation',
    highRiskReview: 'High-risk content — human review required', automaticPolicy: 'Automatic policy', manualPolicy: 'Manual approval policy',
    channelEmail: 'Email', channelWhatsapp: 'WhatsApp', channelSms: 'SMS', channelWebchat: 'Web chat',
inboxFilter: 'Inbox view',
    filterAll: 'All conversations',
    filterNeedsAttention: 'Needs staff attention',
    filterAgentHandled: 'Agent-handled',
    filterStaffHandled: 'Staff-handled',
    filterBookingRelated: 'Booking-related',
    filterUnresolved: 'Unresolved',
    filterOptedOut: 'Opted out or blocked',
    filterPaused: 'Paused',
    filterStaffOwned: 'Staff-owned',
    filterCallbackDue: 'Callback due',
    filterWaitlist: 'Waitlist',
    filterKnowledgeGap: 'Knowledge gap',
    filterVoiceMessages: 'Voice messages',
    filterShared: 'Shared handling',
    filterRecallRecovery: 'Recall or recovery',
    filterEscalated: 'Escalated',
    languageFilter: 'Conversation language',
    allLanguages: 'All languages',
    patientSearch: 'Search patient',
  },
  provider: {
    title: 'Providers',
    empty: 'No providers yet',
    emptyMessage: 'Add your first provider to start booking appointments.',
    createNew: 'New Provider',
    edit: 'Edit Provider',
    name: 'Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    type: 'Type',
    dentist: 'Dentist',
    hygienist: 'Hygienist',
    email: 'Email',
    phone: 'Phone',
    notes: 'Notes',
    active: 'Active',
    inactive: 'Inactive',
    deactivate: 'Deactivate',
    loadError: 'Failed to load providers',
    workingHours: 'Working hours',
    workingHoursHint: 'Set one weekly time range for each day. Uncheck Off to activate a day.',
    workingHoursAfterCreate: 'Save the provider first, then edit the provider to set working hours.',
    off: 'Off',
    startTime: 'Start',
    endTime: 'End',
    scheduleLoadError: 'Failed to load working hours',
  },
  appointmentType: {
    title: 'Appointment Types',
    empty: 'No appointment types yet',
    emptyMessage: 'Create your first appointment type to start booking.',
    createNew: 'New Type',
    edit: 'Edit Type',
    name: 'Name',
    description: 'Description',
    duration: 'Duration',
    durationHint: 'Duration in minutes (1-480)',
    price: 'Price',
    currency: 'Currency',
    priceHint: 'Optional default value; stored safely in minor currency units.',
    priceInvalid: 'Enter a valid non-negative price with at most two decimal places.',
    minutes: 'min',
    active: 'Active',
    inactive: 'Inactive',
    deactivate: 'Deactivate',
    loadError: 'Failed to load appointment types',
  },
  staff: {
    title: 'Staff',
    empty: 'No staff members',
    email: 'Email',
    role: 'Role',
    active: 'Active',
    suspended: 'Suspended',
    inactive: 'Inactive',
    you: 'you',
    editRole: 'Edit Role',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    remove: 'Remove',
    removeConfirm: 'Are you sure you want to remove this staff member from the clinic?',
    removeError: 'Failed to remove staff member',
    loadError: 'Failed to load staff',
    permissions: 'Permissions',
    teamTitle: 'Clinic team',
    teamSubtitle: 'Invite reception staff or doctors and manage clinic access. Invitations are delivered through secure email.',
    managerOnly: 'Only clinic managers can manage staff and invitations.',
    inviteMember: 'Invite team member',
    createInvitation: 'Create invitation',
    inviteRole: 'Clinic role',
    currentMembers: 'Current members',
    member: 'Member',
    joined: 'Joined',
    noMembers: 'No clinic members found',
    invitations: 'Invitations',
    noInvitations: 'No invitations found',
    invitationStatus: 'Invitation status',
    invitationStatusLabels: { pending: 'Pending', accepted: 'Accepted', cancelled: 'Cancelled', expired: 'Expired' },
    invitationDeliveryNotice: 'Invitations are delivered through secure email. Resend if a recipient did not receive one.',
    invitationNoEmail: 'The recipient receives a secure, single-use activation link by email.',
    expires: 'Expires',
    resend: 'Request resend',
    cancelInvitation: 'Cancel invitation',
    deactivate: 'Deactivate',
    roleDerivedPermissions: 'Permissions are derived on the server from the selected clinic role.',
    confirmActionTitle: 'Confirm team action',
    confirm_resend: 'Request delivery for this invitation again? No email is sent until an approved provider is configured.',
    confirm_cancel: 'Cancel this invitation? The invitation link will stop working.',
    confirm_deactivate: 'Deactivate this clinic membership? The member will lose access to this clinic without deleting their account.',
    confirm_reactivate: 'Reactivate this clinic membership?',
    statusLabels: { active: 'Active', inactive: 'Inactive' },
    roleLabels: {
      admin: 'Administrator',
      super_admin: 'Super Admin',
      org_admin: 'Org Admin',
      clinic_admin: 'Clinic Admin',
      staff: 'Receptionist',
      provider: 'Doctor',
    },
  },
  settings: {
    title: 'Settings',
    profile: 'Profile',
    email: 'Email',
    role: 'Role',
    organizationId: 'Organization ID',
    clinicId: 'Clinic ID',
    clinicSettings: 'Clinic Settings',
    clinicName: 'Clinic Name',
    timezone: 'Timezone',
    organization: 'Organization',
    orgName: 'Organization Name',
    plan: 'Plan',
    noClinic: 'No clinic assigned',
    noOrg: 'No organization found',
    saveError: 'Failed to save',
    saved: 'Saved successfully',
    pilotTitle: 'Managed Pilot access',
    pilotDescription: 'A managed Pilot is valid for 30 days. It does not collect payments or activate external providers.',
    pilotActive: 'Pilot active',
    pilotEnds: 'Pilot ends',
    pilotGrant: 'Grant or renew 30-day Pilot',
    pilotGranting: 'Granting Pilot…',
    pilotUnavailable: 'Pilot status is unavailable.',
    pilotStatus: {
      missing: 'No active Pilot',
      expired: 'Pilot expired',
      inactive: 'Pilot inactive',
    },
  },
  privacy: {
    title: 'Privacy and GDPR',
    searchPatient: 'Search patient by name or email...',
    noPatients: 'No patients found',
    retentionPolicy: 'Retention Policy',
    patientDataRetention: 'Patient data retention',
    auditLogRetention: 'Audit log retention',
    conversationRetention: 'Conversation retention',
    gracePeriod: 'Deleted data grace period',
    days: 'days',
    dataAccess: 'Data Access (Right to Access)',
    appointments: 'Appointments',
    conversations: 'Conversations',
    messages: 'Messages',
    notifications: 'Notifications',
    exportedAt: 'Exported at',
    refreshData: 'Refresh',
    exportData: 'Export Data',
    eraseData: 'Erase Data',
    consentStatus: 'Consent Status',
    consentMarketing: 'Marketing consent',
    consentSms: 'SMS consent',
    consentEmail: 'Email consent',
    consentWhatsapp: 'WhatsApp consent',
    consentSaved: 'Consent updated successfully',
    lastUpdated: 'Last updated',
    eraseConfirmTitle: 'Confirm Data Erasure',
    eraseConfirmMessage: 'This will permanently anonymize the patient data. This action cannot be undone.',
    eraseWarning: 'WARNING: This is a destructive GDPR right-to-erasure operation. The patient record will be anonymized and cannot be recovered.',
    eraseConfirm: 'Confirm Erasure',
    error: 'Operation failed',
  },
  notifications: {
    title: 'Notifications',
    unread: 'unread',
    empty: 'No notifications',
    emptyMessage: 'You are all caught up.',
    markRead: 'Mark as read',
    markAllRead: 'Mark all as read',
    urgent: 'Urgent',
    loadError: 'Failed to load notifications',
    open: 'Open related item',
  },
  knowledgeBase: {
    title: 'Knowledge Base',
    empty: 'No knowledge base items',
    emptyMessage: 'Create your first knowledge base entry.',
    createNew: 'New Entry',
    edit: 'Edit',
    category: 'Category',
    content: 'Content',
    status: 'Status',
    version: 'Version',
    updated: 'Updated',
    submit: 'Submit for Review',
    approve: 'Approve',
    reject: 'Reject',
    archive: 'Archive',
    statusDraft: 'Draft',
    statusPendingReview: 'Pending Review',
    statusApproved: 'Approved',
    statusArchived: 'Archived',
    loadError: 'Failed to load knowledge base',
  },
  aiConfig: {
    title: 'AI Configuration',
    disclaimer: 'AI features use deterministic responses, not a production LLM. This is the configuration interface for when AI is enabled.',
    isEnabled: 'Enable AI',
    isEnabledDesc: 'Enable AI-assisted conversation handling for this clinic',
    escalationThreshold: 'Escalation Threshold',
    escalationThresholdDesc: 'Confidence below which AI escalates to staff (0-1)',
    maxTurns: 'Max AI Turns',
    maxTurnsDesc: 'Maximum AI messages before auto-escalation (1-100)',
    autoHandoff: 'Auto Handoff (minutes)',
    autoHandoffDesc: 'Inactivity timeout for auto-handoff to staff (1-1440)',
    saved: 'Configuration saved successfully',
    saveError: 'Failed to save configuration',
    autonomyPolicy: 'Luna assists in supervised mode. In automatic mode, it handles only eligible routine conversations under clinic rules.',
    patientMessaging: 'AI assistance for patient messages',
    patientMessagingSafety: 'Clinical, urgent, sensitive, unclear, or unsupported requests always remain with the team.',
    emailChannel: 'Email', whatsappChannel: 'WhatsApp', providerUnavailable: 'Provider delivery is currently unavailable; this policy does not activate the provider.',
    modeOff: 'Off', modeAssist: 'Assisted — Luna prepares, the team approves', modeAutoReply: 'Automatic — Luna replies only to eligible requests',
  },
  whatsappAgent: {
    eyebrow: 'Governed WhatsApp operations',
    title: 'WhatsApp Agent',
    subtitle: 'A clinic-configurable digital receptionist grounded in approved facts and live clinic data.',
    overview: 'Agent overview',
    connection: 'Connection',
    connected: 'Connected',
    connectionRequired: 'WhatsApp connection required',
    agentStatus: 'Agent status',
    enabled: 'Enabled',
    disabled: 'Disabled',
    autonomy: 'Autonomy',
    autonomous: 'Autonomous',
    supervised: 'Supervised',
    activationTitle: 'Activation',
    activationDescription: 'Autonomous mode is off until a clinic admin explicitly activates it.',
    disconnectedDescription: 'Connect WhatsApp before enabling autonomous mode. No external message will be sent from this page.',
    activate: 'Activate autonomous mode',
    deactivate: 'Turn off autonomy',
    fallbackLanguage: 'Fallback language',
    handoffDestination: 'Handoff destination',
    allowedActions: 'Allowed actions',
    knowledgeTitle: 'Approved clinic knowledge',
    knowledgeDescription: 'Write localized facts the agent may use. Live availability and appointments always come from DentAI data.',
    knowledgeLanguage: 'Knowledge language',
    knowledgeTitleField: 'Knowledge title',
    knowledgeContentField: 'Approved answer',
    saveDraft: 'Save draft',
    approve: 'Approve',
    noKnowledge: 'No knowledge entries in this language.',
    activityTitle: 'Agent activity',
    activityDescription: 'Recent decisions, outcomes, and escalations.',
    event: 'Agent event',
    noActivity: 'No agent activity yet.',
    loadError: 'Unable to load WhatsApp Agent.',
    onboardingTitle: 'Configure WhatsApp Agent',
    onboardingDescription: 'Complete clinic facts first, then review language, handoff, allowed actions, and the disconnected or connected WhatsApp state.',
    required: 'Required',
    recommended: 'Recommended',
    optional: 'Optional',
    unavailable: 'Not available until WhatsApp is connected',
    openAgent: 'Open agent setup',
    statusDraft: 'Draft',
    statusApproved: 'Approved',
    outcomeHandled: 'Handled',
    outcomeEscalated: 'Escalated',
    outcomeBlocked: 'Blocked',
    intentDetected: 'Intent classified',
    actionRecorded: 'Action recorded',
    policyEvaluated: 'Policy decision recorded',
    deterministicSource: 'Deterministic agent event',
    workspaceNav: 'Workspace', conversationsNav: 'Conversations', knowledgeNav: 'Knowledge', appointmentRulesNav: 'Appointment rules', revenuePlaybooksNav: 'Revenue playbooks', safetyNav: 'Safety & escalation', performanceNav: 'Performance', modeTitle: 'Operating mode', modeDescription: 'Governed autonomy keeps every action within clinic policy. Assisted mode drafts; guarded autonomy is the only execution mode.', disconnectedTruth: 'WhatsApp is disconnected. The agent may prepare work, but no external message can be sent.', waitingForStaff: 'Waiting for staff', overviewPeriod: 'All recorded agent activity; no financial value is inferred.', openInbox: 'Open conversations', conversationsHandled: 'Handled conversations', bookings: 'Validated bookings', escalated: 'Escalations', recovery: 'Recovery events', knowledgeGaps: 'Knowledge gaps', policyFailures: 'Policy failures', tasksTitle: 'Safety & staff tasks', noTasks: 'No open staff tasks.', playbooksTitle: 'Revenue playbooks', playbooksDescription: 'Playbooks are paused until a clinic admin activates an eligible, consented workflow.', playbookActive: 'Active', playbookPaused: 'Paused', approvalRequired: 'Staff approval required', noPlaybooks: 'No playbooks configured.', liveDataDescription: 'Availability, provider schedules, clinic hours, and booking validation remain authoritative DentAI data.', authoritativeAvailability: 'Never invent availability.', authoritativeBooking: 'Every booking uses appointment guardrails.',
    dailyOverview: 'Today’s operational overview',
    conversationsHandledToday: 'Handled today',
    needsStaff: 'Needs staff',
    pausedOrStaffOwned: 'Paused or staff-owned',
    unresolved: 'Unresolved',
    callbacksDue: 'Callbacks due',
    waitlistTasks: 'Waitlist tasks',
    bookingRequestsAwaitingReview: 'Booking requests to review',
    knowledgeGapsToday: 'Knowledge gaps today',
    ownership: 'Ownership',
    state: 'State',
    source: 'Source',
    noAuthoritativeSource: 'No authoritative source available — ask staff',
    confidenceHigh: 'High confidence',
    confidenceNeedsConfirmation: 'Needs confirmation',
    confidenceUnavailable: 'Unavailable',
    takeOver: 'Take over',
    releaseToAgent: 'Release to agent',
    pauseAgent: 'Pause agent',
    markResolved: 'Mark resolved',
    agentContext: 'Agent Context', conversationState: 'Conversation state', intent: 'Intent', language: 'Language', confidence: 'Confidence', escalationReason: 'Escalation reason', unknownIntent: 'Unknown', feedbackTitle: 'Staff feedback', feedbackCorrection: 'Optional correction or missing fact', feedbackCorrectionRequired: 'Add a correction before marking the answer incorrect or incomplete.', feedbackDraftNotice: 'Corrections create a review-required knowledge draft; nothing is published automatically.', feedbackHelpful: 'Helpful', feedbackIncorrect: 'Incorrect', feedbackMissing: 'Missing information', feedbackEscalate: 'Escalate',
  },
  analytics: {
    title: 'Analytics',
    loadError: 'Failed to load analytics',
    totalPatients: 'Total Patients',
    activeProviders: 'Active Providers',
    pendingAppointments: 'Pending Appointments',
    openConversations: 'Open Conversations',
    aiEscalations: 'AI Escalations',
    totalMessages: 'Total Messages',
    appointmentStatus: 'Appointments by Status',
    conversationStatus: 'Conversations by Status',
    dateRange: 'Date range',
  },
  auditLogs: {
    title: 'Audit Logs',
    empty: 'No audit logs found',
    loadError: 'Failed to load audit logs',
    filterByAction: 'Filter by action...',
    timestamp: 'Timestamp',
    actor: 'Actor',
    action: 'Action',
    resource: 'Resource',
    result: 'Result',
    resultSuccess: 'Success',
    resultFailure: 'Failure',
    resultDenied: 'Denied',
    page: 'Page',
    entries: 'entries',
    previous: 'Previous',
    next: 'Next',
  },
  revenue: {
    title: 'Growth',
    view_open: 'To manage',
    view_waiting: 'Waiting',
    view_recovered: 'Recovered',
    view_closed: 'Closed',
    view_all: 'All',
    scan: 'Scan opportunities',
    scanResult: 'Scan completed',
    created: 'created',
    skipped: 'already existed',
    loadError: 'Unable to load revenue opportunities',
    accessDenied: 'You do not have permission to access Revenue for this clinic.',
    actionError: 'Unable to complete the revenue action',
    empty: 'No revenue opportunities',
    emptyMessage: 'Run a scan to identify recovery opportunities.',
    totalOpportunities: 'Total opportunities',
    noShowRecovery: 'No-show recovery',
    cancelledRecovery: 'Cancellation recovery',
    opportunity: 'Opportunity',
    reactivation: 'Patient reactivation',
    cancelledSlotRecovery: 'Cancelled slot recovery',
    recall: 'Hygiene recall',
    openOpportunities: 'Open',
    bookedOpportunities: 'Booked',
    type: 'Type',
    patient: 'Patient',
    reason: 'Reason',
    priority: 'Priority',
    status: 'Status',
    identified: 'Identified',
    type_no_show_recovery: 'No-show recovery',
    type_cancelled_recovery: 'Cancellation recovery',
    type_reactivation: 'Patient reactivation',
    type_cancelled_slot_recovery: 'Cancelled slot recovery',
    type_recall: 'Hygiene recall',
    lastHygiene: 'Last hygiene visit',
    lastActivity: 'Last activity',
    daysOverdue: 'Days overdue',
    analyticsTitle: 'Revenue analytics',
    analyticsDescription: 'These rates reflect opportunity statuses, not monetary revenue.',
    period: 'Period',
    recoveryRate: 'Opportunity recovery rate',
    workView: 'Work view',
    openWork: 'Open Work',
    allOpportunities: 'All opportunities',
    attentionNeeded: 'Attention needed',
    openWorkDescription: 'Open opportunities are ordered by priority, then oldest first.',
    openWorkEmpty: 'No open work',
    openWorkEmptyMessage: 'There are no identified or contacted opportunities that need action.',
    conversionRate: 'Conversion rate',
    contactRate: 'Contact rate',
    resolutionRate: 'Resolution rate',
    allTime: 'All time',
    today: 'Today',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    priority_low: 'Low',
    priority_normal: 'Normal',
    priority_high: 'High',
    priority_urgent: 'Urgent',
    status_identified: 'Identified',
    status_contacted: 'Contacted',
    status_booked: 'Booked',
    status_completed: 'Completed',
    status_dismissed: 'Dismissed',
    status_expired: 'Expired',
    actionContacted: 'Mark contacted',
    actionBooked: 'Mark booked',
    actionCompleted: 'Mark completed',
    actionDismiss: 'Dismiss',
    details: 'Open opportunity',
    workflowTitle: 'Opportunity workflow',
    workflowDescription: 'Assign ownership, plan the next step, and record operational follow-up.',
    opportunityContext: 'Opportunity context',
    manualReviewOnly: 'Manual staff workflow only. Preparing or approving a draft does not send a message.',
    analyzeWithLuna: 'Analyze with Luna',
    aiRecommendationTitle: 'Luna recommendation',
    aiProvider: 'AI provider',
    aiAction: 'Recommended action',
    aiConfidence: 'Confidence',
    aiPurpose: 'Communication purpose',
    aiChannel: 'Suggested channel',
    aiReason: 'Reason',
    aiNextStep: 'Suggested next step',
    aiReviewOnly: 'Recommendation only — staff review is required before any action or communication.',
    assignee: 'Assignee',
    unassigned: 'Unassigned',
    assignedStaff: 'Assigned staff #{id}',
    nextAction: 'Next action',
    nextAction_review: 'Review opportunity',
    nextAction_contact: 'Contact patient',
    nextAction_follow_up: 'Follow up',
    nextAction_confirm_booking: 'Confirm booking',
    nextAction_complete_follow_up: 'Complete follow-up',
    dueAt: 'Due date',
    dueAtHint: 'Optional. Times are saved with timezone.',
    overdue: 'Overdue',
    bookingAttribution: 'Booking attribution',
    bookingNotAttributed: 'No appointment linked',
    linkAppointmentFirst: 'Link appointment first',
    linkAppointmentFirstHint: 'Link the actual patient appointment before marking this recovery as booked.',
    bookingAttributed: 'Appointment linked',
    selectAppointment: 'Select an existing appointment',
    noEligibleAppointments: 'No appointments found for this patient in this clinic.',
    linkAppointment: 'Link appointment',
    bookReleasedSlot: 'Book into released slot',
    cancelledSlotBooked: 'Candidate booked into the released slot.',
    bookingAlreadyCompleted: 'This candidate was already booked.',
    cancelledSlotBookingGuardrail: 'Booking is staff-assisted and revalidates the slot, provider, patient, and conflicts on the server.',
    releasedSlot: 'Released slot',
    matchEvidence: 'Deterministic match evidence',
    activityHistory: 'Activity history',
    activity: 'Activity',
    activityType: 'Activity type',
    activity_reviewed: 'Reviewed',
    activity_contacted: 'Contacted',
    activity_follow_up: 'Follow-up',
    activity_booking_attempt: 'Booking attempt',
    activity_booked: 'Booked',
    activity_completed: 'Completed',
    activity_dismissed: 'Dismissed',
    activity_contact_attempt: 'Contact attempt',
    activityNote: 'Short operational note',
    activityNoteHint: 'Do not include clinical information or message content.',
    activityOutcome: 'Outcome',
    outcome_no_response: 'No response',
    outcome_interested: 'Interested',
    outcome_booked: 'Booked',
    outcome_not_interested: 'Not interested',
    outcome_follow_up_needed: 'Follow-up needed',
    outcome_contacted: 'Contacted',
    outcome_no_answer: 'No answer',
    outcome_patient_declined: 'Patient declined',
    outcome_patient_will_rebook: 'Patient will rebook',
    outcome_appointment_recovered: 'Appointment recovered',
    outcome_invalid_contact: 'Invalid contact',
    recoveryTracking: 'Recovery tracking',
    attemptsCount: 'Attempts',
    lastOutcome: 'Last outcome',
    lastAttempt: 'Last attempt',
    followUpDue: 'Follow-up due',
    logContactAttempt: 'Log contact attempt',
    logContactAttemptDescription: 'Record an internal recovery outcome. This does not send a message or create an appointment.',
    contactChannel: 'Contact channel',
    contactOutcome: 'Contact outcome',
    channel_manual: 'Manual',
    channel_phone: 'Phone',
    channel_email: 'Email',
    channel_whatsapp: 'WhatsApp',
    channel_sms: 'SMS',
    channel_other: 'Other',
    recoveryAttemptRecorded: 'Recovery contact attempt recorded',
    recoveryNoteHint: 'Optional operational note. Do not include clinical information or message content.',
    recoveryNextActionHint: 'Optional. Times are saved with timezone.',
    followUpQueue: 'Recovery follow-up queue',
    followUpQueueDescription: 'Prioritized recovery cases that need a staff action.',
    followUpCategory: 'Follow-up category',
    followUp_all_open_recovery: 'All open recovery',
    followUp_overdue: 'Overdue',
    followUp_due_today: 'Due today',
    followUp_upcoming: 'Upcoming',
    followUp_no_follow_up: 'No follow-up',
    followUpQueueEmpty: 'No recovery follow-ups match this category.',
    retentionQueue: 'Retention queue',
    retentionQueueDescription: 'Recall and reactivation opportunities that need a staff action.',
    retentionCategory: 'Retention category',
    retention_all: 'All retention opportunities',
    retention_recall: 'Recall',
    retention_reactivation: 'Reactivation',
    retention_overdue: 'Overdue',
    retention_due_today: 'Due today',
    retention_upcoming: 'Upcoming',
    retention_no_due_date: 'No due date',
    noRetentionOpportunities: 'No retention opportunities match this category.',
    prepareDraft: 'Prepare draft',
    prepareRecoveryDraft: 'Prepare recovery draft',
    prepareCancelledSlotOutreach: 'Prepare released-slot outreach',
    prepareRetentionDraft: 'Prepare retention draft',
    recoveryDraftReviewHint: 'This creates a governed recovery draft. It does not send a message or create an appointment.',
    cancelledSlotOutreachDescription: 'Prepare a governed draft offering this patient an earlier appointment slot that has become available. Consent, currentness, and delivery safeguards remain enforced; no message is sent.',
    effectiveCommunicationPolicy: 'Effective AI communication policy',
    staffApprovalRequired: 'Staff approval required',
    automaticCommunicationAllowed: 'Automatic AI communication allowed',
    draftPrepared: 'Draft prepared',
    draftRequiresStaffApproval: 'This draft requires explicit staff approval before it can become delivery-eligible.',
    draftPolicyAuthorized: 'This draft is policy-authorized for future automatic handling; no human approval is recorded.',
    draftChannelStatus: 'Channel: {channel} · Locale: {locale} · Status: {status}',
    openCommunicationReview: 'Open Communication Review',
    retentionDraftReviewHint: 'This creates a recall or reactivation draft for human review only. It does not send a message.',
    performanceTitle: 'Revenue Performance',
    performanceSubtitle: 'Verified results and opportunities to follow up.', performanceSectionTitle: 'Growth results', performanceSectionDescription: 'Verified results and opportunities to follow up, without confusing monetary amounts and operational volumes.',
    performanceEmpty: 'There are no verified results for the selected period yet.',
    performanceRecoveredValue: 'Verified recovered value', performanceValueUnavailable: 'Value unavailable', performanceFilteredValueHint: 'Verified all-time total; not filtered by period.', performanceVerifiedValueHint: 'Only attributed appointments with a verified amount.',
    performanceRecoveredBookings: 'Recovered bookings', performanceBookingsHint: 'Bookings attributed to recovery opportunities.', performanceOpen: 'Open opportunities', performanceOpenHint: 'Identified or already contacted.', performanceClosed: 'Closed opportunities', performanceClosedHint: 'Completed, expired, or archived.', performanceRecoveryRate: 'Recovery outcome rate', performanceRecoveryRateHint: '{recovered} recovered outcomes / {total} recovery opportunities.',
    performanceDisclosure: 'Financial values represent only verified recovered outcomes. Open opportunities are operational volumes, not realized revenue.', performanceChartAria: 'Single Growth performance chart', performanceEyebrow: 'PERFORMANCE', performanceChartTitle: 'One workflow at a time', performanceChartDescription: 'The chart shows the workflow stages for “{workflow}”. Financial data remains separate and appears only when verified.', performanceSelectWorkflow: 'Select Growth workflow',
    performanceDetected: 'Opportunities detected', performanceToManage: 'To manage', performanceFunnelContacted: 'Contacted', performanceBooked: 'Booked', performanceCompleted: 'Completed', performanceVerifiedRecoveredValue: 'Verified recovered value', performanceNoVerifiedValue: 'No verified recovered value yet.', performanceCurrencyScales: 'Totals are separate by currency and are not filtered by period.', performanceWorkflowDisclosure: 'Stages are filtered by workflow; they are not a revenue forecast. An outcome appears only when recorded in the workflow.', performanceOpenAction: 'View opportunities to manage',
    type_review_request: 'Review requests',
    performanceDescription: 'A concise view of opportunity progress in the selected period.',
    performanceOpportunities: 'Opportunities',
    performanceContacted: 'Recovery contacted',
    performanceAttributedBooked: 'Recovered / booked',
    performanceBreakdown: 'Opportunity breakdown',
    performanceDismissedResolved: 'Dismissed / resolved',
    performanceNeedsAttention: 'Needs attention',
    performanceOverdueFollowUps: 'Overdue follow-ups',
    performanceNoFollowUp: 'No follow-up set',
    performanceRetentionDue: 'Retention due',
    openRecovery: 'Open recovery',
    playbookGuidance: 'Playbook guidance',
    playbookTitle: 'Recovery Playbook',
    playbookStep: 'Step {{step}}',
    playbookTiming: 'Timing',
    playbookAction_initial_contact: 'Make initial contact',
    playbookAction_follow_up: 'Follow up with patient',
    playbookAction_confirm_booking: 'Confirm booking',
    playbookAction_review: 'Review recovery',
    playbookAction_no_action: 'No further action',
    playbookStatus_not_started: 'Not started',
    playbookStatus_due: 'Due now',
    playbookStatus_overdue: 'Overdue',
    playbookStatus_scheduled: 'Scheduled',
    playbookStatus_completed: 'Completed',
    playbookTiming_today: 'Today',
    playbookTiming_schedule_short_follow_up: 'Schedule a short follow-up',
    playbookTiming_scheduled: 'Use the scheduled follow-up',
    playbookTiming_none: 'No timing required',
    playbookReason_no_contact_attempt: 'No contact attempt is recorded yet.',
    playbookReason_no_answer: 'The patient did not answer the latest attempt.',
    playbookReason_patient_will_rebook: 'Confirm that the patient completed the booking.',
    playbookReason_contacted: 'Continue the recovery follow-up.',
    playbookReason_terminal_outcome: 'This recovery is complete or closed.',
    playbookReason_workflow_review: 'Review the recovery workflow before continuing.',
    addActivity: 'Add activity',
    saveWorkflow: 'Save workflow',
    workflowSaved: 'Workflow saved',
    activityCreated: 'Activity added',
    appointmentLinked: 'Appointment linked',
    patientReference: 'Patient #{id}',
    reason_no_show_recovery: 'A patient missed a scheduled appointment.',
    reason_cancelled_recovery: 'An appointment was cancelled.',
    reason_reactivation: 'This patient may benefit from a follow-up.',
    reason_cancelled_slot_recovery: 'A future cancelled appointment released a slot for staff review.',
    reason_recall: 'A hygiene recall is due.',
  },
  kb: {
    title: 'Knowledge Base',
    createNew: 'New knowledge item',
    category: 'Category',
    content: 'Content',
    status: 'Status',
    version: 'Version',
    updated: 'Last updated',
    statusDraft: 'Draft',
    statusPendingReview: 'Pending review',
    statusApproved: 'Approved',
    statusArchived: 'Archived',
    loadError: 'Unable to load the knowledge base',
    empty: 'Your knowledge base is ready when you are',
    emptyMessage: 'Add approved clinic information so your team can keep answers consistent and useful.',
    edit: 'Edit knowledge item',
    submit: 'Submit for review',
    approve: 'Approve',
    reject: 'Reject',
    archive: 'Archive',
  },
  pmsImport: {
    title: 'Concierge PMS import',
    subtitle: 'Create a supervised operating baseline from the documented CSV v1 format. Preview first; no record is written until you confirm.',
    format: 'Accepted format: dentai_concierge_pms_csv_v1',
    selectFile: 'Select CSV file',
    preview: 'Preview & dry run',
    previewing: 'Preparing preview…',
    confirm: 'Confirm import',
    confirming: 'Importing…',
    noFile: 'Choose a CSV file before previewing.',
    sourceNote: 'The source file is used in memory only. DentAI stores no raw CSV or source identifiers.',
    previewTitle: 'Dry-run result',
    rows: 'rows',
    errors: 'Validation errors',
    warnings: 'Duplicate and linking notices',
    canConfirm: 'This exact file is valid. Confirmation will write the planned records atomically.',
    cannotConfirm: 'Correct the source file and preview again. Nothing has been written.',
    clinic: 'Clinic settings',
    patients: 'Patients',
    providers: 'Providers',
    appointmentTypes: 'Appointment types',
    appointments: 'Appointments',
    created: 'new',
    linkedExisting: 'linked existing',
    alreadyImported: 'already imported',
    updated: 'updated',
    revenueBaseline: 'No-show/cancelled appointments available for the existing Revenue scan',
    resultTitle: 'Import completed',
    idempotent: 'This source was already completed; no duplicate records were created.',
    job: 'Import job',
    baselineTitle: 'Pilot baseline snapshot',
    baselineCapturedAt: 'Captured',
    totalPatients: 'Active patients',
    activeProviders: 'Active providers',
    totalAppointments: 'Imported operating appointments',
    recoveryEligible: 'No-show/cancelled appointments',
    activationTitle: 'Pilot activation',
    activationReady: 'Baseline verified. Activate the supervised pilot when your clinic is operationally ready.',
    activationActive: 'Pilot active from this baseline.',
    activationRequired: 'Confirm activation to mark this clinic ready for the supervised pilot. This does not activate messaging, AI, billing, or external providers.',
    activate: 'Activate pilot',
    activating: 'Activating…',
    noBaseline: 'Complete a valid import before pilot activation.',
  },
  careJourneys: {
    title: 'Administrative care journeys',
    subtitle: 'Track clinic-approved administrative progress. DentAI does not diagnose, recommend treatment, or replace the clinical PMS record.',
    newJourney: 'New care journey',
    patient: 'Patient',
    summary: 'Administrative summary',
    value: 'Administrative value',
    followUp: 'Follow-up due',
    revenueOpportunity: 'Revenue opportunity ID (optional)',
    initialItems: 'Initial administrative items (one per line)',
    create: 'Create care journey',
    creating: 'Creating…',
    metrics: 'Owner metrics',
    acceptedValue: 'Accepted administrative value',
    completedValue: 'Completed administrative value',
    dueFollowUps: 'Follow-ups due',
    plannedAppointments: 'Planned appointments',
    completedAppointments: 'Completed appointments',
    noJourneys: 'No care journeys yet.',
    items: 'Administrative items',
    appointments: 'Linked appointments',
    addItem: 'Add item',
    linkAppointment: 'Link appointment',
    status: 'Status',
    updateStatus: 'Update status',
    selectPatient: 'Select a patient',
    selectAppointment: 'Select an appointment for this patient',
    noAppointments: 'No linked appointments.',
    created: 'Care journey created.',
    loadError: 'Unable to load care journeys.',
    noClinical: 'Administrative only — do not enter a diagnosis, recommendation, prescription, or clinical record.',
  },
  aiCopilot: {
    title: 'Staff-only copilot',
    subtitle: 'Uses approved clinic knowledge and the current conversation to prepare a reviewable draft. It does not send messages or book appointments.',
    purpose: 'Communication purpose',
    channel: 'Draft channel',
    locale: 'Draft language',
    variables: 'Approved-template variables (JSON)',
    staffNote: 'Optional staff instruction',
    prepare: 'Prepare supervised draft',
    preparing: 'Preparing…',
    resultTitle: 'Draft prepared for review',
    reviewRequired: 'AI-assisted draft — review required before any action or communication.',
    provider: 'AI provider',
    providerLuna: 'Luna',
    providerLocal: 'Local deterministic provider',
    noDraft: 'No draft has been prepared yet.',
  },
  legal: {
    privacyTitle: 'Privacy Policy',
    termsTitle: 'Terms of Service',
    lastUpdated: 'Last updated',
    lastUpdatedDate: '[To be set by legal team]',
    placeholderNotice: 'Placeholder Content',
    placeholderNoticeText: 'This page contains placeholder text. The actual legal content must be provided by a qualified legal or privacy professional before production use.',
    privacyIntro: 'Introduction',
    privacySection1Title: 'Data Controller',
    privacySection1Body: '[Legal content to be provided.]',
    privacySection2Title: 'Data We Collect',
    privacySection2Body: '[Legal content to be provided.]',
    privacySection3Title: 'Legal Basis for Processing',
    privacySection3Body: '[Legal content to be provided.]',
    privacySection4Title: 'Data Subject Rights',
    privacySection4Body: '[Legal content to be provided.]',
    privacySection5Title: 'Data Retention',
    privacySection5Body: '[Legal content to be provided.]',
    privacySection6Title: 'Data Sharing and Processors',
    privacySection6Body: '[Legal content to be provided.]',
    privacyContactTitle: 'Contact Us',
    privacyContactBody: '[Legal content to be provided.]',
    termsIntro: 'Introduction',
    termsSection1Title: 'Acceptance of Terms',
    termsSection1Body: '[Legal content to be provided.]',
    termsSection2Title: 'Service Description',
    termsSection2Body: '[Legal content to be provided.]',
    termsSection3Title: 'User Obligations',
    termsSection3Body: '[Legal content to be provided.]',
    termsSection4Title: 'Limitation of Liability',
    termsSection4Body: '[Legal content to be provided.]',
    termsSection5Title: 'Termination',
    termsSection5Body: '[Legal content to be provided.]',
    termsSection6Title: 'Governing Law',
    termsSection6Body: '[Legal content to be provided.]',
    termsContactTitle: 'Contact Us',
    termsContactBody: '[Legal content to be provided.]',
    backToHome: 'Back to Home',
    agreePrefix: 'I have read and agree to the',
    agreeAnd: 'and',
    privacyLinkText: 'Privacy Policy',
    termsLinkText: 'Terms of Service',
  },
};

const it: TranslationBundle = {
  app: {
    name: 'DentAI',
    tagline: 'Gestione clinica dentale con intelligenza artificiale',
  },
  landing: {
    navFeatures: 'Funzionalita',
    navHowItWorks: 'Come Funziona',
    navForClinics: 'Per Cliniche',
    ctaGetStarted: 'Inizia Ora',
    ctaLogin: 'Accedi',
    ctaTryDentai: 'Prova DentAI',
    heroTitle: 'Il gestionale intelligente per lo studio dentistico moderno',
    heroSubtitle: 'DentAI riunisce pazienti, appuntamenti, personale e operazioni della clinica in un unica piattaforma con architettura AI-ready.',
    heroCtaPrimary: 'Prova DentAI',
    heroCtaSecondary: 'Accedi',
    heroBadge: 'Piattaforma AI-ready',
    trustTitle: 'Una piattaforma progettata per gli studi dentistici moderni',
    trustSubtitle: 'Tutto cio di cui la tua clinica ha bisogno, in un unico workspace.',
    trustItem1: 'Architettura multi-clinic con isolamento a livello organizzazione',
    trustItem2: 'Controllo degli accessi basato sui ruoli per ogni membro del team',
    trustItem3: 'Gestione pazienti con tracciamento dei consensi',
    trustItem4: 'Pianificazione appuntamenti con workflow di stato',
    trustItem5: 'Inbox conversazioni unificata tra canali',
    trustItem6: 'Interfaccia multilingua: italiano, inglese, arabo',
    featuresTitle: 'Tutto cio di cui la tua clinica ha bisogno',
    featuresSubtitle: 'Una piattaforma completa per la gestione dello studio dentistico.',
    feature1Title: 'Gestione Pazienti',
    feature1Desc: 'Crea, cerca e gestisci cartelle pazienti con tracciamento consensi e protezione soft-delete.',
    feature2Title: 'Appuntamenti Smart',
    feature2Desc: 'Prenota, conferma, annulla e monitora appuntamenti con gestione dentisti e tipi di appuntamento.',
    feature3Title: 'Cruscotto Clinica',
    feature3Desc: 'Metriche in tempo reale: appuntamenti di oggi, conteggio pazienti, attivita dentisti e azioni recenti.',
    feature4Title: 'Personale e Ruoli',
    feature4Desc: 'Gestisci il personale con accessi basati sui ruoli, sospensione, riattivazione e protezioni di sicurezza.',
    feature5Title: 'Conversazioni',
    feature5Desc: 'Inbox unificata per la comunicazione con i pazienti con indicatori di passaggio AI e messaggi dello staff.',
    feature6Title: 'Architettura AI-Ready',
    feature6Desc: 'Progettata per supportare futuri workflow AI, integrazione knowledge base e automazione intelligente.',
    howTitle: 'Come Funziona',
    howStep1Title: 'Crea la tua clinica',
    howStep1Desc: 'Registra organizzazione e clinica in pochi minuti. Nessun costo di setup, nessuna installazione.',
    howStep2Title: 'Organizza pazienti e personale',
    howStep2Desc: 'Aggiungi dentisti, tipi di appuntamento, pazienti e personale con i ruoli e permessi giusti.',
    howStep3Title: 'Gestisci da un unico workspace',
    howStep3Desc: 'Cruscotto, appuntamenti, conversazioni e impostazioni tutto in un posto, accessibile da qualsiasi dispositivo.',
    clinicOpsTitle: 'Una piattaforma operativa unificata per la clinica',
    clinicOpsSubtitle: 'DentAI connette ogni parte della tua clinica.',
    clinicOpsPatients: 'Pazienti',
    clinicOpsAppointments: 'Appuntamenti',
    clinicOpsStaff: 'Personale',
    clinicOpsConversations: 'Conversazioni',
    clinicOpsDashboard: 'Cruscotto',
    aiTitle: 'Costruito per la prossima generazione di cure dentali',
    aiSubtitle: 'L architettura di DentAI e progettata per supportare workflow intelligenti e futura comunicazione assistita AI e automazione della clinica.',
    aiPoint1: 'Framework di integrazione knowledge base',
    aiPoint2: 'Passaggio conversazione tra AI e staff',
    aiPoint3: 'Guardrail di sicurezza per input e output',
    aiPoint4: 'Logging di audit per tutte le azioni AI',
    aiDisclaimer: 'Le funzionalita AI sono in sviluppo attivo. L implementazione attuale usa risposte deterministiche, non un LLM di produzione.',
    multilingualTitle: 'Parla la tua lingua',
    multilingualSubtitle: 'DentAI supporta tre lingue con pieno supporto RTL.',
    langItalian: 'Italiano',
    langEnglish: 'Inglese',
    langArabic: 'Arabo',
    rtlNote: 'Pieno supporto right-to-left per l arabo',
    finalCtaTitle: 'Porta il tuo studio dentistico nel futuro',
    finalCtaSubtitle: 'Inizia a gestire la tua clinica con DentAI oggi.',
    finalCtaPrimary: 'Inizia Ora',
    finalCtaSecondary: 'Accedi',
    footerProduct: 'Prodotto',
    footerFeatures: 'Funzionalita',
    footerHowItWorks: 'Come Funziona',
    footerAccount: 'Account',
    footerLogin: 'Accedi',
    footerRegister: 'Inizia Ora',
    footerLegal: 'Legale',
    footerPrivacy: 'Privacy Policy',
    footerTerms: 'Termini di Servizio',
    footerRights: 'Tutti i diritti riservati.',
    footerComingSoon: 'In arrivo',
    dashMockTitle: 'Cruscotto Clinica',
    dashMockPatients: 'Pazienti',
    dashMockToday: 'Oggi',
    dashMockPending: 'In attesa',
    dashMockConfirmed: 'Confermati',
  },
  nav: {
    dashboard: 'Cruscotto',    overview: 'Panoramica',
    communications: 'Comunicazioni',
    automations: 'Automazioni',
    reports: 'Report',
    appointments: 'Appuntamenti',
    patients: 'Pazienti',
    conversations: 'Conversazioni',
    staff: 'Personale',
    settings: 'Impostazioni',
    logout: 'Esci',
    receptionist: 'Receptionist',
    providers: 'Dentisti',
    appointmentTypes: 'Tipi Appuntamento',
    privacy: 'Privacy',
    notifications: 'Notifiche',
    knowledgeBase: 'Knowledge Base',
    aiConfig: 'Impostazioni AI',
    whatsappAgent: 'Agente WhatsApp',
    analytics: 'Analitica',
    auditLogs: 'Log di Audit',
    revenue: 'Ricavi',
    communication: 'Revisione comunicazioni',
    pmsImport: 'Import PMS',
    careJourneys: 'Percorsi di cura',
    menu: 'Apri navigazione',
    closeNavigation: 'Chiudi navigazione',
    language: 'Lingua',
    operations: 'Operatività',
    growth: 'Crescita',
    knowledgeCommunication: 'Conoscenza e comunicazione',
    administration: 'Amministrazione',    primary: 'Spazio principale',
    workspace: 'Altri strumenti',
    admin: 'Configurazione studio',
  },
  auth: {
    login: 'Accedi',
    register: 'Registrati',
    email: 'Email',
    password: 'Password',
    passwordHint: 'La password deve contenere almeno 12 caratteri',
    fullName: 'Nome e cognome',
    organizationName: 'Nome organizzazione (opzionale)',
    clinicName: 'Nome clinica (opzionale)',
    clinicTimezone: 'Fuso orario della clinica',
    plan: 'Piano account',
    freePlan: 'Free — gestione clinica essenziale',
    trialPlan: 'Prova di 14 giorni — accesso Professional completo',
    loginButton: 'Accedi',
    registerButton: 'Crea Account',
    forgotPassword: 'Password dimenticata?',
    resetRequestTitle: 'Richiedi il reimpostazione della password',
    resetRequestSubtitle: 'Inserisci l’email di lavoro. Per sicurezza, la risposta è la stessa anche se l’account non esiste.',
    resetRequestButton: 'Richiedi reimpostazione',
    resetRequestSuccess: 'La richiesta è stata ricevuta. Se non hai già un token sicuro di reimpostazione, contatta l’amministratore della clinica.',
    resetRequestError: 'Non è stato possibile elaborare la richiesta. Riprova tra poco.',
    resetTokenTitle: 'Reimposta la password',
    resetTokenSubtitle: 'Usa il token sicuro fornito tramite la procedura approvata della clinica.',
    resetToken: 'Token di reimpostazione',
    resetTokenHint: 'Inserisci il token completo esattamente come fornito.',
    newPassword: 'Nuova password',
    confirmPassword: 'Conferma la nuova password',
    passwordMismatch: 'Le password non coincidono.',
    resetPasswordButton: 'Reimposta password',
    resetPasswordSuccess: 'La password è stata reimpostata. Ora puoi accedere.',
    resetPasswordError: 'Non è stato possibile reimpostare la password. Controlla il token e riprova.',
    returnToLogin: 'Torna al login',
    loginTitle: 'Bentornato',
    loginSubtitle: 'Accedi per mantenere operativa la tua clinica.',
    registerTitle: 'Crea lo spazio di lavoro della tua clinica',
    registerSubtitle: 'Configura ora gli elementi essenziali. Potrai completare i dettagli della clinica in seguito.',
    newToDentai: 'Nuovo su DentAI?',
    alreadyHaveAccount: 'Hai già un account?',
  },
  common: {
    loading: 'Caricamento…',
    error: 'Si è verificato un errore',
    retry: 'Riprova',
    save: 'Salva',
    cancel: 'Annulla',
    delete: 'Elimina',
    confirm: 'Conferma',
    search: 'Cerca',
    noResults: 'Nessun risultato trovato',
    create: 'Crea',
    edit: 'Modifica',
    close: 'Chiudi',
    actions: 'Azioni',
    name: 'Nome',
    status: 'Stato',
    all: 'Tutti',
  },
  health: {
    status: 'Stato',
    version: 'Versione',
    uptime: 'Tempo di attività',
    ok: 'Tutti i sistemi operativi',
    degraded: 'Danneggiato',
    down: 'Non disponibile',
  },
  patient: {
    title: 'Pazienti',
    searchPlaceholder: 'Cerca per nome o email…',
    empty: 'Nessun paziente',
    emptyMessage: 'Crea il tuo primo paziente per iniziare.',
    createNew: 'Nuovo Paziente',
    editPatient: 'Modifica Paziente',
    deletePatient: 'Elimina Paziente',
    deleteConfirm: 'Sei sicuro di voler eliminare questo paziente?',
    firstName: 'Nome',
    lastName: 'Cognome',
    email: 'Email',
    phone: 'Telefono',
    dateOfBirth: 'Data di nascita',
    notes: 'Note',
    status: 'Stato',
    active: 'Attivo',
    inactive: 'Inattivo',
    blocked: 'Bloccato',
    created: 'Creato',
    updated: 'Aggiornato',
    deleted: 'Eliminato con successo',
    saved: 'Salvato con successo',
    createError: 'Impossibile creare il paziente',
    updateError: 'Impossibile aggiornare il paziente',
    deleteError: 'Impossibile eliminare il paziente',
    loadError: 'Impossibile caricare i pazienti',
    totalCount: '{count} pazienti',
  },
  appointment: {
    title: 'Appuntamenti',
    empty: 'Nessun appuntamento',
    emptyMessage: 'Prenota il tuo primo appuntamento per iniziare.',
    createNew: 'Nuovo Appuntamento',
    startTime: 'Ora di inizio',
    endTime: 'Ora di fine',
    patient: 'Paziente',
    provider: 'Dentista',
    type: 'Tipo di appuntamento',
    notes: 'Note',
    status: 'Stato',
    allStatuses: 'Tutti gli stati',
    pending: 'In attesa',
    confirmed: 'Confermato',
    cancelled: 'Annullato',
    completed: 'Completato',
    noShow: 'Non presentato',
    confirm: 'Conferma',
    cancel: 'Annulla',
    cancelReason: 'Motivo annullamento',
    cancelReasonRequired: 'Il motivo è obbligatorio',
    complete: 'Segna come completato',
    noShowAction: 'Segna come non presentato',
    createError: 'Impossibile creare l\'appuntamento',
    requiredFields: 'Completa tutti i campi obbligatori prima di salvare.',
    loadError: 'Impossibile caricare gli appuntamenti',
    statusError: 'Impossibile aggiornare lo stato',
    selectPatient: 'Seleziona un paziente',
    selectProvider: 'Seleziona un dentista',
    selectType: 'Seleziona un tipo',
    noTypes: 'Nessun tipo di appuntamento disponibile',
    noProviders: 'Nessun dentista disponibile',
    noPatients: 'Nessun paziente disponibile',
    reschedule: 'Riprogramma',
    rescheduleTitle: 'Riprogramma appuntamento',
    rescheduleDescription: 'Scegli un nuovo orario di inizio e fine per questo appuntamento.',
    rescheduleTimeRangeError: 'L’orario di fine deve essere successivo all’orario di inizio.',
    rescheduleConflict: 'Questo professionista ha già un appuntamento in quell’orario. Scegli un altro orario.',
    rescheduleError: 'Non è stato possibile riprogrammare questo appuntamento. Riprova.',
    confirmationQueueEyebrow: 'Preparazione reception',
    confirmationQueueTitle: 'Coda conferme appuntamenti',
    confirmationQueueWindow: 'Appuntamenti futuri in attesa nei prossimi {days} giorni.',
    confirmationQueueError: 'Impossibile caricare le conferme',
    noConfirmationsNeeded: 'Non ci sono appuntamenti in attesa da confermare in questa finestra.',
    needsAttentionToday: 'Da gestire oggi',
    upcomingConfirmations: 'In arrivo',
    confirmationStatus: 'Priorità di conferma',
    attendanceEyebrow: 'Presenze reception',
    attendanceTitle: 'Presenze di oggi',
    attendanceDescription: 'Registra gli arrivi e risolvi gli appuntamenti oltre l’orario previsto.',
    attendanceCheckedIn: 'Arrivati',
    attendancePastUnresolved: 'Scaduti da risolvere',
    attendanceStatus: 'Stato presenza',
    attendanceLoadError: 'Impossibile caricare le presenze di oggi',
    noAttendanceNeeded: 'Nessun appuntamento confermato richiede un’azione di presenza oggi.',
    checkIn: 'Registra arrivo',
    checkedIn: 'Arrivato',
    awaitingArrival: 'In attesa di arrivo',
    pastUnresolved: 'Orario superato — risolvi ora',
  },
  dashboard: {
    title: 'Cruscotto',
    overviewTitle: 'La tua clinica, organizzata per oggi',
    goodMorning: 'Buongiorno',
    goodAfternoon: 'Buon pomeriggio',
    goodEvening: 'Buonasera',
    newAppointment: 'Nuovo appuntamento',
    setupAlertTitle: 'Completa la configurazione dello studio',
    setupAlertBody: 'Aggiungi professionisti e prestazioni per riflettere la giornata reale in agenda.',
    completeSetup: 'Completa configurazione',
    today: 'Oggi',
    openCalendar: 'Apri calendario',
    availableSpaces: 'Da confermare',
    cancellationsNoShows: 'Cancellazioni / no-show',
    todayAgenda: 'Agenda di oggi',
    viewAll: 'Vedi tutto',
    toManage: 'Da gestire',
    toManageDescription: 'Le poche cose che meritano attenzione per prime.',
    communicationAwaiting: 'Comunicazione in attesa di revisione',
    nothingToManage: 'Nessuna urgenza da gestire al momento.',
    opportunities: 'Opportunità',
    noAccess: 'Questa area non è disponibile per il tuo ruolo.',
    noOpportunities: 'Nessuna opportunità aperta.',
    opportunityNeedsReview: 'Opportunità da esaminare',
    communication: 'Comunicazioni',
    communicationDraft: 'Bozza pronta per la revisione',
    noCommunications: 'Nessuna comunicazione richiede attenzione.',
    draftStatus: 'Bozza',
    lunaTitle: 'Luna, al tuo fianco',
    lunaBody: 'Chiedi a Luna un suggerimento operativo. Il team mantiene il controllo su ogni azione.',
    openLuna: 'Apri Luna',
    totalPatients: 'Pazienti totali',
    activeProviders: 'Dentisti attivi',
    todaysAppointments: 'Appuntamenti di oggi',
    clinicOverview: 'Panoramica della clinica',
    pendingAppointments: 'Appuntamenti in attesa',
    confirmedAppointments: 'Appuntamenti confermati',
    completedAppointments: 'Appuntamenti completati',
    time: 'Ora',
    patient: 'Paziente',
    provider: 'Dentista',
    type: 'Tipo',
    status: 'Stato',
    noAppointmentsToday: 'Nessun appuntamento previsto per oggi',
    noRecentActivity: 'Nessuna attività recente',
    recentActivity: 'Attività recente',
    loadError: 'Impossibile caricare il cruscotto',
    welcomeTitle: 'Una vista chiara della tua clinica, oggi',
    welcomeSubtitle: 'Mantieni operative le attività quotidiane e trasforma le occasioni mancate in follow-up utili.',
    nextStepTitle: 'Inizia dalle basi',
    nextStepBody: 'Aggiungi il team clinico e le tipologie di appuntamento prima di programmare la prima visita.',
    setupProviders: 'Configura i professionisti',
    setupAppointments: 'Configura le tipologie di appuntamento',
    activityRecorded: 'È stata registrata un’attività',
    activityLogin: 'Un membro del team ha effettuato l’accesso',
    activityRegister: 'È stato creato uno spazio di lavoro',
    activityRefresh: 'Sessione aggiornata',
    activityRevenueScan: 'Opportunità di ricavo esaminate',
  },
  automation: {
    eyebrow: 'Operatività', title: 'Automazioni', subtitle: 'Regole operative chiare, sempre soggette a consensi, autorizzazioni e controlli DentAI.',
    controlTitle: 'Controllo umano preservato.', controlBody: 'Una regola attiva non aggira consenso, approvazione o controlli di consegna.', denied: 'Solo i responsabili autorizzati della clinica possono gestire le automazioni.',
    loading: 'Caricamento automazioni', loadError: 'Impossibile caricare le automazioni.', empty: 'Nessuna automazione configurata', emptyBody: 'Le automazioni preparano attività e bozze in modo controllato. Le comunicazioni seguono le regole dello studio e richiedono l’approvazione del personale quando prevista. Rivolgiti all’amministratore dello studio per configurare una regola.',
    active: 'Attiva', paused: 'In pausa', staffApproval: 'Approvazione del personale', required: 'Richiesta', governed: 'Secondo regole e controlli applicabili',
    cooldown: 'Intervallo minimo', none: 'Nessuno', pauseHint: 'La pausa impedisce nuove esecuzioni della regola.', pause: 'Metti in pausa', activate: 'Attiva', updateError: 'Impossibile aggiornare la regola. Nessuna impostazione è stata modificata.',
    no_show_recovery: 'Recupero mancata presentazione', cancelled_recovery: 'Recupero cancellazione', reactivation: 'Riattivazione pazienti', recall: 'Richiamo igiene', review_request: 'Richiesta recensione',
    appointment_no_show: 'Quando un appuntamento diventa no-show', appointment_cancelled: 'Quando un appuntamento viene cancellato', patient_inactive: 'Quando un paziente risulta inattivo', hygiene_recall_due: 'Quando arriva la scadenza del richiamo', appointment_completed: 'Quando un appuntamento viene completato',
  },
  verification: {
    verifying: 'Verifica in corso…', verified: 'Email verificata', alreadyVerified: 'Email già verificata', expired: 'Link scaduto', invalid: 'Link non valido', waiting: 'Controlla la tua email',
    successBody: 'Ora puoi accedere a DentAI Italia.', expiredBody: 'Richiedi un nuovo link di verifica.', invalidBody: 'Il link non è valido o è già stato utilizzato.', waitingBody: 'Apri il link inviato al tuo indirizzo email. Controlla anche la cartella spam.', verifyingBody: 'Stiamo verificando il link in modo sicuro.',
    destination: 'Destinazione', resend: 'Invia di nuovo l’email', resendIn: 'Invia di nuovo tra {seconds}s', resendSuccess: 'Se l’account richiede la verifica, riceverai un nuovo link.', resendError: 'Non è stato possibile inviare l’email. Riprova tra poco.', login: 'Vai al login',
  },
  lunaPage: {
    eyebrow: 'Assistenza operativa', title: 'Luna, al fianco del team', subtitle: 'Luna aiuta nei contesti supportati mentre le regole deterministiche DentAI restano autorevoli.',
    growth: 'Crescita', growthBody: 'Apri un’opportunità idonea, chiedi l’analisi di Luna e valuta il motivo del suggerimento prima dell’azione governata.', growthAction: 'Vai alle opportunità',
    communications: 'Comunicazioni', communicationsBody: 'Usa il Copilot supervisionato nelle conversazioni supportate. Ogni bozza resta soggetta a consenso, approvazione e controlli di invio.', communicationsAction: 'Apri conversazioni',
    limits: 'Limiti e responsabilità', limit1: 'Luna propone; il team verifica e decide.', limit2: 'Questa pagina non invia comunicazioni né modifica appuntamenti automaticamente.', limit3: 'I guardrail deterministici, il consenso e i permessi DentAI restano autorevoli.',
    admin: 'Configurazione amministrativa AI', adminBody: 'Le impostazioni tecniche sono separate dall’assistenza operativa.', adminAction: 'Apri configurazione',
  },
  billingReturn: { checkout: 'Checkout Professional', verified: 'Abbonamento verificato', verifying: 'Verifica del pagamento in corso', loading: 'Verifica abbonamento', activeBody: 'Lo stato Professional è stato confermato dal server.', pendingBody: 'Stripe sta comunicando lo stato al server. Questa pagina non assegna diritti Professional.', dashboard: 'Vai alla dashboard', plan: 'Controlla il piano', cancelled: 'Checkout annullato', cancelledBody: 'Il tuo account e lo studio restano attivi. Questo ritorno non conferma pagamenti o diritti Professional.', retry: 'Riprova da Impostazioni' },
  conversation: {
    title: 'Conversazioni',
    active: 'Attiva',
    escalated: 'Inoltrata',
    closed: 'Chiusa',
    empty: 'Nessuna conversazione',
    selectConversation: 'Seleziona una conversazione',
    noMessages: 'Nessun messaggio',
    typeMessage: 'Scrivi un messaggio…',
    send: 'Invia',
    close: 'Chiudi',
    escalate: 'Inoltra',
    assignToMe: 'Assegna a me',
    aiActive: 'IA attiva',
    loadError: 'Impossibile caricare le conversazioni',
    createInternal: 'Crea conversazione interna per Copilot',
    createInternalButton: 'Crea conversazione',
    internalSubject: 'Revisione interna Copilot',
    selectPatient: 'Seleziona un paziente esistente',
    patientLoadError: 'Impossibile caricare i pazienti per creare la conversazione',
    inbox: 'Posta pazienti', unread: 'Da leggere', awaitingReview: 'In attesa di revisione', replied: 'Risposto', waitingPatient: 'In attesa del paziente',
    resolved: 'Risolta', inbound: 'In entrata', outbound: 'In uscita', lunaGenerated: 'Generata da Luna', humanGenerated: 'Scritta dal personale',
    editReply: 'Modifica risposta', approveAndSend: 'Approva e invia email', manualReply: 'Risposta manuale', backToInbox: 'Torna alla posta',
    requiresReview: 'Revisione necessaria', lunaReplyReview: 'Risposta di Luna · revisione necessaria', providerFailure: 'Errore del provider email',
    lunaFailure: 'Luna non disponibile — revisione umana necessaria', unknownSender: 'Mittente sconosciuto', ambiguousCorrelation: 'Conversazione non identificata con certezza',
    highRiskReview: 'Contenuto ad alto rischio — revisione umana necessaria', automaticPolicy: 'Regola automatica', manualPolicy: 'Approvazione manuale',
    channelEmail: 'Email', channelWhatsapp: 'WhatsApp', channelSms: 'SMS', channelWebchat: 'Chat web',
inboxFilter: 'Vista inbox',
    filterAll: 'Tutte le conversazioni',
    filterNeedsAttention: 'Richiede attenzione dello staff',
    filterAgentHandled: 'Gestite dall’agente',
    filterStaffHandled: 'Gestite dallo staff',
    filterBookingRelated: 'Relative a prenotazioni',
    filterUnresolved: 'Non risolte',
    filterOptedOut: 'Opt-out o bloccate',
    filterPaused: 'In pausa',
    filterStaffOwned: 'Dello staff',
    filterCallbackDue: 'Callback in scadenza',
    filterWaitlist: 'Lista d’attesa',
    filterKnowledgeGap: 'Lacuna di conoscenza',
    filterVoiceMessages: 'Messaggi vocali',
    filterShared: 'Gestione condivisa',
    filterRecallRecovery: 'Richiamo o recupero',
    filterEscalated: 'In escalation',
    languageFilter: 'Lingua conversazione',
    allLanguages: 'Tutte le lingue',
    patientSearch: 'Cerca paziente',
  },
  provider: {
    title: 'Dentisti',
    empty: 'Nessun dentista',
    emptyMessage: 'Aggiungi il primo dentista per iniziare a prenotare appuntamenti.',
    createNew: 'Nuovo Dentista',
    edit: 'Modifica Dentista',
    name: 'Nome',
    firstName: 'Nome',
    lastName: 'Cognome',
    type: 'Tipo',
    dentist: 'Dentista',
    hygienist: 'Igienista',
    email: 'Email',
    phone: 'Telefono',
    notes: 'Note',
    active: 'Attivo',
    inactive: 'Inattivo',
    deactivate: 'Disattiva',
    loadError: 'Impossibile caricare i dentisti',
    workingHours: 'Orari di lavoro',
    workingHoursHint: 'Imposta un intervallo settimanale per ogni giorno. Disattiva Off per attivare un giorno.',
    workingHoursAfterCreate: 'Salva prima il dentista, poi modificalo per impostare gli orari di lavoro.',
    off: 'Chiuso',
    startTime: 'Inizio',
    endTime: 'Fine',
    scheduleLoadError: 'Impossibile caricare gli orari di lavoro',
  },
  appointmentType: {
    title: 'Tipi di Appuntamento',
    empty: 'Nessun tipo di appuntamento',
    emptyMessage: 'Crea il primo tipo di appuntamento per iniziare.',
    createNew: 'Nuovo Tipo',
    edit: 'Modifica Tipo',
    name: 'Nome',
    description: 'Descrizione',
    duration: 'Durata',
    durationHint: 'Durata in minuti (1-480)',
    price: 'Prezzo',
    currency: 'Valuta',
    priceHint: 'Valore predefinito facoltativo, salvato in unità monetarie minori.',
    priceInvalid: 'Inserisci un prezzo valido non negativo con massimo due decimali.',
    minutes: 'min',
    active: 'Attivo',
    inactive: 'Inattivo',
    deactivate: 'Disattiva',
    loadError: 'Impossibile caricare i tipi di appuntamento',
  },
  staff: {
    title: 'Personale',
    empty: 'Nessun membro del personale',
    email: 'Email',
    role: 'Ruolo',
    active: 'Attivo',
    suspended: 'Sospeso',
    inactive: 'Inattivo',
    you: 'tu',
    editRole: 'Modifica ruolo',
    suspend: 'Sospendi',
    reactivate: 'Riattiva',
    remove: 'Rimuovi',
    removeConfirm: 'Sei sicuro di voler rimuovere questo membro del personale dalla clinica?',
    removeError: 'Impossibile rimuovere il membro del personale',
    loadError: 'Impossibile caricare il personale',
    permissions: 'Permessi',
    teamTitle: 'Team della clinica',
    teamSubtitle: 'Invita personale di reception o dentisti e gestisci l’accesso alla clinica. Gli inviti vengono consegnati tramite email sicura.',
    managerOnly: 'Solo i responsabili della clinica possono gestire il personale e gli inviti.',
    inviteMember: 'Invita membro del team',
    createInvitation: 'Crea invito',
    inviteRole: 'Ruolo in clinica',
    currentMembers: 'Membri attuali',
    member: 'Membro',
    joined: 'Aggiunto il',
    noMembers: 'Nessun membro della clinica',
    invitations: 'Inviti',
    noInvitations: 'Nessun invito',
    invitationStatus: 'Stato invito',
    invitationStatusLabels: { pending: 'In attesa', accepted: 'Accettato', cancelled: 'Annullato', expired: 'Scaduto' },
    invitationDeliveryNotice: 'Gli inviti vengono consegnati tramite email sicura. In caso di mancata consegna, invia di nuovo.',
    invitationNoEmail: 'Il destinatario riceve via email un link di attivazione sicuro e monouso.',
    expires: 'Scade il',
    resend: 'Richiedi nuovo invio',
    cancelInvitation: 'Annulla invito',
    deactivate: 'Disattiva',
    roleDerivedPermissions: 'I permessi sono derivati dal server in base al ruolo selezionato nella clinica.',
    confirmActionTitle: 'Conferma azione sul team',
    confirm_resend: 'Richiedere nuovamente la consegna dell’invito? Nessuna email viene inviata finché non è configurato un provider approvato.',
    confirm_cancel: 'Annullare questo invito? Il relativo link smetterà di funzionare.',
    confirm_deactivate: 'Disattivare questa appartenenza alla clinica? Il membro perderà l’accesso alla clinica senza eliminare il suo account.',
    confirm_reactivate: 'Riattivare questa appartenenza alla clinica?',
    statusLabels: { active: 'Attivo', inactive: 'Inattivo' },
    roleLabels: {
      admin: 'Amministratore',
      super_admin: 'Super Admin',
      org_admin: 'Admin Organizzazione',
      clinic_admin: 'Admin Clinica',
      staff: 'Reception',
      provider: 'Dentista',
    },
  },
  settings: {
    title: 'Impostazioni',
    profile: 'Profilo',
    email: 'Email',
    role: 'Ruolo',
    organizationId: 'ID Organizzazione',
    clinicId: 'ID Clinica',
    clinicSettings: 'Impostazioni Clinica',
    clinicName: 'Nome Clinica',
    timezone: 'Fuso orario',
    organization: 'Organizzazione',
    orgName: 'Nome Organizzazione',
    plan: 'Piano',
    noClinic: 'Nessuna clinica assegnata',
    noOrg: 'Nessuna organizzazione trovata',
    saveError: 'Impossibile salvare',
    saved: 'Salvato con successo',
    pilotTitle: 'Accesso pilota gestito',
    pilotDescription: 'Un Pilot gestito è valido per 30 giorni. Non raccoglie pagamenti né attiva provider esterni.',
    pilotActive: 'Pilot attivo',
    pilotEnds: 'Fine del Pilot',
    pilotGrant: 'Concedi o rinnova Pilot di 30 giorni',
    pilotGranting: 'Concessione Pilot…',
    pilotUnavailable: 'Stato Pilot non disponibile.',
    pilotStatus: {
      missing: 'Nessun Pilot attivo',
      expired: 'Pilot scaduto',
      inactive: 'Pilot inattivo',
    },
  },
  privacy: {
    title: 'Privacy e GDPR',
    searchPatient: 'Cerca paziente per nome o email...',
    noPatients: 'Nessun paziente trovato',
    retentionPolicy: 'Politica di Conservazione',
    patientDataRetention: 'Conservazione dati paziente',
    auditLogRetention: 'Conservazione log di audit',
    conversationRetention: 'Conservazione conversazioni',
    gracePeriod: 'Periodo di grazia dati cancellati',
    days: 'giorni',
    dataAccess: 'Accesso ai Dati (Diritto di Accesso)',
    appointments: 'Appuntamenti',
    conversations: 'Conversazioni',
    messages: 'Messaggi',
    notifications: 'Notifiche',
    exportedAt: 'Esportato il',
    refreshData: 'Aggiorna',
    exportData: 'Esporta Dati',
    eraseData: 'Cancella Dati',
    consentStatus: 'Stato dei Consensi',
    consentMarketing: 'Consenso marketing',
    consentSms: 'Consenso SMS',
    consentEmail: 'Consenso email',
    consentWhatsapp: 'Consenso WhatsApp',
    consentSaved: 'Consenso aggiornato con successo',
    lastUpdated: 'Ultimo aggiornamento',
    eraseConfirmTitle: 'Conferma Cancellazione Dati',
    eraseConfirmMessage: 'Questo anonimizzera permanentemente i dati del paziente. Questa azione non puo essere annullata.',
    eraseWarning: 'ATTENZIONE: Questa e un operazione distruttiva GDPR di diritto alla cancellazione. Il record del paziente sara anonimizzato e non potranno essere recuperato.',
    eraseConfirm: 'Conferma Cancellazione',
    error: 'Operazione fallita',
  },
  notifications: {
    title: 'Notifiche',
    unread: 'non lette',
    empty: 'Nessuna notifica',
    emptyMessage: 'Sei aggiornato.',
    markRead: 'Segna come letta',
    markAllRead: 'Segna tutte come lette',
    urgent: 'Urgente',
    loadError: 'Impossibile caricare le notifiche',
    open: 'Apri elemento collegato',
  },
  knowledgeBase: {
    title: 'Knowledge Base',
    empty: 'Nessuna voce nella knowledge base',
    emptyMessage: 'Crea la prima voce della knowledge base.',
    createNew: 'Nuova Voce',
    edit: 'Modifica',
    category: 'Categoria',
    content: 'Contenuto',
    status: 'Stato',
    version: 'Versione',
    updated: 'Aggiornato',
    submit: 'Invia per Revisione',
    approve: 'Approva',
    reject: 'Rifiuta',
    archive: 'Archivia',
    statusDraft: 'Bozza',
    statusPendingReview: 'In Revisione',
    statusApproved: 'Approvato',
    statusArchived: 'Archiviato',
    loadError: 'Impossibile caricare la knowledge base',
  },
  aiConfig: {
    title: 'Configurazione AI',
    disclaimer: 'Le funzionalita AI usano risposte deterministiche, non un LLM di produzione. Questa e l interfaccia di configurazione.',
    isEnabled: 'Abilita AI',
    isEnabledDesc: 'Abilita la gestione delle conversazioni assistita AI per questa clinica',
    escalationThreshold: 'Soglia di Escalation',
    escalationThresholdDesc: 'Confidenza sotto la quale l AI passa allo staff (0-1)',
    maxTurns: 'Turni AI Massimi',
    maxTurnsDesc: 'Messaggi AI massimi prima dell escalation automatica (1-100)',
    autoHandoff: 'Passaggio Automatico (minuti)',
    autoHandoffDesc: 'Timeout di inattivita per il passaggio automatico allo staff (1-1440)',
    saved: 'Configurazione salvata con successo',
    saveError: 'Impossibile salvare la configurazione',
    autonomyPolicy: 'Luna assiste in modalità supervisionata. In modalità automatica gestisce solo conversazioni ordinarie idonee secondo le regole dello studio.',
    patientMessaging: 'Assistenza AI ai messaggi dei pazienti',
    patientMessagingSafety: 'Le richieste cliniche, urgenti, sensibili, non chiare o non supportate vengono sempre lasciate al team.',
    emailChannel: 'Email', whatsappChannel: 'WhatsApp', providerUnavailable: 'Il canale non è attualmente operativo; questa impostazione non attiva il provider.',
    modeOff: 'Disattivata', modeAssist: 'Assistita — Luna prepara, il team approva', modeAutoReply: 'Automatica — Luna risponde solo alle richieste idonee',
  },
  whatsappAgent: {
    eyebrow: 'Operazioni WhatsApp governate',
    title: 'Agente WhatsApp',
    subtitle: 'Una receptionist digitale configurabile, basata su fatti approvati e dati operativi della clinica.',
    overview: 'Panoramica agente',
    connection: 'Connessione',
    connected: 'Connesso',
    connectionRequired: 'Connessione WhatsApp richiesta',
    agentStatus: 'Stato agente',
    enabled: 'Attivo',
    disabled: 'Disattivo',
    autonomy: 'Autonomia',
    autonomous: 'Autonomo',
    supervised: 'Supervisionato',
    activationTitle: 'Attivazione',
    activationDescription: 'La modalità autonoma resta disattivata finché un amministratore non la attiva esplicitamente.',
    disconnectedDescription: 'Collega WhatsApp prima di abilitare l’autonomia. Questa pagina non invia messaggi esterni.',
    activate: 'Attiva modalità autonoma',
    deactivate: 'Disattiva autonomia',
    fallbackLanguage: 'Lingua di fallback',
    handoffDestination: 'Destinazione passaggio',
    allowedActions: 'Azioni consentite',
    knowledgeTitle: 'Conoscenza approvata della clinica',
    knowledgeDescription: 'Inserisci fatti localizzati che l’agente può usare. Disponibilità e appuntamenti provengono sempre dai dati DentAI.',
    knowledgeLanguage: 'Lingua conoscenza',
    knowledgeTitleField: 'Titolo',
    knowledgeContentField: 'Risposta approvata',
    saveDraft: 'Salva bozza',
    approve: 'Approva',
    noKnowledge: 'Nessuna voce in questa lingua.',
    activityTitle: 'Attività agente',
    activityDescription: 'Decisioni, risultati ed escalation recenti.',
    event: 'Evento agente',
    noActivity: 'Nessuna attività agente.',
    loadError: 'Impossibile caricare l’Agente WhatsApp.',
    onboardingTitle: 'Configura l’Agente WhatsApp',
    onboardingDescription: 'Completa prima i dati della clinica, poi rivedi lingua, passaggio allo staff, azioni consentite e lo stato della connessione WhatsApp.',
    required: 'Obbligatorio',
    recommended: 'Raccomandato',
    optional: 'Opzionale',
    unavailable: 'Non disponibile finché WhatsApp non è connesso',
    openAgent: 'Apri configurazione agente',
    statusDraft: 'Bozza',
    statusApproved: 'Approvato',
    outcomeHandled: 'Gestito',
    outcomeEscalated: 'Inoltrato allo staff',
    outcomeBlocked: 'Bloccato',
    intentDetected: 'Intento classificato',
    actionRecorded: 'Azione registrata',
    policyEvaluated: 'Decisione di policy registrata',
    deterministicSource: 'Evento deterministico dell’agente',
    workspaceNav: 'Workspace', conversationsNav: 'Conversazioni', knowledgeNav: 'Conoscenza', appointmentRulesNav: 'Regole appuntamenti', revenuePlaybooksNav: 'Playbook ricavi', safetyNav: 'Sicurezza ed escalation', performanceNav: 'Prestazioni', modeTitle: 'Modalità operativa', modeDescription: 'L’autonomia governata mantiene ogni azione entro la policy della clinica. La modalità assistita prepara bozze; l’autonomia sorvegliata è l’unica modalità esecutiva.', disconnectedTruth: 'WhatsApp non è connesso. L’agente può preparare il lavoro, ma non può inviare messaggi esterni.', waitingForStaff: 'In attesa dello staff', overviewPeriod: 'Tutta l’attività registrata dell’agente; non viene inferito alcun valore economico.', openInbox: 'Apri conversazioni', conversationsHandled: 'Conversazioni gestite', bookings: 'Prenotazioni validate', escalated: 'Escalation', recovery: 'Eventi di recupero', knowledgeGaps: 'Lacune di conoscenza', policyFailures: 'Errori di policy', tasksTitle: 'Sicurezza e attività staff', noTasks: 'Nessuna attività staff aperta.', playbooksTitle: 'Playbook ricavi', playbooksDescription: 'I playbook restano in pausa finché un amministratore non attiva un flusso idoneo e basato sul consenso.', playbookActive: 'Attivo', playbookPaused: 'In pausa', approvalRequired: 'Approvazione staff richiesta', noPlaybooks: 'Nessun playbook configurato.', liveDataDescription: 'Disponibilità, agende dei professionisti, orari della clinica e validazione delle prenotazioni restano dati DentAI autorevoli.', authoritativeAvailability: 'Non inventare mai la disponibilità.', authoritativeBooking: 'Ogni prenotazione usa le regole degli appuntamenti.',
    dailyOverview: 'Panoramica operativa di oggi',
    conversationsHandledToday: 'Gestite oggi',
    needsStaff: 'Richiedono lo staff',
    pausedOrStaffOwned: 'In pausa o dello staff',
    unresolved: 'Non risolte',
    callbacksDue: 'Callback in scadenza',
    waitlistTasks: 'Attività lista d’attesa',
    bookingRequestsAwaitingReview: 'Richieste di prenotazione da rivedere',
    knowledgeGapsToday: 'Lacune di conoscenza oggi',
    ownership: 'Proprietà',
    state: 'Stato',
    source: 'Fonte',
    noAuthoritativeSource: 'Nessuna fonte autorevole — chiedere allo staff',
    confidenceHigh: 'Confidenza alta',
    confidenceNeedsConfirmation: 'Richiede conferma',
    confidenceUnavailable: 'Non disponibile',
    takeOver: 'Prendi in carico',
    releaseToAgent: 'Rilascia all’agente',
    pauseAgent: 'Metti in pausa l’agente',
    markResolved: 'Segna come risolta',
    agentContext: 'Contesto dell’agente', conversationState: 'Stato della conversazione', intent: 'Intento', language: 'Lingua', confidence: 'Confidenza', escalationReason: 'Motivo dell’escalation', unknownIntent: 'Sconosciuto', feedbackTitle: 'Feedback dello staff', feedbackCorrection: 'Correzione o informazione mancante (opzionale)', feedbackCorrectionRequired: 'Inserisci una correzione prima di segnalare una risposta errata o incompleta.', feedbackDraftNotice: 'Le correzioni creano una bozza di conoscenza da rivedere; nulla viene pubblicato automaticamente.', feedbackHelpful: 'Utile', feedbackIncorrect: 'Errata', feedbackMissing: 'Informazione mancante', feedbackEscalate: 'Escalation',
  },
  analytics: {
    title: 'Analitica',
    loadError: 'Impossibile caricare l analitica',
    totalPatients: 'Pazienti totali',
    activeProviders: 'Dentisti attivi',
    pendingAppointments: 'Appuntamenti in attesa',
    openConversations: 'Conversazioni aperte',
    aiEscalations: 'Escalation AI',
    totalMessages: 'Messaggi totali',
    appointmentStatus: 'Appuntamenti per stato',
    conversationStatus: 'Conversazioni per stato',
    dateRange: 'Intervallo date',
  },
  auditLogs: {
    title: 'Log di Audit',
    empty: 'Nessun log di audit trovato',
    loadError: 'Impossibile caricare i log di audit',
    filterByAction: 'Filtra per azione...',
    timestamp: 'Timestamp',
    actor: 'Attore',
    action: 'Azione',
    resource: 'Risorsa',
    result: 'Risultato',
    resultSuccess: 'Successo',
    resultFailure: 'Fallimento',
    resultDenied: 'Negato',
    page: 'Pagina',
    entries: 'voci',
    previous: 'Precedente',
    next: 'Successivo',
  },
  revenue: {
    title: 'Crescita',
    view_open: 'Aperte',
    view_waiting: 'In attesa',
    view_recovered: 'Recuperate',
    view_closed: 'Chiuse',
    view_all: 'Tutte',
    scan: 'Scansiona opportunità',
    scanResult: 'Scansione completata',
    created: 'create',
    skipped: 'gia esistenti',
    loadError: 'Impossibile caricare le opportunita di ricavo',
    accessDenied: 'Non hai l’autorizzazione per accedere ai ricavi di questa clinica.',
    actionError: 'Impossibile completare l’azione sui ricavi',
    empty: 'Nessuna opportunita di ricavo',
    emptyMessage: 'Esegui una scansione per identificare opportunita di recupero.',
    totalOpportunities: 'Opportunita Totali',
    noShowRecovery: 'Recupero no-show',
    cancelledRecovery: 'Recupero cancellazioni',
    opportunity: 'Opportunità',
    reactivation: 'Riattivazione',
    cancelledSlotRecovery: 'Recupero slot cancellato',
    recall: 'Richiamo Igiene',
    openOpportunities: 'Aperte',
    bookedOpportunities: 'Prenotate',
    type: 'Tipo',
    patient: 'Paziente',
    reason: 'Motivo',
    priority: 'Priorita',
    status: 'Stato',
    identified: 'Identificate',
    type_no_show_recovery: 'Recupero no-show',
    type_cancelled_recovery: 'Recupero cancellazioni',
    type_reactivation: 'Riattivazione',
    type_cancelled_slot_recovery: 'Recupero slot cancellato',
    type_recall: 'Richiamo Igiene',
    lastHygiene: 'Ultima Igiene',
    lastActivity: 'Ultima attività',
    daysOverdue: 'Giorni di Ritardo',
    analyticsTitle: 'Analisi delle opportunità',
    analyticsDescription: 'Questi tassi riflettono gli stati delle opportunità, non ricavi monetari.',
    period: 'Periodo',
    recoveryRate: 'Tasso di recupero delle opportunità',
    workView: 'Filtro opportunità',
    openWork: 'Lavoro aperto',
    allOpportunities: 'Tutte le opportunità',
    attentionNeeded: 'Richiede attenzione',
    openWorkDescription: 'Le opportunità aperte sono ordinate per priorità, poi dalla più vecchia.',
    openWorkEmpty: 'Nessun lavoro aperto',
    openWorkEmptyMessage: 'Non ci sono opportunità identificate o contattate che richiedono un’azione.',
    conversionRate: 'Tasso di Conversione',
    contactRate: 'Tasso di Contatto',
    resolutionRate: 'Tasso di Risoluzione',
    allTime: 'Tutto il Periodo',
    today: 'Oggi',
    last7Days: 'Ultimi 7 Giorni',
    last30Days: 'Ultimi 30 Giorni',
    priority_low: 'Bassa',
    priority_normal: 'Normale',
    priority_high: 'Alta',
    priority_urgent: 'Urgente',
    status_identified: 'Identificata',
    status_contacted: 'Contattato',
    status_booked: 'Prenotato',
    status_completed: 'Completato',
    status_dismissed: 'Ignorato',
    status_expired: 'Scaduto',
    actionContacted: 'Segna Contattato',
    actionBooked: 'Segna Prenotato',
    actionCompleted: 'Segna Completato',
    actionDismiss: 'Ignora',
    details: 'Apri opportunità',
    workflowTitle: 'Flusso di lavoro dell’opportunità',
    workflowDescription: 'Assegna la responsabilità, pianifica il passo successivo e registra il follow-up operativo.',
    opportunityContext: 'Contesto dell’opportunità',
    manualReviewOnly: 'Solo flusso manuale del personale. Preparare o approvare una bozza non invia messaggi.',
    analyzeWithLuna: 'Analizza con Luna',
    aiRecommendationTitle: 'Raccomandazione di Luna',
    aiProvider: 'Provider AI',
    aiAction: 'Azione consigliata',
    aiConfidence: 'Confidenza',
    aiPurpose: 'Finalità della comunicazione',
    aiChannel: 'Canale suggerito',
    aiReason: 'Motivazione',
    aiNextStep: 'Prossimo passo suggerito',
    aiReviewOnly: 'Solo raccomandazione — la revisione del personale è necessaria prima di qualsiasi azione o comunicazione.',
    assignee: 'Assegnatario',
    unassigned: 'Non assegnata',
    assignedStaff: 'Personale assegnato n. {id}',
    nextAction: 'Prossima azione',
    nextAction_review: 'Esamina l’opportunità',
    nextAction_contact: 'Contatta il paziente',
    nextAction_follow_up: 'Follow-up',
    nextAction_confirm_booking: 'Conferma la prenotazione',
    nextAction_complete_follow_up: 'Completa il follow-up',
    dueAt: 'Scadenza',
    dueAtHint: 'Facoltativa. Gli orari sono salvati con fuso orario.',
    overdue: 'Scaduta',
    bookingAttribution: 'Attribuzione della prenotazione',
    bookingNotAttributed: 'Nessun appuntamento collegato',
    linkAppointmentFirst: 'Collega prima l’appuntamento',
    linkAppointmentFirstHint: 'Collega l’appuntamento effettivo del paziente prima di contrassegnare questo recupero come prenotato.',
    bookingAttributed: 'Appuntamento collegato',
    selectAppointment: 'Seleziona un appuntamento esistente',
    noEligibleAppointments: 'Non sono presenti appuntamenti per questo paziente in questa clinica.',
    linkAppointment: 'Collega appuntamento',
    bookReleasedSlot: 'Prenota nello slot liberato',
    cancelledSlotBooked: 'Il candidato è stato prenotato nello slot liberato.',
    bookingAlreadyCompleted: 'Questo candidato è già stato prenotato.',
    cancelledSlotBookingGuardrail: 'La prenotazione è assistita dallo staff e ricontrolla slot, professionista, paziente e conflitti sul server.',
    releasedSlot: 'Slot liberato',
    matchEvidence: 'Evidenza deterministica della corrispondenza',
    activityHistory: 'Cronologia attività',
    activity: 'Attività',
    activityType: 'Tipo di attività',
    activity_reviewed: 'Esaminata',
    activity_contacted: 'Contattato',
    activity_follow_up: 'Follow-up',
    activity_booking_attempt: 'Tentativo di prenotazione',
    activity_booked: 'Prenotato',
    activity_completed: 'Completato',
    activity_dismissed: 'Ignorato',
    activity_contact_attempt: 'Tentativo di contatto',
    activityNote: 'Breve nota operativa',
    activityNoteHint: 'Non includere informazioni cliniche o contenuti dei messaggi.',
    activityOutcome: 'Esito',
    outcome_no_response: 'Nessuna risposta',
    outcome_interested: 'Interessato',
    outcome_booked: 'Prenotato',
    outcome_not_interested: 'Non interessato',
    outcome_follow_up_needed: 'Serve follow-up',
    outcome_contacted: 'Contattato',
    outcome_no_answer: 'Nessuna risposta',
    outcome_patient_declined: 'Paziente non interessato',
    outcome_patient_will_rebook: 'Il paziente prenoterà di nuovo',
    outcome_appointment_recovered: 'Appuntamento recuperato',
    outcome_invalid_contact: 'Contatto non valido',
    recoveryTracking: 'Monitoraggio recupero',
    attemptsCount: 'Tentativi',
    lastOutcome: 'Ultimo esito',
    lastAttempt: 'Ultimo tentativo',
    followUpDue: 'Follow-up in scadenza',
    logContactAttempt: 'Registra tentativo di contatto',
    logContactAttemptDescription: 'Registra un esito interno di recupero. Non invia messaggi né crea appuntamenti.',
    contactChannel: 'Canale di contatto',
    contactOutcome: 'Esito del contatto',
    channel_manual: 'Manuale',
    channel_phone: 'Telefono',
    channel_email: 'Email',
    channel_whatsapp: 'WhatsApp',
    channel_sms: 'SMS',
    channel_other: 'Altro',
    recoveryAttemptRecorded: 'Tentativo di recupero registrato',
    recoveryNoteHint: 'Nota operativa facoltativa. Non includere informazioni cliniche o contenuto dei messaggi.',
    recoveryNextActionHint: 'Facoltativo. Gli orari vengono salvati con fuso orario.',
    followUpQueue: 'Coda di follow-up recupero',
    followUpQueueDescription: 'Casi di recupero prioritari che richiedono un’azione del personale.',
    followUpCategory: 'Categoria di follow-up',
    followUp_all_open_recovery: 'Tutti i recuperi aperti',
    followUp_overdue: 'Scaduti',
    followUp_due_today: 'In scadenza oggi',
    followUp_upcoming: 'Prossimi',
    followUp_no_follow_up: 'Nessun follow-up',
    followUpQueueEmpty: 'Nessun follow-up di recupero corrisponde a questa categoria.',
    retentionQueue: 'Coda di fidelizzazione',
    retentionQueueDescription: 'Opportunità di richiamo e riattivazione che richiedono un’azione del personale.',
    retentionCategory: 'Categoria di fidelizzazione',
    retention_all: 'Tutte le opportunità di fidelizzazione',
    retention_recall: 'Richiamo',
    retention_reactivation: 'Riattivazione',
    retention_overdue: 'In ritardo',
    retention_due_today: 'In scadenza oggi',
    retention_upcoming: 'Prossime',
    retention_no_due_date: 'Senza data di scadenza',
    noRetentionOpportunities: 'Nessuna opportunità di fidelizzazione corrisponde a questa categoria.',
    prepareDraft: 'Prepara bozza',
    prepareRecoveryDraft: 'Prepara bozza di recupero',
    prepareCancelledSlotOutreach: 'Prepara proposta per slot liberato',
    prepareRetentionDraft: 'Prepara bozza di fidelizzazione',
    recoveryDraftReviewHint: 'Crea una bozza di recupero governata. Non invia messaggi né crea appuntamenti.',
    cancelledSlotOutreachDescription: 'Prepara una bozza governata per proporre a questo paziente uno slot anticipato che si è liberato. Consenso, attualità e protezioni di invio restano applicati; non viene inviato alcun messaggio.',
    effectiveCommunicationPolicy: 'Policy AI effettiva per le comunicazioni',
    staffApprovalRequired: 'Richiede approvazione del personale',
    automaticCommunicationAllowed: 'Comunicazione AI automatica consentita',
    draftPrepared: 'Bozza preparata',
    draftRequiresStaffApproval: 'Questa bozza richiede un’approvazione esplicita del personale prima di poter essere idonea all’invio.',
    draftPolicyAuthorized: 'Questa bozza è autorizzata dalla policy per una futura gestione automatica; non viene registrata alcuna approvazione umana.',
    draftChannelStatus: 'Canale: {channel} · Lingua: {locale} · Stato: {status}',
    openCommunicationReview: 'Apri revisione comunicazioni',
    retentionDraftReviewHint: 'Crea solo una bozza di richiamo o riattivazione per la revisione umana. Non invia messaggi.',
    performanceTitle: 'Performance di Crescita',
    performanceSubtitle: 'Risultati verificati e opportunità da seguire.', performanceSectionTitle: 'Risultati di crescita', performanceSectionDescription: 'Risultati verificati e opportunità da seguire, senza confondere importi e volumi.',
    performanceEmpty: 'Non ci sono ancora risultati verificati per il periodo selezionato.',
    performanceRecoveredValue: 'Valore recuperato verificato', performanceValueUnavailable: 'Valore non disponibile', performanceFilteredValueHint: 'Totale verificato di sempre; non filtrato per periodo.', performanceVerifiedValueHint: 'Solo appuntamenti attribuiti con importo verificato.',
    performanceRecoveredBookings: 'Prenotazioni recuperate', performanceBookingsHint: 'Prenotazioni attribuite alle opportunità di recupero.', performanceOpen: 'Opportunità da gestire', performanceOpenHint: 'Identificate o già contattate.', performanceClosed: 'Opportunità concluse', performanceClosedHint: 'Completate, scadute o archiviate.', performanceRecoveryRate: 'Tasso esiti di recupero', performanceRecoveryRateHint: '{recovered} esiti recuperati / {total} opportunità di recupero.',
    performanceDisclosure: 'I valori economici mostrati rappresentano esclusivamente risultati recuperati e verificati. Le opportunità aperte sono indicate come volumi operativi e non come ricavi realizzati.', performanceChartAria: 'Unico grafico prestazioni Crescita', performanceEyebrow: 'PRESTAZIONI', performanceChartTitle: 'Un solo percorso, per il workflow selezionato', performanceChartDescription: 'Il grafico mostra le fasi del workflow “{workflow}”. I dati economici restano separati e vengono mostrati solo quando verificati.', performanceSelectWorkflow: 'Seleziona workflow Crescita',
    performanceDetected: 'Opportunità rilevate', performanceToManage: 'Da gestire', performanceFunnelContacted: 'Contattate', performanceBooked: 'Prenotate', performanceCompleted: 'Concluse', performanceVerifiedRecoveredValue: 'Valore recuperato verificato', performanceNoVerifiedValue: 'Non ci sono ancora valori recuperati verificati.', performanceCurrencyScales: 'Totali per valuta su scale separate; non filtrato per periodo.', performanceWorkflowDisclosure: 'Le fasi sono filtrate per workflow, non sono una previsione di ricavo. Un esito entra qui solo se viene registrato nel workflow.', performanceOpenAction: 'Visualizza opportunità da gestire',
    type_review_request: 'Richieste di recensione',
    performanceDescription: 'Una vista sintetica dell’avanzamento delle opportunità nel periodo selezionato.',
    performanceOpportunities: 'Opportunità',
    performanceContacted: 'Recuperi contattati',
    performanceAttributedBooked: 'Recuperati / prenotati',
    performanceBreakdown: 'Ripartizione opportunità',
    performanceDismissedResolved: 'Archiviati / risolti',
    performanceNeedsAttention: 'Richiede attenzione',
    performanceOverdueFollowUps: 'Follow-up in ritardo',
    performanceNoFollowUp: 'Nessun follow-up impostato',
    performanceRetentionDue: 'Fidelizzazione in scadenza',
    openRecovery: 'Apri recupero',
    playbookGuidance: 'Guida playbook',
    playbookTitle: 'Playbook di recupero',
    playbookStep: 'Passo {{step}}',
    playbookTiming: 'Tempistica',
    playbookAction_initial_contact: 'Effettua il primo contatto',
    playbookAction_follow_up: 'Ricontatta il paziente',
    playbookAction_confirm_booking: 'Conferma la prenotazione',
    playbookAction_review: 'Rivedi il recupero',
    playbookAction_no_action: 'Nessuna ulteriore azione',
    playbookStatus_not_started: 'Non iniziato',
    playbookStatus_due: 'Da fare ora',
    playbookStatus_overdue: 'Scaduto',
    playbookStatus_scheduled: 'Programmato',
    playbookStatus_completed: 'Completato',
    playbookTiming_today: 'Oggi',
    playbookTiming_schedule_short_follow_up: 'Programma un breve follow-up',
    playbookTiming_scheduled: 'Usa il follow-up programmato',
    playbookTiming_none: 'Nessuna tempistica richiesta',
    playbookReason_no_contact_attempt: 'Non è ancora registrato alcun tentativo di contatto.',
    playbookReason_no_answer: 'Il paziente non ha risposto all’ultimo tentativo.',
    playbookReason_patient_will_rebook: 'Conferma che il paziente ha completato la prenotazione.',
    playbookReason_contacted: 'Prosegui il follow-up di recupero.',
    playbookReason_terminal_outcome: 'Questo recupero è completato o chiuso.',
    playbookReason_workflow_review: 'Rivedi il flusso di recupero prima di continuare.',
    addActivity: 'Aggiungi attività',
    saveWorkflow: 'Salva flusso di lavoro',
    workflowSaved: 'Flusso di lavoro salvato',
    activityCreated: 'Attività aggiunta',
    appointmentLinked: 'Appuntamento collegato',
    patientReference: 'Paziente n. {id}',
    reason_no_show_recovery: 'Un paziente non si è presentato a un appuntamento programmato.',
    reason_cancelled_recovery: 'Un appuntamento è stato annullato.',
    reason_reactivation: 'Questo paziente potrebbe beneficiare di un contatto di follow-up.',
    reason_cancelled_slot_recovery: 'Un appuntamento futuro annullato ha liberato uno slot da esaminare.',
    reason_recall: 'È previsto un richiamo per l’igiene.',
  },
  kb: {
    title: 'Base di conoscenza',
    createNew: 'Nuova voce',
    category: 'Categoria',
    content: 'Contenuto',
    status: 'Stato',
    version: 'Versione',
    updated: 'Ultimo aggiornamento',
    statusDraft: 'Bozza',
    statusPendingReview: 'In revisione',
    statusApproved: 'Approvata',
    statusArchived: 'Archiviata',
    loadError: 'Impossibile caricare la base di conoscenza',
    empty: 'La tua base di conoscenza è pronta quando lo sei tu',
    emptyMessage: 'Aggiungi informazioni approvate della clinica per aiutare il team a fornire risposte coerenti e utili.',
    edit: 'Modifica voce',
    submit: 'Invia in revisione',
    approve: 'Approva',
    reject: 'Rifiuta',
    archive: 'Archivia',
  },
  pmsImport: {
    title: 'Import PMS assistito',
    subtitle: 'Crea una base operativa supervisionata dal formato CSV v1 documentato. Prima l’anteprima: nessun record viene scritto fino alla conferma.',
    format: 'Formato accettato: dentai_concierge_pms_csv_v1',
    selectFile: 'Seleziona file CSV',
    preview: 'Anteprima e prova',
    previewing: 'Preparazione anteprima…',
    confirm: 'Conferma import',
    confirming: 'Importazione…',
    noFile: 'Seleziona un file CSV prima dell’anteprima.',
    sourceNote: 'Il file sorgente viene usato solo in memoria. DentAI non conserva CSV grezzi o identificativi della fonte.',
    previewTitle: 'Risultato della prova',
    rows: 'righe',
    errors: 'Errori di convalida',
    warnings: 'Avvisi su duplicati e collegamenti',
    canConfirm: 'Questo file è valido. La conferma scriverà atomicamente i record pianificati.',
    cannotConfirm: 'Correggi il file sorgente ed esegui di nuovo l’anteprima. Non è stato scritto nulla.',
    clinic: 'Impostazioni clinica',
    patients: 'Pazienti',
    providers: 'Professionisti',
    appointmentTypes: 'Tipi di appuntamento',
    appointments: 'Appuntamenti',
    created: 'nuovi',
    linkedExisting: 'collegati esistenti',
    alreadyImported: 'già importati',
    updated: 'aggiornati',
    revenueBaseline: 'Appuntamenti no-show/annullati disponibili per la scansione Ricavi esistente',
    resultTitle: 'Importazione completata',
    idempotent: 'Questa fonte era già stata completata; non sono stati creati duplicati.',
    job: 'Processo di importazione',
    baselineTitle: 'Snapshot della baseline pilota',
    baselineCapturedAt: 'Rilevata',
    totalPatients: 'Pazienti attivi',
    activeProviders: 'Professionisti attivi',
    totalAppointments: 'Appuntamenti operativi importati',
    recoveryEligible: 'Appuntamenti no-show/annullati',
    activationTitle: 'Attivazione pilota',
    activationReady: 'Baseline verificata. Attiva il pilota supervisionato quando la clinica è operativamente pronta.',
    activationActive: 'Pilota attivo a partire da questa baseline.',
    activationRequired: 'Conferma l’attivazione per contrassegnare la clinica come pronta per il pilota supervisionato. Non attiva messaggistica, AI, fatturazione o provider esterni.',
    activate: 'Attiva pilota',
    activating: 'Attivazione…',
    noBaseline: 'Completa un’importazione valida prima di attivare il pilota.',
  },
  careJourneys: {
    title: 'Percorsi di cura amministrativi',
    subtitle: 'Tieni traccia dei progressi amministrativi approvati dalla clinica. DentAI non effettua diagnosi, non raccomanda cure e non sostituisce il PMS clinico.',
    newJourney: 'Nuovo percorso di cura',
    patient: 'Paziente',
    summary: 'Riepilogo amministrativo',
    value: 'Valore amministrativo',
    followUp: 'Follow-up previsto',
    revenueOpportunity: 'ID opportunità Ricavi (facoltativo)',
    initialItems: 'Elementi amministrativi iniziali (uno per riga)',
    create: 'Crea percorso di cura',
    creating: 'Creazione…',
    metrics: 'Metriche per il titolare',
    acceptedValue: 'Valore amministrativo accettato',
    completedValue: 'Valore amministrativo completato',
    dueFollowUps: 'Follow-up dovuti',
    plannedAppointments: 'Appuntamenti pianificati',
    completedAppointments: 'Appuntamenti completati',
    noJourneys: 'Nessun percorso di cura.',
    items: 'Elementi amministrativi',
    appointments: 'Appuntamenti collegati',
    addItem: 'Aggiungi elemento',
    linkAppointment: 'Collega appuntamento',
    status: 'Stato',
    updateStatus: 'Aggiorna stato',
    selectPatient: 'Seleziona un paziente',
    selectAppointment: 'Seleziona un appuntamento per questo paziente',
    noAppointments: 'Nessun appuntamento collegato.',
    created: 'Percorso di cura creato.',
    loadError: 'Impossibile caricare i percorsi di cura.',
    noClinical: 'Solo amministrativo — non inserire diagnosi, raccomandazioni, prescrizioni o cartelle cliniche.',
  },
  aiCopilot: {
    title: 'Copilot solo per il personale',
    subtitle: 'Usa conoscenza approvata dalla clinica e la conversazione corrente per preparare una bozza revisionabile. Non invia messaggi né prenota appuntamenti.',
    purpose: 'Finalità della comunicazione',
    channel: 'Canale della bozza',
    locale: 'Lingua della bozza',
    variables: 'Variabili del template approvato (JSON)',
    staffNote: 'Istruzione facoltativa del personale',
    prepare: 'Prepara bozza supervisionata',
    preparing: 'Preparazione…',
    resultTitle: 'Bozza pronta per la revisione',
    reviewRequired: 'Bozza assistita dall’AI — revisione obbligatoria prima di qualsiasi azione o comunicazione.',
    provider: 'Provider AI',
    providerLuna: 'Luna',
    providerLocal: 'Provider locale deterministico',
    noDraft: 'Non è stata ancora preparata alcuna bozza.',
  },
  legal: {
    privacyTitle: 'Informativa sulla Privacy',
    termsTitle: 'Termini di Servizio',
    lastUpdated: 'Ultimo aggiornamento',
    lastUpdatedDate: '[Da definire dal team legale]',
    placeholderNotice: 'Contenuto Provvisorio',
    placeholderNoticeText: 'Questa pagina contiene testo provvisorio. I contenuti legali devono essere forniti da un professionista qualificato.',
    privacyIntro: 'Introduzione',
    privacySection1Title: 'Titolare del Trattamento',
    privacySection1Body: '[Contenuto legale da fornire.]',
    privacySection2Title: 'Dati Raccolti',
    privacySection2Body: '[Contenuto legale da fornire.]',
    privacySection3Title: 'Base Giuridica',
    privacySection3Body: '[Contenuto legale da fornire.]',
    privacySection4Title: 'Diritti dell Interessato',
    privacySection4Body: '[Contenuto legale da fornire.]',
    privacySection5Title: 'Conservazione dei Dati',
    privacySection5Body: '[Contenuto legale da fornire.]',
    privacySection6Title: 'Condivisione dei Dati',
    privacySection6Body: '[Contenuto legale da fornire.]',
    privacyContactTitle: 'Contattaci',
    privacyContactBody: '[Contenuto legale da fornire.]',
    termsIntro: 'Introduzione',
    termsSection1Title: 'Accettazione dei Termini',
    termsSection1Body: '[Contenuto legale da fornire.]',
    termsSection2Title: 'Descrizione del Servizio',
    termsSection2Body: '[Contenuto legale da fornire.]',
    termsSection3Title: 'Obblighi dell Utente',
    termsSection3Body: '[Contenuto legale da fornire.]',
    termsSection4Title: 'Limitazione di Responsabilita',
    termsSection4Body: '[Contenuto legale da fornire.]',
    termsSection5Title: 'Risoluzione',
    termsSection5Body: '[Contenuto legale da fornire.]',
    termsSection6Title: 'Legge Applicabile',
    termsSection6Body: '[Contenuto legale da fornire.]',
    termsContactTitle: 'Contattaci',
    termsContactBody: '[Contenuto legale da fornire.]',
    backToHome: 'Torna alla Home',
    agreePrefix: 'Ho letto e accetto la',
    agreeAnd: 'e i',
    privacyLinkText: 'Informativa sulla Privacy',
    termsLinkText: 'Termini di Servizio',
  },
};

const ar: TranslationBundle = {
  app: {
    name: 'DentAI',
    tagline: 'إدارة العيادات السنية بالذكاء الاصطناعي',
  },
  landing: {
    navFeatures: 'الميزات',
    navHowItWorks: 'كيف يعمل',
    navForClinics: 'للعيادات',
    ctaGetStarted: 'ابدأ الآن',
    ctaLogin: 'تسجيل الدخول',
    ctaTryDentai: 'جرب DentAI',
    heroTitle: 'برنامج ذكي للعيادة الأسنان الحديثة',
    heroSubtitle: 'DentAI يجمع المرضى والمواعيد والموظفين وعمليات العيادة في منصة واحدة مع بنية جاهزة للذكاء الاصطناعي.',
    heroCtaPrimary: 'جرب DentAI',
    heroCtaSecondary: 'تسجيل الدخول',
    heroBadge: 'منصة جاهزة للذكاء الاصطناعي',
    trustTitle: 'منصة مصممة لعيادات الأسنان الحديثة',
    trustSubtitle: 'كل ما تحتاجه عيادتك ، في مساحة عمل واحدة.',
    trustItem1: 'بنية متعددة العيادات مع عزل على مستوى المؤسسة',
    trustItem2: 'تحكم في الوصول بناء على الدور لكل عضو فيم',
    trustItem3: 'إدارة المرضى مع تتبع الموافقة',
    trustItem4: 'جدولة المواعيد مع سير الحالة',
    trustItem5: 'صندوق محادثات موحد عبر القنوات',
    trustItem6: 'واجهة متعددة اللغات: إيطالية ، إنجليزية ، عربية',
    featuresTitle: 'كل ما تحتاجه عيادتك',
    featuresSubtitle: 'منصة كاملة لإدارة عيادة الأسنان.',
    feature1Title: 'إدارة المرضى',
    feature1Desc: 'إنشاء وبحث وإدارة سجلات المرضى مع تتبع الموافقة وحماية الحذف الناعم.',
    feature2Title: 'مواعيد ذكية',
    feature2Desc: 'حجز وتأكيد وإلغاء وتتبع المواعيد مع إدارة الأطباء وأنواع المواعيد.',
    feature3Title: 'لوحة العيادة',
    feature3Desc: 'مؤشرات فورية: مواعيد اليوم ، عدد المرضى ، نشاط الأطباء ، والإجراءات الأخيرة.',
    feature4Title: 'الموظفون والأدوار',
    feature4Desc: 'إدارة الموطفين مع وصول بناء على الدور وإيقاف وإعادة تفعيل وحمايات أمنتة.',
    feature5Title: 'المحادثات',
    feature5Desc: 'صندوق موحد للتواصل مع المرضى مع مؤشرات تسليم الذكاء الاصطناعي ورسائل الموظفين.',
    feature6Title: 'بنية جاهزة للذكاء الاصطناعي',
    feature6Desc: 'مصممة لدعم سلسلات العمل الذكية في المستقبل وتكامل قاعدة المعرفة والأتمتة الذكية.',
    howTitle: 'كيف يعمل',
    howStep1Title: 'أنشئ عيادتك',
    howStep1Desc: 'سجل مؤسستك وعيادتك في دقائق. لا تكالف إعداد ، لا تثبيت.',
    howStep2Title: 'نظم المرضى والموظفين',
    howStep2Desc: 'أضف أطباء وأنواع مواعيد ومرضى وموظفين مع الأدوار والصلاحيات المناسبة.',
    howStep3Title: 'إدار من مساحة عمل واحدة',
    howStep3Desc: 'لوحة التحكم والمواعيد والمحادثات والإعدادات كلها في مكان واحد ، متاح من أي جهاز.',
    clinicOpsTitle: 'منصة تشغيل عيادة موحدة',
    clinicOpsSubtitle: 'DentAI يربط كل جزء من عيادتك.',
    clinicOpsPatients: 'المرضى',
    clinicOpsAppointments: 'المواعيد',
    clinicOpsStaff: 'الموظفون',
    clinicOpsConversations: 'المحادثات',
    clinicOpsDashboard: 'لوحة التحكم',
    aiTitle: 'مصمم للجيل القادم من رعاية الأسنان',
    aiSubtitle: 'بنية DentAI مصممة لدعم سلسلات العمل الذكية والتواصل المستقبلي مع الذكاء الاصطناعي وأتمتة العيادة.',
    aiPoint1: 'إطار تكامل قاعدة المعرفة',
    aiPoint2: 'تسليم المحادثة بين الذكاء الاصطناعي والموظفين',
    aiPoint3: 'حواجز أمان للإدخال والإخراج',
    aiPoint4: 'تسجيل التدقيق لجميع إجراءات الذكاء الاصطناعي',
    aiDisclaimer: 'ميزات الذكاء الاصطناعي قيد التطوير النشط. التطبيق الحالي يستخدم ردود حتمية ، ليس LLM للإنتاج.',
    multilingualTitle: 'تحدث بلغتك',
    multilingualSubtitle: 'DentAI يدعم ثلاث لغات مع دعم كامل RTL.',
    langItalian: 'إيطالي',
    langEnglish: 'إنجليزية',
    langArabic: 'عربية',
    rtlNote: 'دعم كامل للكتابة من اليمين لليسار للعربية',
    finalCtaTitle: 'انقل بعيادة أسنانك إلى المستقبل',
    finalCtaSubtitle: 'ابدأ إدارة عيادتك مع DentAI اليوم.',
    finalCtaPrimary: 'ابدأ الآن',
    finalCtaSecondary: 'تسجيل الدخول',
    footerProduct: 'المنتج',
    footerFeatures: 'الميزات',
    footerHowItWorks: 'كيف يعمل',
    footerAccount: 'الحساب',
    footerLogin: 'تسجيل الدخول',
    footerRegister: 'ابدأ الآن',
    footerLegal: 'القانون',
    footerPrivacy: 'سياسة الخصوصية',
    footerTerms: 'شروط الخدمة',
    footerRights: 'جميع الحقوق محفوظة.',
    footerComingSoon: 'قريباً',
    dashMockTitle: 'لوحة العيادة',
    dashMockPatients: 'المرضى',
    dashMockToday: 'اليوم',
    dashMockPending: 'قيد الانتظار',
    dashMockConfirmed: 'مؤكد',
  },
  nav: {
    dashboard: 'لوحة التحكم',    overview: 'نظرة عامة',
    communications: 'التواصل',
    automations: 'الأتمتة',
    reports: 'التقارير',
    appointments: 'المواعيد',
    patients: 'المرضى',
    conversations: 'المحادثات',
    staff: 'الموظفون',
    settings: 'الإعدادات',
    logout: 'تسجيل الخروج',
    receptionist: 'موظف الاستقبال',
    providers: 'الأطباء',
    appointmentTypes: 'أنواع المواعيد',
    privacy: 'الخصوصية',
    notifications: 'الإشعارات',
    knowledgeBase: 'قاعدة المعرفة',
    aiConfig: 'إعدادات الذكاء الاصطناعي',
    whatsappAgent: 'وكيل واتساب',
    analytics: 'التحليل',
    auditLogs: 'سجلات التدقيق',
    revenue: 'الإيرادات',
    communication: 'مراجعة التواصل',
    pmsImport: 'استيراد PMS',
    careJourneys: 'مسارات الرعاية',
    menu: 'فتح التنقل',
    closeNavigation: 'إغلاق التنقل',
    language: 'اللغة',
    operations: 'العمليات',
    growth: 'النمو',
    knowledgeCommunication: 'المعرفة والتواصل',
    administration: 'الإدارة',    primary: 'المساحة الرئيسية',
    workspace: 'أدوات إضافية',
    admin: 'إعدادات العيادة',
  },
  auth: {
    login: 'تسجيل الدخول',
    register: 'إنشاء حساب',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    passwordHint: 'يجب أن تتكون كلمة المرور من 12 حرفًا على الأقل',
    fullName: 'الاسم الكامل',
    organizationName: 'اسم المؤسسة (اختياري)',
    clinicName: 'اسم العيادة (اختياري)',
    clinicTimezone: 'المنطقة الزمنية للعيادة',
    plan: 'خطة الحساب',
    freePlan: 'مجاني — إدارة العيادة الأساسية',
    trialPlan: 'تجربة لمدة 14 يومًا — وصول كامل إلى Professional',
    loginButton: 'دخول',
    registerButton: 'إنشاء حساب',
    forgotPassword: 'نسيت كلمة المرور؟',
    resetRequestTitle: 'اطلب إعادة تعيين كلمة المرور',
    resetRequestSubtitle: 'أدخل بريد العمل الإلكتروني. لأسباب أمنية، تكون الرسالة نفسها سواء وُجد حساب أم لا.',
    resetRequestButton: 'طلب إعادة التعيين',
    resetRequestSuccess: 'تم استلام طلبك. إذا لم يكن لديك رمز إعادة تعيين آمن، فتواصل مع مسؤول العيادة.',
    resetRequestError: 'تعذر معالجة الطلب. يرجى المحاولة مرة أخرى بعد قليل.',
    resetTokenTitle: 'إعادة تعيين كلمة المرور',
    resetTokenSubtitle: 'استخدم رمز إعادة التعيين الآمن المقدم عبر إجراء العيادة المعتمد.',
    resetToken: 'رمز إعادة التعيين',
    resetTokenHint: 'أدخل الرمز كاملًا تمامًا كما تم تقديمه.',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'تأكيد كلمة المرور الجديدة',
    passwordMismatch: 'كلمتا المرور غير متطابقتين.',
    resetPasswordButton: 'إعادة تعيين كلمة المرور',
    resetPasswordSuccess: 'تمت إعادة تعيين كلمة المرور. يمكنك الآن تسجيل الدخول.',
    resetPasswordError: 'تعذر إعادة تعيين كلمة المرور. تحقق من الرمز وحاول مجددًا.',
    returnToLogin: 'العودة إلى تسجيل الدخول',
    loginTitle: 'مرحبًا بعودتك',
    loginSubtitle: 'سجّل الدخول لمواصلة إدارة عمليات عيادتك.',
    registerTitle: 'أنشئ مساحة عمل عيادتك',
    registerSubtitle: 'أكمل الأساسيات الآن، ويمكنك تنقيح تفاصيل العيادة لاحقًا.',
    newToDentai: 'هل أنت جديد على DentAI؟',
    alreadyHaveAccount: 'لديك حساب بالفعل؟',
  },
  common: {
    loading: 'جارٍ التحميل…',
    error: 'حدث خطأ ما',
    retry: 'حاول مجددًا',
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    confirm: 'تأكيد',
    search: 'بحث',
    noResults: 'لا توجد نتائج',
    create: 'إضافة',
    edit: 'تعديل',
    close: 'إغلاق',
    actions: 'إجراءات',
    name: 'الاسم',
    status: 'الحالة',
    all: 'الكل',
  },
  health: {
    status: 'الحالة',
    version: 'الإصدار',
    uptime: 'وقت التشغيل',
    ok: 'جميع الأنظمة تعمل',
    degraded: 'متدهور',
    down: 'غير متاح',
  },
  patient: {
    title: 'المرضى',
    searchPlaceholder: 'ابحث بالاسم أو البريد الإلكتروني…',
    empty: 'لا يوجد مرضى بعد',
    emptyMessage: 'أضف أول مريض للبدء.',
    createNew: 'مريض جديد',
    editPatient: 'تعديل المريض',
    deletePatient: 'حذف المريض',
    deleteConfirm: 'هل أنت متأكد من حذف هذا المريض؟',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    dateOfBirth: 'تاريخ الميلاد',
    notes: 'ملاحظات',
    status: 'الحالة',
    active: 'نشط',
    inactive: 'غير نشط',
    blocked: 'محظور',
    created: 'تم الإنشاء',
    updated: 'تم التحديث',
    deleted: 'تم الحذف بنجاح',
    saved: 'تم الحفظ بنجاح',
    createError: 'فشل في إنشاء المريض',
    updateError: 'فشل في تحديث المريض',
    deleteError: 'فشل في حذف المريض',
    loadError: 'فشل في تحميل المرضى',
    totalCount: '{count} مريض',
  },
  appointment: {
    title: 'المواعيد',
    empty: 'لا توجد مواعيد',
    emptyMessage: 'احجز أول موعد للبدء.',
    createNew: 'موعد جديد',
    startTime: 'وقت البدء',
    endTime: 'وقت الانتهاء',
    patient: 'المريض',
    provider: 'الطبيب',
    type: 'نوع الموعد',
    notes: 'ملاحظات',
    status: 'الحالة',
    allStatuses: 'جميع الحالات',
    pending: 'قيد الانتظار',
    confirmed: 'مؤكد',
    cancelled: 'ملغي',
    completed: 'مكتمل',
    noShow: 'لم يحضر',
    confirm: 'تأكيد',
    cancel: 'إلغاء',
    cancelReason: 'سبب الإلغاء',
    cancelReasonRequired: 'السبب مطلوب',
    complete: 'تحديد كمكتمل',
    noShowAction: 'تحديد كغائب',
    createError: 'فشل في إنشاء الموعد',
    requiredFields: 'أكمل جميع الحقول المطلوبة قبل الحفظ.',
    loadError: 'فشل في تحميل المواعيد',
    statusError: 'فشل في تحديث حالة الموعد',
    selectPatient: 'اختر مريضاً',
    selectProvider: 'اختر طبيباً',
    selectType: 'اختر نوعاً',
    noTypes: 'لا توجد أنواع مواعيد متاحة',
    noProviders: 'لا يوجد أطباء متاحون',
    noPatients: 'لا يوجد مرضى متاحون',
    reschedule: 'إعادة جدولة',
    rescheduleTitle: 'إعادة جدولة الموعد',
    rescheduleDescription: 'اختر وقت بداية ونهاية جديدين لهذا الموعد.',
    rescheduleTimeRangeError: 'يجب أن يكون وقت الانتهاء بعد وقت البدء.',
    rescheduleConflict: 'لدى هذا الطبيب موعد آخر خلال هذا الوقت. اختر وقتًا مختلفًا.',
    rescheduleError: 'تعذر إعادة جدولة هذا الموعد. يرجى المحاولة مرة أخرى.',
    confirmationQueueEyebrow: 'جاهزية الاستقبال',
    confirmationQueueTitle: 'قائمة تأكيد المواعيد',
    confirmationQueueWindow: 'مواعيد مستقبلية قيد الانتظار خلال الأيام {days} القادمة.',
    confirmationQueueError: 'تعذر تحميل قائمة التأكيد',
    noConfirmationsNeeded: 'لا توجد مواعيد قيد الانتظار تحتاج تأكيدًا ضمن هذه الفترة.',
    needsAttentionToday: 'تحتاج اهتمامًا اليوم',
    upcomingConfirmations: 'قادمة',
    confirmationStatus: 'أولوية التأكيد',
    attendanceEyebrow: 'حضور الاستقبال',
    attendanceTitle: 'حضور اليوم',
    attendanceDescription: 'سجّل وصول المرضى واحسم المواعيد بعد وقتها المحدد.',
    attendanceCheckedIn: 'تم تسجيل الوصول',
    attendancePastUnresolved: 'مواعيد منتهية غير محسومة',
    attendanceStatus: 'حالة الحضور',
    attendanceLoadError: 'تعذر تحميل حضور اليوم',
    noAttendanceNeeded: 'لا توجد مواعيد مؤكدة تحتاج إجراء حضور اليوم.',
    checkIn: 'تسجيل الوصول',
    checkedIn: 'تم تسجيل الوصول',
    awaitingArrival: 'بانتظار الوصول',
    pastUnresolved: 'تجاوز الوقت — احسم الآن',
  },
  dashboard: {
    title: 'لوحة التحكم',
    overviewTitle: 'عيادتك، منظمة بوضوح لهذا اليوم',
    goodMorning: 'صباح الخير',
    goodAfternoon: 'مساء الخير',
    goodEvening: 'مساء الخير',
    newAppointment: 'موعد جديد',
    setupAlertTitle: 'أكمل إعداد العيادة',
    setupAlertBody: 'أضف الأطباء وأنواع المواعيد حتى تعكس الأجندة يومك الحقيقي.',
    completeSetup: 'إكمال الإعداد',
    today: 'اليوم',
    openCalendar: 'فتح التقويم',
    availableSpaces: 'بانتظار التأكيد',
    cancellationsNoShows: 'الإلغاءات / عدم الحضور',
    todayAgenda: 'أجندة اليوم',
    viewAll: 'عرض الكل',
    toManage: 'ما يحتاج إلى إجراء',
    toManageDescription: 'العناصر القليلة التي تستحق الأولوية أولًا.',
    communicationAwaiting: 'تواصل بانتظار المراجعة',
    nothingToManage: 'لا توجد أمور عاجلة الآن.',
    opportunities: 'الفرص',
    noAccess: 'هذه المنطقة غير متاحة لدورك.',
    noOpportunities: 'لا توجد فرص مفتوحة.',
    opportunityNeedsReview: 'فرصة تحتاج إلى مراجعة',
    communication: 'التواصل',
    communicationDraft: 'مسودة جاهزة للمراجعة',
    noCommunications: 'لا توجد رسائل تحتاج إلى انتباه.',
    draftStatus: 'مسودة',
    lunaTitle: 'لونا إلى جانبك',
    lunaBody: 'استخدم لونا للحصول على اقتراح تشغيلي. يحتفظ الفريق بالتحكم في كل إجراء.',
    openLuna: 'فتح لونا',
    totalPatients: 'إجمالي المرضى',
    activeProviders: 'الأطباء النشطون',
    todaysAppointments: 'مواعيد اليوم',
    clinicOverview: 'نظرة عامة على العيادة',
    pendingAppointments: 'مواعيد قيد الانتظار',
    confirmedAppointments: 'مواعيد مؤكدة',
    completedAppointments: 'مواعيد مكتملة',
    time: 'الوقت',
    patient: 'المريض',
    provider: 'الطبيب',
    type: 'النوع',
    status: 'الحالة',
    noAppointmentsToday: 'لا توجد مواعيد لليوم',
    noRecentActivity: 'لا يوجد نشاط حديث',
    recentActivity: 'النشاط الأخير',
    loadError: 'فشل في تحميل لوحة التحكم',
    welcomeTitle: 'صورة واضحة لعيادتك اليوم',
    welcomeSubtitle: 'حافظ على سير العمليات وحوّل الفرص الفائتة إلى متابعات مفيدة.',
    nextStepTitle: 'ابدأ بالأساسيات',
    nextStepBody: 'أضف فريقك السريري وأنواع المواعيد قبل جدولة أول زيارة.',
    setupProviders: 'إعداد مقدمي الخدمة',
    setupAppointments: 'إعداد أنواع المواعيد',
    activityRecorded: 'تم تسجيل نشاط',
    activityLogin: 'سجّل أحد أعضاء الفريق الدخول',
    activityRegister: 'تم إنشاء مساحة عمل',
    activityRefresh: 'تم تحديث الجلسة',
    activityRevenueScan: 'تمت مراجعة فرص الإيرادات',
  },
  automation: {
    eyebrow: 'العمليات', title: 'الأتمتة', subtitle: 'قواعد تشغيلية واضحة تخضع دائمًا للموافقات والصلاحيات وضوابط DentAI.',
    controlTitle: 'التحكم البشري محفوظ.', controlBody: 'لا تتجاوز القاعدة النشطة الموافقة أو الاعتماد أو ضوابط التسليم.', denied: 'يمكن لمسؤولي العيادة المخولين فقط إدارة الأتمتة.',
    loading: 'جارٍ تحميل الأتمتة', loadError: 'تعذر تحميل الأتمتة.', empty: 'لا توجد أتمتة مهيأة', emptyBody: 'تُحضّر الأتمتة المهام والمسودات وفق قواعد العيادة. وتبقى موافقة الموظفين مطلوبة حيثما تم ضبطها. اطلب من مسؤول العيادة إعداد قاعدة.',
    active: 'نشطة', paused: 'متوقفة مؤقتًا', staffApproval: 'اعتماد الموظفين', required: 'مطلوب', governed: 'وفق السياسة والضوابط المطبقة',
    cooldown: 'الحد الأدنى للفاصل', none: 'لا يوجد', pauseHint: 'يمنع الإيقاف المؤقت تنفيذ القاعدة مجددًا.', pause: 'إيقاف مؤقت', activate: 'تفعيل', updateError: 'تعذر تحديث القاعدة. لم تتغير أي إعدادات.',
    no_show_recovery: 'استعادة عدم الحضور', cancelled_recovery: 'استعادة الإلغاء', reactivation: 'إعادة تنشيط المرضى', recall: 'تذكير النظافة', review_request: 'طلب تقييم',
    appointment_no_show: 'عندما يصبح الموعد عدم حضور', appointment_cancelled: 'عند إلغاء موعد', patient_inactive: 'عندما يصبح المريض غير نشط', hygiene_recall_due: 'عند حلول موعد التذكير', appointment_completed: 'عند اكتمال موعد',
  },
  verification: {
    verifying: 'جارٍ التحقق…', verified: 'تم التحقق من البريد الإلكتروني', alreadyVerified: 'تم التحقق من البريد مسبقًا', expired: 'انتهت صلاحية الرابط', invalid: 'الرابط غير صالح', waiting: 'تحقق من بريدك الإلكتروني',
    successBody: 'يمكنك الآن تسجيل الدخول إلى DentAI Italia.', expiredBody: 'اطلب رابط تحقق جديدًا.', invalidBody: 'الرابط غير صالح أو سبق استخدامه.', waitingBody: 'افتح الرابط المرسل إلى بريدك الإلكتروني وتحقق من مجلد الرسائل غير المرغوب فيها.', verifyingBody: 'نتحقق من الرابط بأمان.',
    destination: 'الوجهة', resend: 'إعادة إرسال رسالة التحقق', resendIn: 'إعادة الإرسال خلال {seconds}ث', resendSuccess: 'إذا كان الحساب يتطلب التحقق فسيصلك رابط جديد.', resendError: 'تعذر إرسال البريد الإلكتروني. حاول مرة أخرى بعد قليل.', login: 'الانتقال إلى تسجيل الدخول',
  },
  lunaPage: {
    eyebrow: 'المساعدة التشغيلية', title: 'Luna إلى جانب فريقك', subtitle: 'تساعد Luna في السياقات المدعومة مع بقاء قواعد DentAI الحتمية هي المرجع.',
    growth: 'النمو', growthBody: 'افتح فرصة مؤهلة واطلب تحليل Luna وراجع سبب الاقتراح قبل الإجراء الخاضع للضوابط.', growthAction: 'عرض الفرص',
    communications: 'التواصل', communicationsBody: 'استخدم المساعد الخاضع للإشراف في المحادثات المدعومة. تبقى كل مسودة خاضعة للموافقة والاعتماد وضوابط الإرسال.', communicationsAction: 'فتح المحادثات',
    limits: 'الحدود والمسؤوليات', limit1: 'تقترح Luna ويتحقق الفريق ويقرر.', limit2: 'لا ترسل هذه الصفحة اتصالات ولا تعدّل المواعيد تلقائيًا.', limit3: 'تبقى ضوابط الأمان الحتمية والموافقات وصلاحيات DentAI هي المرجع.',
    admin: 'إدارة الذكاء الاصطناعي', adminBody: 'الإعدادات التقنية منفصلة عن المساعدة التشغيلية.', adminAction: 'فتح الإعدادات',
  },
  billingReturn: { checkout: 'دفع Professional', verified: 'تم التحقق من الاشتراك', verifying: 'جارٍ التحقق من الدفع', loading: 'جارٍ التحقق من الاشتراك', activeBody: 'أكد الخادم حالة Professional.', pendingBody: 'يرسل Stripe الحالة إلى الخادم. لا تمنح هذه الصفحة صلاحيات Professional.', dashboard: 'الانتقال إلى لوحة التحكم', plan: 'التحقق من الخطة', cancelled: 'تم إلغاء الدفع', cancelledBody: 'يبقى حسابك وعيادتك نشطين. لا يؤكد هذا الرجوع دفعًا أو صلاحيات Professional.', retry: 'المحاولة مجددًا من الإعدادات' },
  conversation: {
    title: 'المحادثات',
    active: 'نشطة',
    escalated: 'تم التصعيد',
    closed: 'مغلقة',
    empty: 'لا توجد محادثات',
    selectConversation: 'اختر محادثة',
    noMessages: 'لا توجد رسائل بعد',
    typeMessage: 'اكتب رسالة…',
    send: 'إرسال',
    close: 'إغلاق',
    escalate: 'تصعيد',
    assignToMe: 'إسناد إلي',
    aiActive: 'الذكاء الاصطناعي نشط',
    loadError: 'فشل في تحميل المحادثات',
    createInternal: 'إنشاء محادثة داخلية لـ Copilot',
    createInternalButton: 'إنشاء محادثة',
    internalSubject: 'مراجعة داخلية لـ Copilot',
    selectPatient: 'اختر مريضًا موجودًا',
    patientLoadError: 'تعذر تحميل المرضى لإنشاء المحادثة',
    inbox: 'صندوق رسائل المرضى', unread: 'غير مقروء', awaitingReview: 'بانتظار المراجعة', replied: 'تم الرد', waitingPatient: 'بانتظار المريض',
    resolved: 'تم الحل', inbound: 'واردة', outbound: 'صادرة', lunaGenerated: 'تم إنشاؤها بواسطة Luna', humanGenerated: 'كتبها الموظفون',
    editReply: 'تعديل الرد', approveAndSend: 'اعتماد البريد وإرساله', manualReply: 'رد يدوي', backToInbox: 'العودة إلى صندوق الوارد',
    requiresReview: 'تتطلب المراجعة', lunaReplyReview: 'رد Luna · المراجعة مطلوبة', providerFailure: 'فشل مزود البريد الإلكتروني',
    lunaFailure: 'Luna غير متاحة — المراجعة البشرية مطلوبة', unknownSender: 'مرسل غير معروف', ambiguousCorrelation: 'تعذر تحديد المحادثة بدقة',
    highRiskReview: 'محتوى عالي الخطورة — المراجعة البشرية مطلوبة', automaticPolicy: 'سياسة تلقائية', manualPolicy: 'سياسة اعتماد يدوي',
    channelEmail: 'البريد الإلكتروني', channelWhatsapp: 'WhatsApp', channelSms: 'رسائل نصية', channelWebchat: 'دردشة الويب',
inboxFilter: 'عرض صندوق الوارد',
    filterAll: 'كل المحادثات',
    filterNeedsAttention: 'تحتاج إلى اهتمام الموظفين',
    filterAgentHandled: 'عالجها الوكيل',
    filterStaffHandled: 'عالجها الموظفون',
    filterBookingRelated: 'مرتبطة بالحجز',
    filterUnresolved: 'غير محلولة',
    filterOptedOut: 'مُلغى الاشتراك أو محظورة',
    filterPaused: 'متوقفة مؤقتًا',
    filterStaffOwned: 'مملوكة للموظف',
    filterCallbackDue: 'متابعة مستحقة',
    filterWaitlist: 'قائمة الانتظار',
    filterKnowledgeGap: 'فجوة معرفية',
    filterVoiceMessages: 'رسائل صوتية',
    filterShared: 'معالجة مشتركة',
    filterRecallRecovery: 'استدعاء أو استعادة',
    filterEscalated: 'مصعّدة',
    languageFilter: 'لغة المحادثة',
    allLanguages: 'كل اللغات',
    patientSearch: 'ابحث عن مريض',
  },
  provider: {
    title: 'الأطباء',
    empty: 'لا يوجد أطباء',
    emptyMessage: 'أضف أول طبيب للبدء في حجز المواعيد.',
    createNew: 'طبيب جديد',
    edit: 'تعديل الطبيب',
    name: 'الاسم',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    type: 'النوع',
    dentist: 'طبيب أسنان',
    hygienist: 'أخصائي نظافة الأسنان',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    notes: 'ملاحظات',
    active: 'نشط',
    inactive: 'غير نشط',
    deactivate: 'إيقاف',
    loadError: 'فشل في تحميل الأطباء',
    workingHours: 'ساعات العمل',
    workingHoursHint: 'حدد فترة أسبوعية واحدة لكل يوم. ألغِ تحديد مغلق لتفعيل اليوم.',
    workingHoursAfterCreate: 'احفظ الطبيب أولًا، ثم عدّل الطبيب لتحديد ساعات العمل.',
    off: 'مغلق',
    startTime: 'البداية',
    endTime: 'النهاية',
    scheduleLoadError: 'فشل في تحميل ساعات العمل',
  },
  appointmentType: {
    title: 'أنواع المواعيد',
    empty: 'لا توجد أنواع مواعيد',
    emptyMessage: 'أنشئ أول نوع موعد للبدء.',
    createNew: 'نوع جديد',
    edit: 'تعديل النوع',
    name: 'الاسم',
    description: 'الوصف',
    duration: 'المدة',
    durationHint: 'المدة بالدقائق (1-480)',
    price: 'السعر',
    currency: 'العملة',
    priceHint: 'قيمة افتراضية اختيارية، محفوظة بأصغر وحدة للعملة.',
    priceInvalid: 'أدخل سعراً صالحاً غير سالب وبحد أقصى منزلتين عشريتين.',
    minutes: 'دقيقة',
    active: 'نشط',
    inactive: 'غير نشط',
    deactivate: 'إيقاف',
    loadError: 'فشل في تحميل أنواع المواعيد',
  },
  staff: {
    title: 'الموظفون',
    empty: 'لا يوجد موظفون',
    email: 'البريد الإلكتروني',
    role: 'الدور',
    active: 'نشط',
    suspended: 'موقوف',
    inactive: 'غير نشط',
    you: 'أنت',
    editRole: 'تعديل الدور',
    suspend: 'إيقاف',
    reactivate: 'إعادة تفعيل',
    remove: 'إزالة',
    removeConfirm: 'هل أنت متأكد من إزالة هذا الموظف من العيادة؟',
    removeError: 'فشل في إزالة الموظف',
    loadError: 'فشل في تحميل الموظفين',
    permissions: 'الصلاحيات',
    teamTitle: 'فريق العيادة',
    teamSubtitle: 'ادعُ موظفي الاستقبال أو الأطباء وأدر وصولهم للعيادة. تُرسل الدعوات عبر بريد إلكتروني آمن.',
    managerOnly: 'مديرو العيادة فقط يمكنهم إدارة الموظفين والدعوات.',
    inviteMember: 'دعوة عضو فريق',
    createInvitation: 'إنشاء دعوة',
    inviteRole: 'الدور في العيادة',
    currentMembers: 'الأعضاء الحاليون',
    member: 'العضو',
    joined: 'تاريخ الانضمام',
    noMembers: 'لا يوجد أعضاء في العيادة',
    invitations: 'الدعوات',
    noInvitations: 'لا توجد دعوات',
    invitationStatus: 'حالة الدعوة',
    invitationStatusLabels: { pending: 'بانتظار الإرسال', accepted: 'مقبولة', cancelled: 'ملغاة', expired: 'منتهية' },
    invitationDeliveryNotice: 'تُسلَّم الدعوات عبر بريد إلكتروني آمن. أعد الإرسال إذا لم يستلمها المدعو.',
    invitationNoEmail: 'يتلقى المدعو رابط تفعيل آمنًا وأحادي الاستخدام عبر البريد الإلكتروني.',
    expires: 'تنتهي في',
    resend: 'طلب إعادة إرسال',
    cancelInvitation: 'إلغاء الدعوة',
    deactivate: 'تعطيل',
    roleDerivedPermissions: 'تُشتق الصلاحيات خادميًا من دور العيادة المحدد.',
    confirmActionTitle: 'تأكيد إجراء الفريق',
    confirm_resend: 'هل تطلب إعادة تسليم هذه الدعوة؟ لن يُرسل بريد حتى إعداد مزود معتمد.',
    confirm_cancel: 'هل تلغي هذه الدعوة؟ سيتوقف رابطها عن العمل.',
    confirm_deactivate: 'هل تعطل عضوية العيادة هذه؟ سيفقد العضو وصوله إلى هذه العيادة دون حذف حسابه.',
    confirm_reactivate: 'هل تعيد تفعيل عضوية العيادة هذه؟',
    statusLabels: { active: 'نشط', inactive: 'غير نشط' },
    roleLabels: {
      admin: 'مسؤول',
      super_admin: 'مدير عام',
      org_admin: 'مدير المؤسسة',
      clinic_admin: 'مدير العيادة',
      staff: 'موظف استقبال',
      provider: 'طبيب',
    },
  },
  settings: {
    title: 'الإعدادات',
    profile: 'الملف الشخصي',
    email: 'البريد الإلكتروني',
    role: 'الدور',
    organizationId: 'معرف المؤسسة',
    clinicId: 'معرف العيادة',
    clinicSettings: 'إعدادات العيادة',
    clinicName: 'اسم العيادة',
    timezone: 'المنطقة الزمنية',
    organization: 'المؤسسة',
    orgName: 'اسم المؤسسة',
    plan: 'الخطة',
    noClinic: 'لا توجد عيادة معينة',
    noOrg: 'لا توجد مؤسسة',
    saveError: 'فشل في الحفظ',
    saved: 'تم الحفظ بنجاح',
    pilotTitle: 'وصول التجربة المُدار',
    pilotDescription: 'تجربة Pilot المُدارة صالحة لمدة 30 يومًا. لا تجمع مدفوعات ولا تفعل providers خارجية.',
    pilotActive: 'التجربة نشطة',
    pilotEnds: 'تنتهي التجربة في',
    pilotGrant: 'منح أو تجديد تجربة 30 يومًا',
    pilotGranting: 'جارٍ منح التجربة…',
    pilotUnavailable: 'حالة التجربة غير متاحة.',
    pilotStatus: {
      missing: 'لا توجد تجربة نشطة',
      expired: 'انتهت التجربة',
      inactive: 'التجربة غير نشطة',
    },
  },
  privacy: {
    title: 'الخصوصية و GDPR',
    searchPatient: 'ابحث عن مريض باسم أو بريد إلكتروني...',
    noPatients: 'لا يوجد مرضى',
    retentionPolicy: 'سياسة الاحتفاظ',
    patientDataRetention: 'احتفاظ بيانات المريض',
    auditLogRetention: 'احتفاظ سجلات التدقيق',
    conversationRetention: 'احتفاظ المحادثات',
    gracePeriod: 'فترة سمح للبيانات المحذوفة',
    days: 'أيام',
    dataAccess: 'وصول إلى البيانات (حق الوصول)',
    appointments: 'المواعيد',
    conversations: 'المحادثات',
    messages: 'الرسائل',
    notifications: 'الإشعارات',
    exportedAt: 'تم التصدير في',
    refreshData: 'تحديث',
    exportData: 'تصدير البيانات',
    eraseData: 'مسح البيانات',
    consentStatus: 'حالة الموافقة',
    consentMarketing: 'موافقة التسويق',
    consentSms: 'موافقة SMS',
    consentEmail: 'موافقة البريد الإلكتروني',
    consentWhatsapp: 'موافقة WhatsApp',
    consentSaved: 'تم تحديث الموافقة بنجاح',
    lastUpdated: 'آخر تحديث',
    eraseConfirmTitle: 'تأكيد مسح البيانات',
    eraseConfirmMessage: 'سيؤدي هذا إلى إخفاء هوية بيانات المريض بشكل دائم. لا يمكن التراجع عن هذا الإجراء.',
    eraseWarning: 'تحذير: هذا إجراء GDPR تدميري لحق المسح. سيتم إخفاء هوية سجل المريض ولا يمكن استرداعه.',
    eraseConfirm: 'تأكيد المسح',
    error: 'فشل العملية',
  },
  notifications: {
    title: 'الإشعارات',
    unread: 'غير مقروء',
    empty: 'لا توجد إشعارات',
    emptyMessage: 'أنت محدّث بشكل كامل.',
    markRead: 'تابيع كمقروء',
    markAllRead: 'تابيع الكل كمقروء',
    urgent: 'عاجل',
    loadError: 'فشل في تحميل الإشعارات',
    open: 'فتح العنصر المرتبط',
  },
  knowledgeBase: {
    title: 'قاعدة المعرفة',
    empty: 'لا توجد عناصر في قاعدة المعرفة',
    emptyMessage: 'أنشئ أول عنصر في قاعدة المعرفة.',
    createNew: 'عنصر جديد',
    edit: 'تعديل',
    category: 'الفئة',
    content: 'المحتوى',
    status: 'الحالة',
    version: 'الإصدار',
    updated: 'محدّث',
    submit: 'إرسال للمراجعة',
    approve: 'اعتماد',
    reject: 'رفض',
    archive: 'أرشيف',
    statusDraft: 'مسودة',
    statusPendingReview: 'في المراجعة',
    statusApproved: 'معتمد',
    statusArchived: 'مؤرشف',
    loadError: 'فشل في تحميل قاعدة المعرفة',
  },
  aiConfig: {
    title: 'إعدادات AI',
    disclaimer: 'ميزات AI تستخدم ردود حتمية ، ليس LLM للإنتاج. هذه واجهة الإعدادات.',
    isEnabled: 'تفعيل AI',
    isEnabledDesc: 'تفعيل إدارة المحادثات المساعدة بالذكاء الاصطناعي لهذه العيادة',
    escalationThreshold: 'عتبة التصعيد',
    escalationThresholdDesc: 'الثقة التي تحتها يقوم AI بتسليم الموظفين (0-1)',
    maxTurns: 'الحد الأقصى للأدوار',
    maxTurnsDesc: 'الحد الأقصى لرسائل AI قبل التصعيد التلقائي (1-100)',
    autoHandoff: 'تسليم تلقائي (دقائق)',
    autoHandoffDesc: 'مهلة عدم النشاط للتسليم التلقائي للموظفين (1-1440)',
    saved: 'تم حفظ الإعدادات بنجاح',
    saveError: 'فشل في حفظ الإعدادات',
    autonomyPolicy: 'تساعد Luna في الوضع الخاضع للإشراف. وفي الوضع التلقائي تتعامل فقط مع المحادثات الروتينية المؤهلة وفق قواعد العيادة.',
    patientMessaging: 'مساعدة الذكاء الاصطناعي لرسائل المرضى',
    patientMessagingSafety: 'تظل الطلبات السريرية أو العاجلة أو الحساسة أو غير الواضحة أو غير المدعومة دائمًا لدى الفريق.',
    emailChannel: 'البريد الإلكتروني', whatsappChannel: 'واتساب', providerUnavailable: 'قناة المزود غير متاحة حاليًا، ولا يؤدي هذا الإعداد إلى تفعيلها.',
    modeOff: 'متوقفة', modeAssist: 'مساعدة — تُعد Luna الرد ويوافق الفريق', modeAutoReply: 'تلقائية — ترد Luna فقط على الطلبات المؤهلة',
  },
  whatsappAgent: {
    eyebrow: 'عمليات واتساب الخاضعة للحوكمة',
    title: 'وكيل واتساب',
    subtitle: 'موظف استقبال رقمي قابل للتهيئة، يعتمد على الحقائق المعتمدة وبيانات العيادة الحية.',
    overview: 'نظرة عامة على الوكيل',
    connection: 'الاتصال',
    connected: 'متصل',
    connectionRequired: 'يلزم اتصال واتساب',
    agentStatus: 'حالة الوكيل',
    enabled: 'مفعّل',
    disabled: 'متوقف',
    autonomy: 'الاستقلالية',
    autonomous: 'مستقل',
    supervised: 'خاضع للإشراف',
    activationTitle: 'التفعيل',
    activationDescription: 'تبقى الاستقلالية متوقفة حتى يفعّلها مسؤول العيادة صراحةً.',
    disconnectedDescription: 'صل واتساب قبل تفعيل الاستقلالية. لا ترسل هذه الصفحة رسائل خارجية.',
    activate: 'تفعيل الاستقلالية',
    deactivate: 'إيقاف الاستقلالية',
    fallbackLanguage: 'لغة الاحتياط',
    handoffDestination: 'وجهة التصعيد',
    allowedActions: 'الإجراءات المسموحة',
    knowledgeTitle: 'معرفة العيادة المعتمدة',
    knowledgeDescription: 'أدخل حقائق مترجمة يمكن للوكيل استخدامها. تأتي المواعيد والتوافر دائمًا من بيانات DentAI.',
    knowledgeLanguage: 'لغة المعرفة',
    knowledgeTitleField: 'عنوان المعرفة',
    knowledgeContentField: 'الإجابة المعتمدة',
    saveDraft: 'حفظ المسودة',
    approve: 'اعتماد',
    noKnowledge: 'لا توجد معرفة بهذه اللغة.',
    activityTitle: 'نشاط الوكيل',
    activityDescription: 'أحدث القرارات والنتائج والتصعيدات.',
    event: 'حدث وكيل',
    noActivity: 'لا يوجد نشاط بعد.',
    loadError: 'تعذر تحميل وكيل واتساب.',
    onboardingTitle: 'إعداد وكيل واتساب',
    onboardingDescription: 'أكمل معلومات العيادة أولًا، ثم راجع اللغة والتصعيد والإجراءات المسموحة وحالة اتصال واتساب.',
    required: 'مطلوب',
    recommended: 'موصى به',
    optional: 'اختياري',
    unavailable: 'غير متاح حتى يتم توصيل واتساب',
    openAgent: 'فتح إعداد الوكيل',
    statusDraft: 'مسودة',
    statusApproved: 'معتمد',
    outcomeHandled: 'تمت المعالجة',
    outcomeEscalated: 'تم التصعيد',
    outcomeBlocked: 'محظور',
    intentDetected: 'تم تصنيف الطلب',
    actionRecorded: 'تم تسجيل الإجراء',
    policyEvaluated: 'تم تسجيل قرار السياسة',
    deterministicSource: 'حدث حتمي للوكيل',
    workspaceNav: 'مساحة الوكيل', conversationsNav: 'المحادثات', knowledgeNav: 'المعرفة', appointmentRulesNav: 'قواعد المواعيد', revenuePlaybooksNav: 'خطط استعادة الإيرادات', safetyNav: 'السلامة والتصعيد', performanceNav: 'الأداء', modeTitle: 'وضع التشغيل', modeDescription: 'تحافظ الاستقلالية المحكومة على كل إجراء ضمن سياسة العيادة. الوضع المساعد يُعد المسودات؛ والاستقلالية المحكومة هي وضع التنفيذ الوحيد.', disconnectedTruth: 'واتساب غير متصل. يمكن للوكيل إعداد العمل، لكنه لا يستطيع إرسال رسالة خارجية.', waitingForStaff: 'بانتظار الموظف', overviewPeriod: 'كل نشاط الوكيل المسجل؛ لا يتم استنتاج أي قيمة مالية.', openInbox: 'فتح المحادثات', conversationsHandled: 'محادثات عالجها الوكيل', bookings: 'حجوزات موثقة', escalated: 'حالات التصعيد', recovery: 'أحداث الاستعادة', knowledgeGaps: 'فجوات المعرفة', policyFailures: 'إخفاقات السياسة', tasksTitle: 'السلامة ومهام الموظفين', noTasks: 'لا توجد مهام موظفين مفتوحة.', playbooksTitle: 'خطط استعادة الإيرادات', playbooksDescription: 'تبقى الخطط متوقفة حتى يفعّل مسؤول العيادة سير عمل مؤهلًا وبموافقة المريض.', playbookActive: 'نشطة', playbookPaused: 'متوقفة', approvalRequired: 'موافقة الموظف مطلوبة', noPlaybooks: 'لا توجد خطط مهيأة.', liveDataDescription: 'تبقى الإتاحة وجداول الأطباء وساعات العيادة والتحقق من الحجز بيانات DentAI المعتمدة.', authoritativeAvailability: 'لا تخترع الإتاحة أبدًا.', authoritativeBooking: 'يستخدم كل حجز ضوابط المواعيد.',    dailyOverview: 'نظرة تشغيلية لليوم',
    conversationsHandledToday: 'تمت معالجتها اليوم',
    needsStaff: 'تحتاج إلى الموظف',
    pausedOrStaffOwned: 'متوقفة أو مملوكة للموظف',
    unresolved: 'غير محلولة',
    callbacksDue: 'مكالمات متابعة مستحقة',
    waitlistTasks: 'مهام قائمة الانتظار',
    bookingRequestsAwaitingReview: 'طلبات حجز بانتظار المراجعة',
    knowledgeGapsToday: 'فجوات المعرفة اليوم',
    ownership: 'الملكية',
    state: 'الحالة',
    source: 'المصدر',
    noAuthoritativeSource: 'لا يوجد مصدر موثوق — اطلب من الموظف التأكيد',
    confidenceHigh: 'ثقة عالية',
    confidenceNeedsConfirmation: 'تحتاج إلى تأكيد',
    confidenceUnavailable: 'غير متاحة',
    takeOver: 'تولّي المحادثة',
    releaseToAgent: 'تسليم إلى الوكيل',
    pauseAgent: 'إيقاف الوكيل مؤقتًا',
    markResolved: 'وضع علامة كمحلولة',
    agentContext: 'سياق الوكيل', conversationState: 'حالة المحادثة', intent: 'النية', language: 'اللغة', confidence: 'مستوى الثقة', escalationReason: 'سبب التصعيد', unknownIntent: 'غير معروف', feedbackTitle: 'ملاحظات الموظفين', feedbackCorrection: 'تصحيح أو معلومة ناقصة (اختياري)', feedbackCorrectionRequired: 'أضف تصحيحًا قبل وضع علامة على الرد بأنه خاطئ أو ناقص.', feedbackDraftNotice: 'تنشئ التصحيحات مسودة معرفة تتطلب المراجعة؛ لا يتم النشر تلقائيًا.', feedbackHelpful: 'مفيد', feedbackIncorrect: 'غير صحيح', feedbackMissing: 'معلومة ناقصة', feedbackEscalate: 'تصعيد',
  },
  analytics: {
    title: 'التحليل',
    loadError: 'فشل في تحميل التحليل',
    totalPatients: 'إجمالي المرضى',
    activeProviders: 'الأطباء النشطون',
    pendingAppointments: 'مواعيد قيد الانتظار',
    openConversations: 'محادثات مفتوحة',
    aiEscalations: 'تصعيد AI',
    totalMessages: 'إجمالي الرسائل',
    appointmentStatus: 'المواعيد حسب الحالة',
    conversationStatus: 'المحادثات حسب الحالة',
    dateRange: 'نطاق التاريخ',
  },
  auditLogs: {
    title: 'سجلات التدقيق',
    empty: 'لا توجد سجلات تدقيق',
    loadError: 'فشل في تحميل سجلات التدقيق',
    filterByAction: 'تصفية بالإجراء...',
    timestamp: 'الطابع الزمني',
    actor: 'المستخدم',
    action: 'الإجراء',
    resource: 'المورد',
    result: 'النتيجة',
    resultSuccess: 'نجاح',
    resultFailure: 'فشل',
    resultDenied: 'مرفوض',
    page: 'صفحة',
    entries: 'عنصر',
    previous: 'السابق',
    next: 'التالي',
  },
  revenue: {
    title: 'النمو',
    view_open: 'ما يحتاج إلى إجراء',
    view_waiting: 'قيد الانتظار',
    view_recovered: 'تم استردادها',
    view_closed: 'مغلقة',
    view_all: 'الكل',
    scan: 'فحص الفرص',
    scanResult: 'اكتمل الفحص',
    created: 'تم إنشاؤها',
    skipped: 'كانت موجودة مسبقًا',
    loadError: 'تعذر تحميل فرص الإيرادات',
    accessDenied: 'ليست لديك صلاحية الوصول إلى الإيرادات لهذه العيادة.',
    actionError: 'تعذر إكمال إجراء الإيرادات',
    empty: 'لا توجد فرص إيرادات',
    emptyMessage: 'شغّل الفحص لتحديد فرص الاسترداد.',
    totalOpportunities: 'إجمالي الفرص',
    noShowRecovery: 'استرداد عدم الحضور',
    cancelledRecovery: 'استرداد الإلغاء',
    opportunity: 'فرصة',
    reactivation: 'إعادة تنشيط المرضى',
    cancelledSlotRecovery: 'استرداد المواعيد الملغاة',
    recall: 'تذكير النظافة',
    openOpportunities: 'مفتوحة',
    bookedOpportunities: 'تم حجزها',
    type: 'النوع',
    patient: 'المريض',
    reason: 'السبب',
    priority: 'الأولوية',
    status: 'الحالة',
    identified: 'تم تحديدها',
    type_no_show_recovery: 'استرداد عدم الحضور',
    type_cancelled_recovery: 'استرداد الإلغاء',
    type_reactivation: 'إعادة تنشيط المرضى',
    type_cancelled_slot_recovery: 'استرداد المواعيد الملغاة',
    type_recall: 'تذكير النظافة',
    lastHygiene: 'آخر زيارة نظافة',
    lastActivity: 'آخر نشاط',
    daysOverdue: 'أيام التأخير',
    analyticsTitle: 'تحليلات الفرص',
    analyticsDescription: 'تعكس هذه النسب حالات الفرص ولا تمثل إيرادات مالية.',
    period: 'الفترة',
    recoveryRate: 'معدل استرداد الفرص',
    workView: 'عرض العمل',
    openWork: 'العمل المفتوح',
    allOpportunities: 'كل الفرص',
    attentionNeeded: 'يتطلب اهتمامًا',
    openWorkDescription: 'تُرتّب الفرص المفتوحة حسب الأولوية ثم الأقدم أولًا.',
    openWorkEmpty: 'لا يوجد عمل مفتوح',
    openWorkEmptyMessage: 'لا توجد فرص محددة أو تم التواصل بشأنها وتتطلب إجراءً.',
    conversionRate: 'معدل التحويل',
    contactRate: 'معدل التواصل',
    resolutionRate: 'معدل المعالجة',
    allTime: 'كل الوقت',
    today: 'اليوم',
    last7Days: 'آخر 7 أيام',
    last30Days: 'آخر 30 يومًا',
    priority_low: 'منخفضة',
    priority_normal: 'عادية',
    priority_high: 'مرتفعة',
    priority_urgent: 'عاجلة',
    status_identified: 'تم تحديدها',
    status_contacted: 'تم التواصل',
    status_booked: 'تم الحجز',
    status_completed: 'مكتملة',
    status_dismissed: 'تم التجاهل',
    status_expired: 'منتهية',
    actionContacted: 'وضع علامة تم التواصل',
    actionBooked: 'وضع علامة تم الحجز',
    actionCompleted: 'وضع علامة مكتملة',
    actionDismiss: 'تجاهل',
    details: 'إدارة',
    workflowTitle: 'سير عمل الفرصة',
    workflowDescription: 'عيّن المسؤول وخطط للخطوة التالية وسجل المتابعة التشغيلية.',
    opportunityContext: 'سياق الفرصة',
    manualReviewOnly: 'هذا مسار يدوي للموظفين فقط. إعداد المسودة أو اعتمادها لا يرسل أي رسالة.',
    analyzeWithLuna: 'حلّل مع Luna',
    aiRecommendationTitle: 'توصية Luna',
    aiProvider: 'مزود الذكاء الاصطناعي',
    aiAction: 'الإجراء المقترح',
    aiConfidence: 'درجة الثقة',
    aiPurpose: 'غرض التواصل',
    aiChannel: 'القناة المقترحة',
    aiReason: 'السبب',
    aiNextStep: 'الخطوة التالية المقترحة',
    aiReviewOnly: 'توصية فقط — يجب على الموظف المراجعة قبل أي إجراء أو تواصل.',
    assignee: 'المسؤول',
    unassigned: 'غير مخصصة',
    assignedStaff: 'الموظف المسؤول رقم {id}',
    nextAction: 'الإجراء التالي',
    nextAction_review: 'مراجعة الفرصة',
    nextAction_contact: 'التواصل مع المريض',
    nextAction_follow_up: 'متابعة',
    nextAction_confirm_booking: 'تأكيد الحجز',
    nextAction_complete_follow_up: 'إكمال المتابعة',
    dueAt: 'تاريخ الاستحقاق',
    dueAtHint: 'اختياري. تُحفظ الأوقات مع المنطقة الزمنية.',
    overdue: 'متأخرة',
    bookingAttribution: 'إسناد الحجز',
    bookingNotAttributed: 'لا يوجد موعد مرتبط',
    linkAppointmentFirst: 'اربط الموعد أولًا',
    linkAppointmentFirstHint: 'اربط موعد المريض الفعلي قبل اعتبار هذا الاسترداد محجوزًا.',
    bookingAttributed: 'تم ربط موعد',
    selectAppointment: 'اختر موعدًا موجودًا',
    noEligibleAppointments: 'لا توجد مواعيد لهذا المريض في هذه العيادة.',
    linkAppointment: 'ربط الموعد',
    bookReleasedSlot: 'الحجز في الموعد المتاح',
    cancelledSlotBooked: 'تم حجز المرشح في الموعد المتاح.',
    bookingAlreadyCompleted: 'تم حجز هذا المرشح بالفعل.',
    cancelledSlotBookingGuardrail: 'الحجز بمساعدة الموظف ويعيد التحقق من الموعد والطبيب والمريض والتعارضات على الخادم.',
    releasedSlot: 'الموعد المتاح',
    matchEvidence: 'أدلة المطابقة الحتمية',
    activityHistory: 'سجل الأنشطة',
    activity: 'نشاط',
    activityType: 'نوع النشاط',
    activity_reviewed: 'تمت المراجعة',
    activity_contacted: 'تم التواصل',
    activity_follow_up: 'متابعة',
    activity_booking_attempt: 'محاولة حجز',
    activity_booked: 'تم الحجز',
    activity_completed: 'مكتملة',
    activity_dismissed: 'تم التجاهل',
    activity_contact_attempt: 'محاولة تواصل',
    activityNote: 'ملاحظة تشغيلية قصيرة',
    activityNoteHint: 'لا تدرج معلومات سريرية أو محتوى الرسائل.',
    activityOutcome: 'النتيجة',
    outcome_no_response: 'لا توجد استجابة',
    outcome_interested: 'مهتم',
    outcome_booked: 'تم الحجز',
    outcome_not_interested: 'غير مهتم',
    outcome_follow_up_needed: 'تحتاج متابعة',
    outcome_contacted: 'تم التواصل',
    outcome_no_answer: 'لا توجد إجابة',
    outcome_patient_declined: 'رفض المريض',
    outcome_patient_will_rebook: 'سيعيد المريض الحجز',
    outcome_appointment_recovered: 'تم استرداد الموعد',
    outcome_invalid_contact: 'بيانات اتصال غير صالحة',
    recoveryTracking: 'تتبع الاسترداد',
    attemptsCount: 'المحاولات',
    lastOutcome: 'آخر نتيجة',
    lastAttempt: 'آخر محاولة',
    followUpDue: 'المتابعة مستحقة',
    logContactAttempt: 'تسجيل محاولة تواصل',
    logContactAttemptDescription: 'يسجل نتيجة استرداد داخلية. لا يرسل رسالة ولا ينشئ موعدًا.',
    contactChannel: 'قناة التواصل',
    contactOutcome: 'نتيجة التواصل',
    channel_manual: 'يدوي',
    channel_phone: 'هاتف',
    channel_email: 'بريد إلكتروني',
    channel_whatsapp: 'واتساب',
    channel_sms: 'رسالة نصية',
    channel_other: 'أخرى',
    recoveryAttemptRecorded: 'تم تسجيل محاولة الاسترداد',
    recoveryNoteHint: 'ملاحظة تشغيلية اختيارية. لا تضمن معلومات سريرية أو محتوى الرسائل.',
    recoveryNextActionHint: 'اختياري. تُحفظ الأوقات مع المنطقة الزمنية.',
    followUpQueue: 'قائمة متابعة الاسترداد',
    followUpQueueDescription: 'حالات استرداد ذات أولوية تحتاج إجراءً من الموظفين.',
    followUpCategory: 'فئة المتابعة',
    followUp_all_open_recovery: 'كل حالات الاسترداد المفتوحة',
    followUp_overdue: 'متأخرة',
    followUp_due_today: 'مستحقة اليوم',
    followUp_upcoming: 'قادمة',
    followUp_no_follow_up: 'دون متابعة',
    followUpQueueEmpty: 'لا توجد متابعات استرداد تطابق هذه الفئة.',
    retentionQueue: 'قائمة الاحتفاظ بالمرضى',
    retentionQueueDescription: 'فرص الاستدعاء وإعادة التنشيط التي تحتاج إجراءً من الموظفين.',
    retentionCategory: 'فئة الاحتفاظ',
    retention_all: 'كل فرص الاحتفاظ',
    retention_recall: 'استدعاء',
    retention_reactivation: 'إعادة تنشيط',
    retention_overdue: 'متأخرة',
    retention_due_today: 'مستحقة اليوم',
    retention_upcoming: 'قادمة',
    retention_no_due_date: 'دون تاريخ استحقاق',
    noRetentionOpportunities: 'لا توجد فرص احتفاظ تطابق هذه الفئة.',
    prepareDraft: 'إعداد مسودة',
    prepareRecoveryDraft: 'إعداد مسودة استرداد',
    prepareCancelledSlotOutreach: 'إعداد عرض للموعد المتاح',
    prepareRetentionDraft: 'إعداد مسودة احتفاظ',
    recoveryDraftReviewHint: 'ينشئ هذا الإجراء مسودة استرداد خاضعة للحوكمة. لا يرسل رسالة ولا ينشئ موعدًا.',
    cancelledSlotOutreachDescription: 'إعداد مسودة خاضعة للحوكمة لعرض موعد أبكر أصبح متاحًا على هذا المريض. تظل الموافقة وحالة الموعد وضوابط الإرسال مطبقة؛ لا يتم إرسال أي رسالة.',
    effectiveCommunicationPolicy: 'سياسة التواصل الفعالة بالذكاء الاصطناعي',
    staffApprovalRequired: 'تتطلب موافقة الموظفين',
    automaticCommunicationAllowed: 'التواصل التلقائي بالذكاء الاصطناعي مسموح',
    draftPrepared: 'تم إعداد المسودة',
    draftRequiresStaffApproval: 'تتطلب هذه المسودة موافقة صريحة من الموظفين قبل أن تصبح مؤهلة للإرسال.',
    draftPolicyAuthorized: 'هذه المسودة مصرح بها وفق السياسة للتعامل التلقائي مستقبلًا؛ ولم يتم تسجيل موافقة بشرية.',
    draftChannelStatus: 'القناة: {channel} · اللغة: {locale} · الحالة: {status}',
    openCommunicationReview: 'فتح مراجعة التواصل',
    retentionDraftReviewHint: 'ينشئ هذا الإجراء مسودة استدعاء أو إعادة تنشيط للمراجعة البشرية فقط. لا يرسل رسالة.',
    performanceTitle: 'أداء النمو',
    performanceSubtitle: 'نتائج موثّقة وفرص تحتاج إلى متابعة.', performanceSectionTitle: 'نتائج النمو', performanceSectionDescription: 'نتائج موثّقة وفرص تحتاج إلى متابعة، من دون خلط المبالغ المالية بأحجام العمل التشغيلية.',
    performanceEmpty: 'لا توجد نتائج موثّقة بعد للفترة المحددة.',
    performanceRecoveredValue: 'القيمة المستردة الموثّقة', performanceValueUnavailable: 'القيمة غير متاحة', performanceFilteredValueHint: 'إجمالي موثّق لكل الوقت؛ غير مفلتر حسب الفترة.', performanceVerifiedValueHint: 'المواعيد المرتبطة فقط والتي لها قيمة موثّقة.',
    performanceRecoveredBookings: 'الحجوزات المستردة', performanceBookingsHint: 'حجوزات مرتبطة بفرص الاسترداد.', performanceOpen: 'فرص تحتاج إلى إجراء', performanceOpenHint: 'تم تحديدها أو تم التواصل بشأنها.', performanceClosed: 'فرص مغلقة', performanceClosedHint: 'مكتملة أو منتهية أو مؤرشفة.', performanceRecoveryRate: 'معدل نتائج الاسترداد', performanceRecoveryRateHint: '{recovered} نتيجة مستردة من أصل {total} فرصة استرداد.',
    performanceDisclosure: 'القيم المالية المعروضة تمثل نتائج استرداد موثّقة فقط. الفرص المفتوحة أحجام تشغيلية وليست إيرادًا محققًا.', performanceChartAria: 'مخطط أداء نمو موحّد', performanceEyebrow: 'الأداء', performanceChartTitle: 'مسار واحد لسير العمل المختار', performanceChartDescription: 'يعرض المخطط مراحل سير عمل «{workflow}». تبقى البيانات المالية منفصلة وتظهر فقط عند توثيقها.', performanceSelectWorkflow: 'اختر سير عمل النمو',
    performanceDetected: 'الفرص المحددة', performanceToManage: 'تحتاج إلى إجراء', performanceFunnelContacted: 'تم التواصل', performanceBooked: 'تم الحجز', performanceCompleted: 'مكتملة', performanceVerifiedRecoveredValue: 'القيمة المستردة الموثّقة', performanceNoVerifiedValue: 'لا توجد قيمة مستردة موثّقة بعد.', performanceCurrencyScales: 'الإجماليات منفصلة حسب العملة ولا تُفلتر حسب الفترة.', performanceWorkflowDisclosure: 'تُفلتر المراحل بحسب سير العمل، وليست توقعًا للإيرادات. لا تظهر النتيجة إلا عند تسجيلها في المسار.', performanceOpenAction: 'عرض الفرص التي تحتاج إلى إجراء',
    type_review_request: 'طلبات التقييم',
    performanceDescription: 'عرض موجز لتقدم فرص الإيرادات ضمن الفترة المحددة.',
    performanceOpportunities: 'الفرص',
    performanceContacted: 'حالات الاسترداد التي تم التواصل معها',
    performanceAttributedBooked: 'تم استردادها / حجزها',
    performanceBreakdown: 'تفصيل الفرص',
    performanceDismissedResolved: 'مغلقة / محلولة',
    performanceNeedsAttention: 'تحتاج اهتمامًا',
    performanceOverdueFollowUps: 'متابعات متأخرة',
    performanceNoFollowUp: 'دون متابعة محددة',
    performanceRetentionDue: 'حالات احتفاظ مستحقة',
    openRecovery: 'فتح الاسترداد',
    playbookGuidance: 'إرشاد خطة المتابعة',
    playbookTitle: 'خطة استرداد الموعد',
    playbookStep: 'الخطوة {{step}}',
    playbookTiming: 'التوقيت',
    playbookAction_initial_contact: 'إجراء الاتصال الأول',
    playbookAction_follow_up: 'متابعة المريض',
    playbookAction_confirm_booking: 'تأكيد الحجز',
    playbookAction_review: 'مراجعة الاسترداد',
    playbookAction_no_action: 'لا يلزم إجراء إضافي',
    playbookStatus_not_started: 'لم تبدأ',
    playbookStatus_due: 'مستحقة الآن',
    playbookStatus_overdue: 'متأخرة',
    playbookStatus_scheduled: 'مجدولة',
    playbookStatus_completed: 'مكتملة',
    playbookTiming_today: 'اليوم',
    playbookTiming_schedule_short_follow_up: 'حدّد متابعة قصيرة',
    playbookTiming_scheduled: 'استخدم المتابعة المجدولة',
    playbookTiming_none: 'لا يلزم توقيت',
    playbookReason_no_contact_attempt: 'لا توجد محاولة اتصال مسجلة بعد.',
    playbookReason_no_answer: 'لم يرد المريض على آخر محاولة.',
    playbookReason_patient_will_rebook: 'أكد أن المريض أكمل الحجز.',
    playbookReason_contacted: 'واصل متابعة الاسترداد.',
    playbookReason_terminal_outcome: 'اكتمل هذا الاسترداد أو أُغلق.',
    playbookReason_workflow_review: 'راجع مسار الاسترداد قبل المتابعة.',
    addActivity: 'إضافة نشاط',
    saveWorkflow: 'حفظ سير العمل',
    workflowSaved: 'تم حفظ سير العمل',
    activityCreated: 'تمت إضافة النشاط',
    appointmentLinked: 'تم ربط الموعد',
    patientReference: 'المريض رقم {id}',
    reason_no_show_recovery: 'تغيب مريض عن موعد مجدول.',
    reason_cancelled_recovery: 'تم إلغاء موعد.',
    reason_reactivation: 'قد يستفيد هذا المريض من متابعة.',
    reason_cancelled_slot_recovery: 'أدى إلغاء موعد مستقبلي إلى إتاحة موعد للمراجعة من قبل الموظفين.',
    reason_recall: 'حان موعد تذكير بالنظافة.',
  },
  kb: {
    title: 'قاعدة المعرفة',
    createNew: 'عنصر معرفة جديد',
    category: 'الفئة',
    content: 'المحتوى',
    status: 'الحالة',
    version: 'الإصدار',
    updated: 'آخر تحديث',
    statusDraft: 'مسودة',
    statusPendingReview: 'قيد المراجعة',
    statusApproved: 'معتمد',
    statusArchived: 'مؤرشف',
    loadError: 'تعذر تحميل قاعدة المعرفة',
    empty: 'قاعدة المعرفة جاهزة عندما تكون مستعدًا',
    emptyMessage: 'أضف معلومات عيادتك المعتمدة ليساعد فريقك على تقديم إجابات متسقة ومفيدة.',
    edit: 'تعديل عنصر المعرفة',
    submit: 'إرسال للمراجعة',
    approve: 'اعتماد',
    reject: 'رفض',
    archive: 'أرشفة',
  },
  pmsImport: {
    title: 'استيراد PMS بإشراف',
    subtitle: 'أنشئ خط أساس تشغيليًا بإشراف من صيغة CSV v1 الموثقة. ابدأ بالمعاينة؛ لا يُكتب أي سجل حتى التأكيد.',
    format: 'الصيغة المقبولة: dentai_concierge_pms_csv_v1',
    selectFile: 'اختر ملف CSV',
    preview: 'معاينة وتجربة',
    previewing: 'جارٍ إعداد المعاينة…',
    confirm: 'تأكيد الاستيراد',
    confirming: 'جارٍ الاستيراد…',
    noFile: 'اختر ملف CSV قبل المعاينة.',
    sourceNote: 'يُستخدم الملف المصدر في الذاكرة فقط. لا يحتفظ DentAI بملف CSV خام أو معرّفات المصدر.',
    previewTitle: 'نتيجة التجربة',
    rows: 'صفوف',
    errors: 'أخطاء التحقق',
    warnings: 'تنبيهات التكرار والربط',
    canConfirm: 'هذا الملف صالح. سيكتب التأكيد السجلات المخططة بشكل ذري.',
    cannotConfirm: 'صحح الملف وأعد المعاينة. لم تتم كتابة أي بيانات.',
    clinic: 'إعدادات العيادة',
    patients: 'المرضى',
    providers: 'المختصون',
    appointmentTypes: 'أنواع المواعيد',
    appointments: 'المواعيد',
    created: 'جديدة',
    linkedExisting: 'مرتبطة بسجلات قائمة',
    alreadyImported: 'مستوردة سابقًا',
    updated: 'محدثة',
    revenueBaseline: 'المواعيد الملغاة وحالات عدم الحضور متاحة لمسح الإيرادات الحالي',
    resultTitle: 'اكتمل الاستيراد',
    idempotent: 'اكتمل هذا المصدر سابقًا؛ لم يتم إنشاء سجلات مكررة.',
    job: 'مهمة الاستيراد',
    baselineTitle: 'لقطة خط أساس التجربة',
    baselineCapturedAt: 'تم الالتقاط',
    totalPatients: 'المرضى النشطون',
    activeProviders: 'المختصون النشطون',
    totalAppointments: 'المواعيد التشغيلية المستوردة',
    recoveryEligible: 'مواعيد عدم الحضور/الإلغاء',
    activationTitle: 'تفعيل التجربة',
    activationReady: 'تم التحقق من خط الأساس. فعّل التجربة الخاضعة للإشراف عندما تصبح العيادة جاهزة تشغيليًا.',
    activationActive: 'التجربة نشطة انطلاقًا من خط الأساس هذا.',
    activationRequired: 'أكد التفعيل لوضع علامة جاهزية على العيادة للتجربة الخاضعة للإشراف. لا يؤدي ذلك إلى تفعيل المراسلة أو الذكاء الاصطناعي أو الفوترة أو أي provider خارجي.',
    activate: 'تفعيل التجربة',
    activating: 'جارٍ التفعيل…',
    noBaseline: 'أكمل استيرادًا صالحًا قبل تفعيل التجربة.',
  },
  careJourneys: {
    title: 'مسارات الرعاية الإدارية',
    subtitle: 'تتبّع التقدم الإداري المعتمد من العيادة. لا يشخّص DentAI ولا يوصي بالعلاج ولا يستبدل سجل PMS السريري.',
    newJourney: 'مسار رعاية جديد',
    patient: 'المريض',
    summary: 'ملخص إداري',
    value: 'القيمة الإدارية',
    followUp: 'موعد المتابعة',
    revenueOpportunity: 'معرّف فرصة الإيراد (اختياري)',
    initialItems: 'عناصر إدارية أولية (واحد لكل سطر)',
    create: 'إنشاء مسار رعاية',
    creating: 'جارٍ الإنشاء…',
    metrics: 'مقاييس المالك',
    acceptedValue: 'القيمة الإدارية المقبولة',
    completedValue: 'القيمة الإدارية المكتملة',
    dueFollowUps: 'متابعات مستحقة',
    plannedAppointments: 'مواعيد مخططة',
    completedAppointments: 'مواعيد مكتملة',
    noJourneys: 'لا توجد مسارات رعاية بعد.',
    items: 'عناصر إدارية',
    appointments: 'مواعيد مرتبطة',
    addItem: 'إضافة عنصر',
    linkAppointment: 'ربط موعد',
    status: 'الحالة',
    updateStatus: 'تحديث الحالة',
    selectPatient: 'اختر مريضًا',
    selectAppointment: 'اختر موعدًا لهذا المريض',
    noAppointments: 'لا توجد مواعيد مرتبطة.',
    created: 'تم إنشاء مسار الرعاية.',
    loadError: 'تعذر تحميل مسارات الرعاية.',
    noClinical: 'إداري فقط — لا تدخل تشخيصًا أو توصية أو وصفة أو سجلًا سريريًا.',
  },
  aiCopilot: {
    title: 'مساعد للموظفين فقط',
    subtitle: 'يستخدم معرفة العيادة المعتمدة والمحادثة الحالية لإعداد مسودة قابلة للمراجعة. لا يرسل رسائل ولا يحجز مواعيد.',
    purpose: 'غرض التواصل',
    channel: 'قناة المسودة',
    locale: 'لغة المسودة',
    variables: 'متغيرات القالب المعتمد (JSON)',
    staffNote: 'تعليمات اختيارية من الموظف',
    prepare: 'إعداد مسودة بإشراف',
    preparing: 'جارٍ الإعداد…',
    resultTitle: 'المسودة جاهزة للمراجعة',
    reviewRequired: 'مسودة مدعومة بالذكاء الاصطناعي — المراجعة مطلوبة قبل أي إجراء أو تواصل.',
    provider: 'مزود الذكاء الاصطناعي',
    providerLuna: 'Luna',
    providerLocal: 'مزود محلي حتمي',
    noDraft: 'لم يتم إعداد مسودة بعد.',
  },
  legal: {
    privacyTitle: 'سياسة الخصوصية',
    termsTitle: 'شروط الخدمة',
    lastUpdated: 'آخر تحديث',
    lastUpdatedDate: '[يتم تحديده بواسطة الفريق القانوني]',
    placeholderNotice: 'محتوى افتراضي',
    placeholderNoticeText: 'تحتوي هذه الصفحة على نص افتراضي. يجب أن يكون المحتوى القانوني من قبل أختصاصي قانوني مؤهل.',
    privacyIntro: 'مقدمة',
    privacySection1Title: 'مسؤول البيانات',
    privacySection1Body: '[محتوى قانوني للتقديم.]',
    privacySection2Title: 'البيانات التي نجمعها',
    privacySection2Body: '[محتوى قانوني للتقديم.]',
    privacySection3Title: 'الأساس القانوني',
    privacySection3Body: '[محتوى قانوني للتقديم.]',
    privacySection4Title: 'حقوق البيانات',
    privacySection4Body: '[محتوى قانوني للتقديم.]',
    privacySection5Title: 'الاحتفاظ بالبيانات',
    privacySection5Body: '[محتوى قانوني للتقديم.]',
    privacySection6Title: 'مشاركة البيانات',
    privacySection6Body: '[محتوى قانوني للتقديم.]',
    privacyContactTitle: 'تواصل معنا',
    privacyContactBody: '[محتوى قانوني للتقديم.]',
    termsIntro: 'مقدمة',
    termsSection1Title: 'قبول الشروط',
    termsSection1Body: '[محتوى قانوني للتقديم.]',
    termsSection2Title: 'وصف الخدمة',
    termsSection2Body: '[محتوى قانوني للتقديم.]',
    termsSection3Title: 'الالتزامات',
    termsSection3Body: '[محتوى قانوني للتقديم.]',
    termsSection4Title: 'حد المسؤوليية',
    termsSection4Body: '[محتوى قانوني للتقديم.]',
    termsSection5Title: 'الانهاء',
    termsSection5Body: '[محبوى قانوني للتقديم.]',
    termsSection6Title: 'القانون الاختصاصي',
    termsSection6Body: '[محتوى قانوني للتقديم.]',
    termsContactTitle: 'تواصل معنا',
    termsContactBody: '[محتوى قانوني للتقديم.]',
    backToHome: 'العودة إلى الرئيسية',
    agreePrefix: 'قرأت وأوافق على ال',
    agreeAnd: 'و',
    privacyLinkText: 'الاستراده والخصوصية',
    termsLinkText: 'شروط الخدمة',
  },
};

Object.assign(en, {
  clinicHours: { title: 'Clinic opening hours', description: 'These define when the clinic is open. Provider hours remain separate under Team and providers.', loading: 'Loading hours', loadError: 'Unable to load clinic hours.', open: 'Open', closed: 'Closed', opens: 'Opens', closes: 'Closes', saved: 'Hours saved.', saving: 'Saving…', save: 'Save clinic hours', denied: 'Only authorized managers can change these hours.', manageProviders: 'Manage provider hours →', allDays: 'Configure all seven days of the week.', invalidRange: 'For each open day, closing time must be later than opening time.', days: { sunday: 'Sunday', monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday' } },
  checkoutButton: { unavailable: 'Unable to start checkout. Please try again.', denied: 'Only an authorized administrator can start checkout.', opening: 'Opening secure checkout…' },
  whatsappSetup: { eyebrow: 'Clinic integration', description: 'Connect your clinic’s WhatsApp Business number to DentAI. Your clinic keeps ownership of its WhatsApp Business assets; DentAI receives only the permissions required to provide the service. You can disconnect at any time.', disconnected: 'Disconnected', pending: 'Connection in progress', connected: 'Connected', error: 'Connection needs attention', loading: 'Loading WhatsApp connection status', pendingBody: 'WhatsApp connection is in progress. It will remain inactive until the server confirms it.', errorBody: 'The WhatsApp connection needs attention. No credentials or provider details are displayed.', businessIdentity: 'Business identity', phoneIdentity: 'Phone identity', connectionDate: 'Connection date', pendingConfirmation: 'WhatsApp connection is awaiting server confirmation. Delivery remains disabled.', onboardingFailed: 'WhatsApp onboarding could not be completed. No connection was made.', continueMeta: 'Continue securely in the Meta window to connect this clinic’s WhatsApp number.', unavailable: 'WhatsApp onboarding is currently unavailable. No connection was made.', disconnectedSuccess: 'The WhatsApp connection has been disconnected.', changeFailed: 'The connection could not be changed. Please try again later.', statusUnavailable: 'Connection status is temporarily unavailable.', preparing: 'Preparing secure connection…', confirming: 'Confirming connection…', reconnect: 'Reconnect WhatsApp', connect: 'Connect WhatsApp', disconnect: 'Disconnect', metaUnavailable: 'Secure clinic onboarding will be available after the Meta connection is configured.', denied: 'Only a clinic administrator can manage this connection.', activateSandbox: 'Activate Meta Sandbox', sandboxConnected: 'Meta Sandbox connected for this clinic. Delivery remains disabled.' },
});
Object.assign(it, {
  clinicHours: { title: 'Orari di apertura dello studio', description: 'Definiscono quando lo studio è aperto. Gli orari dei singoli professionisti restano separati in Team e professionisti.', loading: 'Caricamento orari', loadError: 'Impossibile caricare gli orari dello studio.', open: 'Aperto', closed: 'Chiuso', opens: 'Apertura', closes: 'Chiusura', saved: 'Orari salvati.', saving: 'Salvataggio…', save: 'Salva orari studio', denied: 'Solo i responsabili autorizzati possono modificare questi orari.', manageProviders: 'Gestisci gli orari dei professionisti →', allDays: 'Configura tutti i sette giorni della settimana.', invalidRange: 'Per ogni giorno aperto, l’orario di chiusura deve essere successivo a quello di apertura.', days: { sunday: 'Domenica', monday: 'Lunedì', tuesday: 'Martedì', wednesday: 'Mercoledì', thursday: 'Giovedì', friday: 'Venerdì', saturday: 'Sabato' } },
  checkoutButton: { unavailable: 'Impossibile avviare il checkout. Riprova.', denied: 'Solo un amministratore autorizzato può avviare il checkout.', opening: 'Apertura checkout sicuro…' },
  whatsappSetup: { eyebrow: 'Integrazione dello studio', description: 'Collega il numero WhatsApp Business dello studio a DentAI. Lo studio mantiene la proprietà delle risorse WhatsApp Business; DentAI riceve solo i permessi necessari per fornire il servizio. Puoi scollegarlo in qualsiasi momento.', disconnected: 'Non collegato', pending: 'Collegamento in corso', connected: 'Collegato', error: 'Collegamento da verificare', loading: 'Caricamento stato WhatsApp', pendingBody: 'Il collegamento WhatsApp è in corso. Resterà inattivo finché il server non lo conferma.', errorBody: 'Il collegamento WhatsApp richiede attenzione. Credenziali e dettagli del provider non vengono mostrati.', businessIdentity: 'Identità aziendale', phoneIdentity: 'Identità telefonica', connectionDate: 'Data di collegamento', pendingConfirmation: 'Il collegamento WhatsApp attende la conferma del server. Gli invii restano disattivati.', onboardingFailed: 'Non è stato possibile completare il collegamento WhatsApp. Nessun collegamento è stato creato.', continueMeta: 'Continua in modo sicuro nella finestra Meta per collegare il numero WhatsApp dello studio.', unavailable: 'Il collegamento WhatsApp non è disponibile. Nessun collegamento è stato creato.', disconnectedSuccess: 'Il collegamento WhatsApp è stato disconnesso.', changeFailed: 'Non è stato possibile modificare il collegamento. Riprova più tardi.', statusUnavailable: 'Lo stato del collegamento non è temporaneamente disponibile.', preparing: 'Preparazione collegamento sicuro…', confirming: 'Conferma collegamento…', reconnect: 'Ricollega WhatsApp', connect: 'Collega WhatsApp', disconnect: 'Scollega', metaUnavailable: 'Il collegamento sicuro sarà disponibile dopo la configurazione Meta.', denied: 'Solo un amministratore dello studio può gestire questo collegamento.', activateSandbox: 'Attiva Meta Sandbox', sandboxConnected: 'Meta Sandbox collegato per questo studio. Gli invii restano disattivati.' },
});
Object.assign(ar, {
  clinicHours: { title: 'ساعات عمل العيادة', description: 'تحدد أوقات فتح العيادة. تبقى ساعات مقدمي الخدمة منفصلة ضمن الفريق ومقدمي الخدمة.', loading: 'جارٍ تحميل الساعات', loadError: 'تعذر تحميل ساعات العيادة.', open: 'مفتوح', closed: 'مغلق', opens: 'يفتح', closes: 'يغلق', saved: 'تم حفظ الساعات.', saving: 'جارٍ الحفظ…', save: 'حفظ ساعات العيادة', denied: 'يمكن للمديرين المخولين فقط تعديل هذه الساعات.', manageProviders: 'إدارة ساعات مقدمي الخدمة ←', allDays: 'اضبط أيام الأسبوع السبعة.', invalidRange: 'يجب أن يكون وقت الإغلاق بعد وقت الفتح في كل يوم مفتوح.', days: { sunday: 'الأحد', monday: 'الاثنين', tuesday: 'الثلاثاء', wednesday: 'الأربعاء', thursday: 'الخميس', friday: 'الجمعة', saturday: 'السبت' } },
  checkoutButton: { unavailable: 'تعذر بدء الدفع. حاول مرة أخرى.', denied: 'يمكن لمسؤول مخول فقط بدء الدفع.', opening: 'جارٍ فتح صفحة الدفع الآمنة…' },
  whatsappSetup: { eyebrow: 'تكامل العيادة', description: 'اربط رقم WhatsApp Business الخاص بعيادتك مع DentAI. تحتفظ العيادة بملكية أصول WhatsApp Business، ولا يحصل DentAI إلا على الأذونات اللازمة لتقديم الخدمة. يمكنك قطع الاتصال في أي وقت.', disconnected: 'غير متصل', pending: 'جارٍ الاتصال', connected: 'متصل', error: 'الاتصال يحتاج إلى مراجعة', loading: 'جارٍ تحميل حالة اتصال WhatsApp', pendingBody: 'اتصال WhatsApp قيد التنفيذ وسيبقى غير نشط حتى يؤكده الخادم.', errorBody: 'اتصال WhatsApp يحتاج إلى مراجعة. لا تُعرض بيانات الاعتماد أو تفاصيل المزود.', businessIdentity: 'هوية النشاط التجاري', phoneIdentity: 'هوية الهاتف', connectionDate: 'تاريخ الاتصال', pendingConfirmation: 'اتصال WhatsApp بانتظار تأكيد الخادم. يظل الإرسال معطلاً.', onboardingFailed: 'تعذر إكمال إعداد WhatsApp. لم يتم إنشاء اتصال.', continueMeta: 'تابع بأمان في نافذة Meta لربط رقم WhatsApp الخاص بالعيادة.', unavailable: 'إعداد WhatsApp غير متاح حاليًا. لم يتم إنشاء اتصال.', disconnectedSuccess: 'تم قطع اتصال WhatsApp.', changeFailed: 'تعذر تغيير الاتصال. حاول مرة أخرى لاحقًا.', statusUnavailable: 'حالة الاتصال غير متاحة مؤقتًا.', preparing: 'جارٍ إعداد الاتصال الآمن…', confirming: 'جارٍ تأكيد الاتصال…', reconnect: 'إعادة ربط WhatsApp', connect: 'ربط WhatsApp', disconnect: 'قطع الاتصال', metaUnavailable: 'سيصبح إعداد العيادة الآمن متاحًا بعد تهيئة اتصال Meta.', denied: 'يمكن لمسؤول العيادة فقط إدارة هذا الاتصال.', activateSandbox: 'تفعيل Meta Sandbox', sandboxConnected: 'تم ربط Meta Sandbox بهذه العيادة. يظل الإرسال معطلاً.' },
});

Object.assign(en, { billingSettings: { plan: 'Plan', title: 'Subscription and payments', loading: 'Loading plan', unavailable: 'Plan status is unavailable.', trial: 'Professional trial', day: 'day remaining', days: 'days remaining', ends: 'Ends on {date}.', trialConversion: 'Conversion requires a separate checkout and never happens automatically.', stripeTrial: 'Professional — Trial period', monthly: 'monthly', annual: 'annual', active: 'Active', activeUntil: 'Active until {date}', currentPeriod: 'the end of the current period', nextRenewal: 'Next renewal: {date}', pastDue: 'Professional — payment required', pastDueBody: 'Professional features are temporarily unavailable. Your clinic data remains stored.', verifying: 'Payment verification in progress', verifyingBody: 'The plan becomes active only after server confirmation.', refresh: 'Refresh status', free: 'Free plan', trialEnded: 'The Professional trial has ended. ', freeBody: 'You can continue using features included in the free plan.', continue: 'Continue with Professional', upgrade: 'Upgrade to Professional', currentOffer: 'Current offer', annualSaving: professionalAnnualSavingLabel('en'), chooseMonthly: 'Choose monthly', chooseAnnual: 'Choose annual', openingPortal: 'Opening…', settlePayment: 'Resolve payment', manage: 'Manage payment and subscription', manageDenied: 'Only an authorized administrator can manage payments and subscriptions.', confirmCancel: 'Confirm cancellation at the end of the period?', availableUntil: 'Professional remains available until {date}.', paidPeriod: 'the end of the paid period', scheduling: 'Scheduling…', confirm: 'Confirm cancellation', keep: 'Keep subscription', cancel: 'Cancel at period end', portalError: 'The payment portal is unavailable. Please try again later.', cancelError: 'Cancellation could not be scheduled. Please try again.', cancelScheduled: 'Cancellation scheduled. The updated status was requested from the server.' } });
Object.assign(it, { billingSettings: { plan: 'Piano', title: 'Abbonamento e pagamenti', loading: 'Caricamento piano', unavailable: 'Stato del piano non disponibile.', trial: 'Prova Professional', day: 'giorno rimanente', days: 'giorni rimanenti', ends: 'Termina il {date}.', trialConversion: 'La conversione richiede un checkout separato e non avviene automaticamente.', stripeTrial: 'Professional — Periodo di prova', monthly: 'mensile', annual: 'annuale', active: 'Attivo', activeUntil: 'Attivo fino al {date}', currentPeriod: 'termine del periodo corrente', nextRenewal: 'Prossimo rinnovo: {date}', pastDue: 'Professional — pagamento da regolarizzare', pastDueBody: 'Le funzionalità Professional sono temporaneamente non disponibili. I dati dello studio restano conservati.', verifying: 'Verifica del pagamento in corso', verifyingBody: 'Il piano diventa attivo solo dopo la conferma del server.', refresh: 'Aggiorna stato', free: 'Piano Gratuito', trialEnded: 'La prova Professional è terminata. ', freeBody: 'Puoi continuare a usare le funzionalità incluse nel piano gratuito.', continue: 'Continua con Professional', upgrade: 'Passa a Professional', currentOffer: 'Offerta attuale', annualSaving: professionalAnnualSavingLabel('it'), chooseMonthly: 'Scegli mensile', chooseAnnual: 'Scegli annuale', openingPortal: 'Apertura in corso…', settlePayment: 'Regolarizza il pagamento', manage: 'Gestisci pagamento e abbonamento', manageDenied: 'Solo un amministratore autorizzato può gestire pagamenti e abbonamento.', confirmCancel: 'Confermi l’annullamento al termine del periodo?', availableUntil: 'Professional resterà disponibile fino al {date}.', paidPeriod: 'termine del periodo già pagato', scheduling: 'Programmazione…', confirm: 'Conferma annullamento', keep: 'Mantieni abbonamento', cancel: 'Annulla al termine del periodo', portalError: 'Il portale di pagamento non è disponibile. Riprova più tardi.', cancelError: 'Non è stato possibile programmare l’annullamento. Riprova.', cancelScheduled: 'Annullamento programmato. Lo stato aggiornato è stato richiesto al server.' } });
Object.assign(ar, { billingSettings: { plan: 'الخطة', title: 'الاشتراك والمدفوعات', loading: 'جارٍ تحميل الخطة', unavailable: 'حالة الخطة غير متاحة.', trial: 'تجربة Professional', day: 'يوم متبقٍ', days: 'أيام متبقية', ends: 'تنتهي في {date}.', trialConversion: 'يتطلب التحويل عملية دفع منفصلة ولا يحدث تلقائيًا.', stripeTrial: 'Professional — فترة تجريبية', monthly: 'شهري', annual: 'سنوي', active: 'نشط', activeUntil: 'نشط حتى {date}', currentPeriod: 'نهاية الفترة الحالية', nextRenewal: 'التجديد التالي: {date}', pastDue: 'Professional — يلزم تسوية الدفع', pastDueBody: 'ميزات Professional غير متاحة مؤقتًا. تبقى بيانات العيادة محفوظة.', verifying: 'جارٍ التحقق من الدفع', verifyingBody: 'لا تصبح الخطة نشطة إلا بعد تأكيد الخادم.', refresh: 'تحديث الحالة', free: 'الخطة المجانية', trialEnded: 'انتهت تجربة Professional. ', freeBody: 'يمكنك متابعة استخدام الميزات المشمولة في الخطة المجانية.', continue: 'المتابعة مع Professional', upgrade: 'الترقية إلى Professional', currentOffer: 'العرض الحالي', annualSaving: professionalAnnualSavingLabel('ar'), chooseMonthly: 'اختر الشهري', chooseAnnual: 'اختر السنوي', openingPortal: 'جارٍ الفتح…', settlePayment: 'تسوية الدفع', manage: 'إدارة الدفع والاشتراك', manageDenied: 'يمكن لمسؤول مخول فقط إدارة المدفوعات والاشتراك.', confirmCancel: 'تأكيد الإلغاء في نهاية الفترة؟', availableUntil: 'يبقى Professional متاحًا حتى {date}.', paidPeriod: 'نهاية الفترة المدفوعة', scheduling: 'جارٍ الجدولة…', confirm: 'تأكيد الإلغاء', keep: 'الاحتفاظ بالاشتراك', cancel: 'الإلغاء في نهاية الفترة', portalError: 'بوابة الدفع غير متاحة. حاول مرة أخرى لاحقًا.', cancelError: 'تعذرت جدولة الإلغاء. حاول مرة أخرى.', cancelScheduled: 'تمت جدولة الإلغاء وطلب الحالة المحدثة من الخادم.' } });

Object.assign(en, { onboarding: { loading: 'Preparing guided setup', steps: { profile: 'Clinic profile', hours: 'Clinic hours', providers: 'Providers', communications: 'Communications', growth: 'Growth', finish: 'Finish' }, ready: 'Ready', later: 'Later', incomplete: 'To complete', denied: 'Your role can view status but cannot change this configuration.', eyebrow: 'Guided setup', title: 'Let’s prepare your clinic', subtitle: 'Six steps to configure available features. You can review everything in Settings.', progress: 'Onboarding progress', stepOf: 'Step {step} of {total}', essentials: '{count}/3 essentials ready', step: 'Step {step}', optionalStep: 'Step {step} · optional', profileTitle: 'Clinic profile', profileBody: 'Check the name and timezone using the same data shown in Settings.', clinic: 'Clinic', timezone: 'Timezone', notConfigured: 'Not configured', editSettings: 'Edit in Settings', hoursTitle: 'Clinic opening hours', hoursBody: 'These show when the clinic is open. Provider schedules remain separate.', providersTitle: 'Providers and working hours', providersBody: 'Add at least one provider and define availability separately from clinic hours.', activeProviders: '{count} active providers', providersReady: 'Every provider has at least one configured schedule.', providersMissing: 'Add a provider or complete missing schedules.', manageProviders: 'Manage providers and hours', communicationsTitle: 'WhatsApp communications', communicationsBody: 'WhatsApp connection can be activated when configuration is available. Review the AI communication policy below.', currentPolicy: 'Current policy', automaticPolicy: 'Automatic communication is allowed by the existing configuration.', approvalPolicy: 'Staff approval is required before communication.', channelsOptional: 'WhatsApp is not active yet. You can finish clinic setup and connect it when configuration is available.', reviewAi: 'Review AI policy', reviewCommunications: 'Review communications', growthTitle: 'Growth and automations', growthBody: 'Overview based on the existing plan and configuration. It does not enable patient actions.', freePlan: 'Free plan', freePlanBody: 'Professional features remain locked and can be considered later.', operationalSettings: 'Open operational settings', summaryTitle: 'Setup summary', completeBody: 'Essential items are ready. Optional integrations can be completed later.', incompleteBody: 'You completed {count} of 3 essential items. You can enter the dashboard and continue later.', intentMonthly: `Professional monthly — ${professionalPricing('monthly', 'en').label}`, intentAnnual: `Professional annual — ${professionalPricing('annual', 'en').label}`, intentBody: 'This registration choice was preserved. Stripe checkout is separate and the plan activates only after server verification.', checkout: 'Continue to secure checkout', dashboard: 'Open dashboard', previous: 'Previous', next: 'Next', finish: 'Finish setup' } });
Object.assign(it, { onboarding: { loading: 'Preparazione configurazione guidata', steps: { profile: 'Profilo studio', hours: 'Orari studio', providers: 'Professionisti', communications: 'Comunicazioni', growth: 'Crescita', finish: 'Fine' }, ready: 'Pronto', later: 'Più tardi', incomplete: 'Da completare', denied: 'Il tuo ruolo può consultare lo stato, ma non modificare questa configurazione.', eyebrow: 'Configurazione guidata', title: 'Prepariamo il tuo studio', subtitle: 'Sei passaggi per configurare le funzioni già disponibili. Potrai rivedere tutto in Impostazioni.', progress: 'Avanzamento onboarding', stepOf: 'Passaggio {step} di {total}', essentials: '{count}/3 essenziali pronti', step: 'Passaggio {step}', optionalStep: 'Passaggio {step} · facoltativo', profileTitle: 'Profilo dello studio', profileBody: 'Verifica nome e fuso orario usando gli stessi dati di Impostazioni.', clinic: 'Studio', timezone: 'Fuso orario', notConfigured: 'Da configurare', editSettings: 'Modifica in Impostazioni', hoursTitle: 'Orari di apertura dello studio', hoursBody: 'Indicano quando lo studio è aperto. Gli orari dei professionisti sono separati.', providersTitle: 'Professionisti e orari di lavoro', providersBody: 'Aggiungi almeno un professionista e definisci la sua disponibilità, distinta dagli orari dello studio.', activeProviders: '{count} professionisti attivi', providersReady: 'Ogni professionista ha almeno un orario configurato.', providersMissing: 'Aggiungi un professionista o completa gli orari mancanti.', manageProviders: 'Gestisci professionisti e orari', communicationsTitle: 'Comunicazioni WhatsApp', communicationsBody: 'Il collegamento WhatsApp potrà essere attivato quando la configurazione sarà disponibile. Rivedi qui la politica per le comunicazioni assistite da AI.', currentPolicy: 'Politica attuale', automaticPolicy: 'Comunicazione automatica consentita dalla configurazione esistente.', approvalPolicy: 'Approvazione del personale richiesta prima della comunicazione.', channelsOptional: 'WhatsApp non è ancora attivo. Puoi completare la configurazione dello studio e collegarlo quando sarà disponibile.', reviewAi: 'Rivedi politica AI', reviewCommunications: 'Rivedi comunicazioni', growthTitle: 'Crescita e automazioni', growthBody: 'Panoramica basata sul piano e sulla configurazione esistenti. Non abilita azioni sui pazienti.', freePlan: 'Piano Free', freePlanBody: 'Le funzioni Professional restano bloccate e possono essere valutate più avanti.', operationalSettings: 'Apri impostazioni operative', summaryTitle: 'Configurazione riepilogata', completeBody: 'Gli elementi essenziali sono pronti. Le integrazioni facoltative possono essere completate in seguito.', incompleteBody: 'Hai completato {count} di 3 elementi essenziali. Puoi entrare nella dashboard e continuare più tardi.', intentMonthly: `Professional mensile — ${professionalPricing('monthly', 'it').label}`, intentAnnual: `Professional annuale — ${professionalPricing('annual', 'it').label}`, intentBody: 'Hai conservato questa scelta dalla registrazione. Il checkout Stripe è separato e il piano diventa attivo solo dopo la verifica server.', checkout: 'Continua al checkout sicuro', dashboard: 'Apri dashboard', previous: 'Indietro', next: 'Avanti', finish: 'Termina configurazione' } });
Object.assign(ar, { onboarding: { loading: 'جارٍ إعداد التهيئة الموجهة', steps: { profile: 'ملف العيادة', hours: 'ساعات العيادة', providers: 'مقدمو الخدمة', communications: 'التواصل', growth: 'النمو', finish: 'الإنهاء' }, ready: 'جاهز', later: 'لاحقًا', incomplete: 'يحتاج إلى إكمال', denied: 'يمكن لدورك عرض الحالة، لكنه لا يسمح بتعديل هذا الإعداد.', eyebrow: 'الإعداد الموجّه', title: 'لنجهز عيادتك', subtitle: 'ست خطوات لتهيئة الميزات المتاحة. يمكنك مراجعة كل شيء في الإعدادات.', progress: 'تقدم الإعداد', stepOf: 'الخطوة {step} من {total}', essentials: '{count}/3 عناصر أساسية جاهزة', step: 'الخطوة {step}', optionalStep: 'الخطوة {step} · اختيارية', profileTitle: 'ملف العيادة', profileBody: 'تحقق من الاسم والمنطقة الزمنية باستخدام البيانات نفسها في الإعدادات.', clinic: 'العيادة', timezone: 'المنطقة الزمنية', notConfigured: 'غير مضبوط', editSettings: 'تعديل في الإعدادات', hoursTitle: 'ساعات عمل العيادة', hoursBody: 'توضح أوقات فتح العيادة. تبقى جداول مقدمي الخدمة منفصلة.', providersTitle: 'مقدمو الخدمة وساعات العمل', providersBody: 'أضف مقدم خدمة واحدًا على الأقل وحدد توفره بصورة منفصلة عن ساعات العيادة.', activeProviders: '{count} من مقدمي الخدمة النشطين', providersReady: 'لدى كل مقدم خدمة جدول واحد مضبوط على الأقل.', providersMissing: 'أضف مقدم خدمة أو أكمل الجداول الناقصة.', manageProviders: 'إدارة مقدمي الخدمة والساعات', communicationsTitle: 'تواصل واتساب', communicationsBody: 'يمكن تفعيل اتصال واتساب عندما تتوفر التهيئة. راجع هنا سياسة التواصل بمساعدة الذكاء الاصطناعي.', currentPolicy: 'السياسة الحالية', automaticPolicy: 'تسمح التهيئة الحالية بالتواصل التلقائي.', approvalPolicy: 'تلزم موافقة الموظفين قبل التواصل.', channelsOptional: 'واتساب غير نشط بعد. يمكنك إكمال إعداد العيادة وربطه عندما تصبح التهيئة متاحة.', reviewAi: 'مراجعة سياسة الذكاء الاصطناعي', reviewCommunications: 'مراجعة التواصل', growthTitle: 'النمو والأتمتة', growthBody: 'نظرة عامة تستند إلى الخطة والتهيئة الحاليتين ولا تفعل إجراءات على المرضى.', freePlan: 'الخطة المجانية', freePlanBody: 'تبقى ميزات Professional مقفلة ويمكن تقييمها لاحقًا.', operationalSettings: 'فتح الإعدادات التشغيلية', summaryTitle: 'ملخص الإعداد', completeBody: 'العناصر الأساسية جاهزة. يمكن إكمال التكاملات الاختيارية لاحقًا.', incompleteBody: 'أكملت {count} من 3 عناصر أساسية. يمكنك دخول لوحة التحكم والمتابعة لاحقًا.', intentMonthly: `Professional شهري — ${professionalPricing('monthly', 'ar').label}`, intentAnnual: `Professional سنوي — ${professionalPricing('annual', 'ar').label}`, intentBody: 'تم الاحتفاظ باختيار التسجيل. عملية Stripe منفصلة ولا تنشط الخطة إلا بعد تحقق الخادم.', checkout: 'المتابعة إلى الدفع الآمن', dashboard: 'فتح لوحة التحكم', previous: 'السابق', next: 'التالي', finish: 'إنهاء الإعداد' } });

Object.assign(en.onboarding as TranslationBundle, { cancellationRecovery: 'Cancellation recovery', noShowRecovery: 'No-show recovery', recall: 'Recall and hygiene', reactivation: 'Patient reactivation', reviewRequests: 'Review requests', slotRecovery: 'Cancelled-slot recovery', automations: 'Automations', luna: 'Luna', professionalNoSend: 'Available with Professional; no delivery is enabled here.', professionalRules: 'Available with Professional; existing rules apply.', configuredInterval: 'Interval configured.', configureType: 'Configure an eligible appointment type.', configuredThreshold: 'Threshold configured.', configureThreshold: 'Set the threshold in Settings.', configured: 'Configuration present.', destinationRequired: 'A real destination is required before use.', professionalAvailable: 'Available with Professional.', automationControlled: 'They remain controlled and are not enabled here.', availableByPlan: 'Available according to plan and permissions.', professionalRequired: 'Professional access required.', providerHours: 'Providers and hours', enterDashboard: 'Enter dashboard', continueSettings: 'Continue in Settings', readOnly: 'Setup is read-only for your role.' });
Object.assign(it.onboarding as TranslationBundle, { cancellationRecovery: 'Recupero cancellazioni', noShowRecovery: 'Recupero no-show', recall: 'Richiami e igiene', reactivation: 'Riattivazione pazienti', reviewRequests: 'Richieste recensione', slotRecovery: 'Recupero slot cancellati', automations: 'Automazioni', luna: 'Luna', professionalNoSend: 'Disponibile con Professional; nessun invio viene attivato qui.', professionalRules: 'Disponibile con Professional; valgono le regole esistenti.', configuredInterval: 'Intervallo configurato.', configureType: 'Configura un tipo appuntamento idoneo.', configuredThreshold: 'Soglia configurata.', configureThreshold: 'Definisci la soglia nelle impostazioni.', configured: 'Configurazione presente.', destinationRequired: 'Serve una destinazione reale prima dell’uso.', professionalAvailable: 'Disponibile con Professional.', automationControlled: 'Restano controllate e non vengono abilitate qui.', availableByPlan: 'Disponibile secondo piano e permessi.', professionalRequired: 'Richiede accesso Professional.', providerHours: 'Professionisti e orari', enterDashboard: 'Entra nella dashboard', continueSettings: 'Continua in Impostazioni', readOnly: 'La configurazione è in sola lettura per il tuo ruolo.' });
Object.assign(ar.onboarding as TranslationBundle, { cancellationRecovery: 'استعادة الإلغاءات', noShowRecovery: 'استعادة حالات عدم الحضور', recall: 'الاستدعاء والنظافة', reactivation: 'إعادة تنشيط المرضى', reviewRequests: 'طلبات التقييم', slotRecovery: 'استعادة المواعيد الملغاة', automations: 'الأتمتة', luna: 'Luna', professionalNoSend: 'متاح مع Professional؛ لا يتم تفعيل إرسال هنا.', professionalRules: 'متاح مع Professional؛ تطبق القواعد الحالية.', configuredInterval: 'تم ضبط الفترة.', configureType: 'اضبط نوع موعد مؤهلًا.', configuredThreshold: 'تم ضبط الحد.', configureThreshold: 'حدد الحد في الإعدادات.', configured: 'التهيئة موجودة.', destinationRequired: 'تلزم وجهة حقيقية قبل الاستخدام.', professionalAvailable: 'متاح مع Professional.', automationControlled: 'تبقى خاضعة للتحكم ولا يتم تفعيلها هنا.', availableByPlan: 'متاح وفق الخطة والصلاحيات.', professionalRequired: 'يتطلب وصول Professional.', providerHours: 'مقدمو الخدمة والساعات', enterDashboard: 'دخول لوحة التحكم', continueSettings: 'المتابعة في الإعدادات', readOnly: 'الإعداد للقراءة فقط وفق دورك.' });

Object.assign(en.analytics as TranslationBundle, { eyebrow: 'Operational data', reportTitle: 'Reports', subtitle: 'A clear view of data already available to the clinic.', sections: 'Report sections', overview: 'Overview', appointments: 'Appointments', growth: 'Growth', communications: 'Communications', growthPerformance: 'Growth performance', growthBody: 'Recovery and conversion metrics are available in the Growth Performance view.', growthAction: 'Open Growth Performance', today: 'today' });
Object.assign(it.analytics as TranslationBundle, { eyebrow: 'Dati operativi', reportTitle: 'Report', subtitle: 'Una lettura semplice dei dati già disponibili per lo studio.', sections: 'Sezioni report', overview: 'Panoramica', appointments: 'Appuntamenti', growth: 'Crescita', communications: 'Comunicazioni', growthPerformance: 'Performance Crescita', growthBody: 'Le metriche di recupero e conversione sono disponibili nella vista Performance di Crescita.', growthAction: 'Apri Performance Crescita', today: 'oggi' });
Object.assign(ar.analytics as TranslationBundle, { eyebrow: 'البيانات التشغيلية', reportTitle: 'التقارير', subtitle: 'عرض واضح للبيانات المتاحة بالفعل للعيادة.', sections: 'أقسام التقارير', overview: 'نظرة عامة', appointments: 'المواعيد', growth: 'النمو', communications: 'التواصل', growthPerformance: 'أداء النمو', growthBody: 'تتوفر مقاييس الاستعادة والتحويل في عرض أداء النمو.', growthAction: 'فتح أداء النمو', today: 'اليوم' });

Object.assign(en.appointmentType as TranslationBundle, { recall: 'Recall', recallAfter: 'Remind patients to return after', days: 'Days', weeks: 'Weeks', months: 'Months', recallHint: 'DentAI can identify patients who are due to return based on this interval.' });
Object.assign(it.appointmentType as TranslationBundle, { recall: 'Richiamo', recallAfter: 'Ricorda ai pazienti di tornare dopo', days: 'Giorni', weeks: 'Settimane', months: 'Mesi', recallHint: 'DentAI può identificare i pazienti che devono tornare in base a questo intervallo.' });
Object.assign(ar.appointmentType as TranslationBundle, { recall: 'الاستدعاء', recallAfter: 'ذكّر المرضى بالعودة بعد', days: 'أيام', weeks: 'أسابيع', months: 'أشهر', recallHint: 'يمكن لـ DentAI تحديد المرضى الذين حان موعد عودتهم وفق هذه الفترة.' });

Object.assign(en, { staffInvite: { invalidLink: 'This invitation link is invalid or incomplete.', passwordLength: 'Choose a password with at least 12 characters.', passwordMismatch: 'The passwords do not match.', nameRequired: 'Enter your name to create your provider profile.', acceptError: 'This invitation is invalid, expired, or could not be accepted.', eyebrow: 'Team access', title: 'Accept your invitation', subtitle: 'Set a secure password to activate your DentAI access. The invitation link is single-use and expires after seven days.', success: 'Your access has been activated. You can now sign in to DentAI.', fullName: 'Full name', password: 'Password', confirmPassword: 'Confirm password', providerChoice: 'I am accepting a provider invitation and need to create my provider profile.', providerType: 'Provider type', dentist: 'Dentist', hygienist: 'Hygienist', activate: 'Activate access', login: 'Return to sign in' } });
Object.assign(it, { staffInvite: { invalidLink: 'Il link di invito non è valido o è incompleto.', passwordLength: 'Scegli una password di almeno 12 caratteri.', passwordMismatch: 'Le password non coincidono.', nameRequired: 'Inserisci il tuo nome per creare il profilo professionista.', acceptError: 'L’invito non è valido, è scaduto oppure non può essere accettato.', eyebrow: 'Accesso al team', title: 'Accetta il tuo invito', subtitle: 'Imposta una password sicura per attivare l’accesso a DentAI. Il link è monouso e scade dopo sette giorni.', success: 'Il tuo accesso è stato attivato. Ora puoi accedere a DentAI.', fullName: 'Nome completo', password: 'Password', confirmPassword: 'Conferma password', providerChoice: 'Sto accettando un invito come professionista e devo creare il relativo profilo.', providerType: 'Tipo di professionista', dentist: 'Dentista', hygienist: 'Igienista', activate: 'Attiva accesso', login: 'Torna al login' } });
Object.assign(ar, { staffInvite: { invalidLink: 'رابط الدعوة غير صالح أو غير مكتمل.', passwordLength: 'اختر كلمة مرور من 12 حرفًا على الأقل.', passwordMismatch: 'كلمتا المرور غير متطابقتين.', nameRequired: 'أدخل اسمك لإنشاء ملف مقدم الخدمة.', acceptError: 'الدعوة غير صالحة أو منتهية أو تعذر قبولها.', eyebrow: 'وصول الفريق', title: 'قبول دعوتك', subtitle: 'عيّن كلمة مرور آمنة لتفعيل وصولك إلى DentAI. الرابط أحادي الاستخدام وينتهي بعد سبعة أيام.', success: 'تم تفعيل وصولك. يمكنك الآن تسجيل الدخول إلى DentAI.', fullName: 'الاسم الكامل', password: 'كلمة المرور', confirmPassword: 'تأكيد كلمة المرور', providerChoice: 'أقبل دعوة مقدم خدمة وأحتاج إلى إنشاء ملفي المهني.', providerType: 'نوع مقدم الخدمة', dentist: 'طبيب أسنان', hygienist: 'أخصائي صحة أسنان', activate: 'تفعيل الوصول', login: 'العودة إلى تسجيل الدخول' } });

Object.assign(en, { clinicEmail: { title: 'DentAI communication email', description: 'This managed address identifies your clinic in patient email and routes replies to Patient Inbox.', current: 'Current address', status: 'Status', notConfigured: 'Not configured', pending: 'Pending external activation', active: 'Active', localPart: 'Address name', create: 'Create address', update: 'Change address', readOnly: 'Only authorized clinic administrators can change this address.', domainUnavailable: 'The managed email domain has not been configured on the server.', history: 'Previous addresses remain reserved and continue routing replies to this clinic.', error: 'The address could not be saved.' } });
Object.assign(it, { clinicEmail: { title: 'Email comunicazioni DentAI', description: 'Questo indirizzo gestito identifica lo studio nelle email ai pazienti e inoltra le risposte alla Posta pazienti.', current: 'Indirizzo attuale', status: 'Stato', notConfigured: 'Non configurato', pending: 'In attesa di attivazione esterna', active: 'Attivo', localPart: 'Nome indirizzo', create: 'Crea indirizzo', update: 'Modifica indirizzo', readOnly: 'Solo gli amministratori autorizzati dello studio possono modificare questo indirizzo.', domainUnavailable: 'Il dominio email gestito non è configurato sul server.', history: 'Gli indirizzi precedenti restano riservati e continuano a inoltrare le risposte a questo studio.', error: 'Impossibile salvare l’indirizzo.' } });
Object.assign(ar, { clinicEmail: { title: 'بريد التواصل المُدار من DentAI', description: 'يحدد هذا العنوان المُدار عيادتك في رسائل المرضى ويوجه الردود إلى صندوق وارد المرضى.', current: 'العنوان الحالي', status: 'الحالة', notConfigured: 'غير مضبوط', pending: 'بانتظار التفعيل الخارجي', active: 'نشط', localPart: 'اسم العنوان', create: 'إنشاء العنوان', update: 'تغيير العنوان', readOnly: 'يمكن لمسؤولي العيادة المصرح لهم فقط تغيير هذا العنوان.', domainUnavailable: 'نطاق البريد المُدار غير مضبوط على الخادم.', history: 'تبقى العناوين السابقة محجوزة وتواصل توجيه الردود إلى هذه العيادة.', error: 'تعذر حفظ العنوان.' } });

Object.assign(en.settings as TranslationBundle, { reviewDestinationRequired: 'Add a real clinic review destination before enabling review requests.' });
Object.assign(it.settings as TranslationBundle, { reviewDestinationRequired: 'Aggiungi una destinazione reale per le recensioni dello studio prima di abilitare le richieste.' });
Object.assign(ar.settings as TranslationBundle, { reviewDestinationRequired: 'أضف وجهة حقيقية لتقييمات العيادة قبل تفعيل طلبات التقييم.' });

Object.assign(it, { inbox: {"workflowHelp":"Scegli una conversazione, leggi i messaggi e prepara una risposta sicura.","chooseConversation":"Scegli una conversazione","chooseHelp":"Seleziona un paziente dall’elenco per aprire la conversazione.","search":"Cerca paziente o messaggio","noSearchResults":"Nessuna conversazione corrisponde alla ricerca.","emptyHelp":"Le nuove conversazioni compariranno qui.","selectConversationHelp":"Scegli un paziente dall’elenco per leggere i messaggi e rispondere.","selectedConversation":"Conversazione selezionata","fromPatient":"Dal paziente","fromClinic":"Dalla clinica","replyArea":"Rispondi","replyGovernanceEmail":"La risposta viene salvata come bozza e richiede approvazione prima dell’invio.","replyGovernance":"Invia una risposta nella conversazione selezionata secondo i permessi disponibili.","channel_email":"Email","channel_whatsapp":"WhatsApp","channel_internal":"Interno","channel_webchat":"Chat web","channel_sms":"SMS","channel_instagram":"Instagram","channel_facebook":"Facebook","channelReadOnly":"Canale non operativo: consultazione dello storico soltanto.","title":"Posta pazienti","unmatched":"Mittente non associato a un paziente","senderUnknown":"Mittente non disponibile","subject":"Conversazione paziente","unread":"Nuovo messaggio","read":"Letto","unreadOnly":"Solo non letti","received":"Ricevuto","inbound":"Ricevuto","outbound":"In uscita","patient":"Apri scheda paziente","empty":"Nessun messaggio ricevuto","settings":"Configura email della clinica","notConfigured":"Email della clinica non configurata. La ricezione sulla casella gestita non è ancora disponibile.","pending":"Indirizzo creato. Attivazione in attesa di verifica.","active":"Email della clinica attiva","unavailable":"Stato email non disponibile. Riprova più tardi.","guidance":"Usa da 3 a 63 lettere, numeri o trattini interni. Alcuni nomi di sistema sono riservati.","invalid":"Indirizzo non valido o riservato. Scegli un altro nome.","conflict":"Indirizzo non disponibile. Scegli un altro nome.","reserved":"Questo nome è riservato. Scegli un altro nome.","saved":"Indirizzo riservato. Attivazione in attesa di verifica.","saveError":"Impossibile riservare l’indirizzo. Riprova più tardi.","domainMissing":"La configurazione email richiede l’intervento dell’amministratore.","createDraft":"Salva risposta in bozza","draftSaved":"Bozza salvata. Non ancora inviato.","replyHelp":"La risposta richiede una bozza e l’approvazione prima dell’invio.","replyUnavailable":"Associa il mittente tramite un processo autorizzato prima di rispondere.","review":"Rivedi e approva la risposta","sendApproved":"Approva e invia","sent":"Inviato · Accettato dal provider","failed":"Invio non riuscito. Controlla Comunicazioni.","notConfiguredStatus":"Non configurata","pendingStatus":"In attesa di attivazione","activeStatus":"Attiva","related":"Contesto della comunicazione"} });
Object.assign(en, { inbox: {"workflowHelp":"Choose a conversation, read the messages, and prepare a safe reply.","chooseConversation":"Choose a conversation","chooseHelp":"Select a patient from the list to open the conversation.","search":"Search patient or message","noSearchResults":"No conversations match your search.","emptyHelp":"New conversations will appear here.","selectConversationHelp":"Choose a patient from the list to read messages and reply.","selectedConversation":"Selected conversation","fromPatient":"From patient","fromClinic":"From clinic","replyArea":"Reply","replyGovernanceEmail":"Your reply is saved as a draft and requires approval before sending.","replyGovernance":"Send a reply in the selected conversation according to your permissions.","channel_email":"Email","channel_whatsapp":"WhatsApp","channel_internal":"Internal","channel_webchat":"Web chat","channel_sms":"SMS","channel_instagram":"Instagram","channel_facebook":"Facebook","channelReadOnly":"Channel unavailable: history is read-only.","title":"Patient Inbox","unmatched":"Sender not associated with a patient","senderUnknown":"Sender unavailable","subject":"Patient conversation","unread":"New message","read":"Read","unreadOnly":"Unread only","received":"Received","inbound":"Received","outbound":"Outgoing","patient":"Open patient record","empty":"No messages received","settings":"Configure clinic email","notConfigured":"Clinic email is not configured. Managed email receiving is not available yet.","pending":"Address created. Activation awaits verification.","active":"Clinic email active","unavailable":"Email status unavailable. Try again later.","guidance":"Use 3–63 letters, numbers or internal hyphens. System names are reserved.","invalid":"Invalid or reserved address. Choose another name.","conflict":"Address unavailable. Choose another name.","reserved":"This name is reserved. Choose another name.","saved":"Address reserved. Activation awaits verification.","saveError":"Unable to reserve the address. Try again later.","domainMissing":"Email setup requires administrator assistance.","createDraft":"Save reply draft","draftSaved":"Draft saved. Not sent yet.","replyHelp":"Replies require a draft and approval before sending.","replyUnavailable":"An authorized patient association is required before replying.","review":"Review and approve reply","sendApproved":"Approve and send","sent":"Sent · Accepted by provider","failed":"Delivery failed. Check Communications.","notConfiguredStatus":"Not configured","pendingStatus":"Pending activation","activeStatus":"Active","related":"Communication context"} });
Object.assign(ar, { inbox: {"workflowHelp":"اختر محادثة واقرأ الرسائل وجهّز رداً آمناً.","chooseConversation":"اختر محادثة","chooseHelp":"اختر مريضاً من القائمة لفتح المحادثة.","search":"البحث عن مريض أو رسالة","noSearchResults":"لا توجد محادثات تطابق البحث.","emptyHelp":"ستظهر المحادثات الجديدة هنا.","selectConversationHelp":"اختر مريضاً من القائمة لقراءة الرسائل والرد.","selectedConversation":"المحادثة المحددة","fromPatient":"من المريض","fromClinic":"من العيادة","replyArea":"الرد","replyGovernanceEmail":"يُحفظ الرد كمسودة ويتطلب الموافقة قبل الإرسال.","replyGovernance":"أرسل رداً في المحادثة المحددة وفقاً لصلاحياتك.","channel_email":"البريد الإلكتروني","channel_whatsapp":"واتساب","channel_internal":"داخلي","channel_webchat":"دردشة الويب","channel_sms":"رسالة نصية","channel_instagram":"إنستغرام","channel_facebook":"فيسبوك","channelReadOnly":"القناة غير متاحة: السجل للقراءة فقط.","title":"صندوق الوارد","unmatched":"المرسل غير مرتبط بمريض","senderUnknown":"المرسل غير متاح","subject":"محادثة المريض","unread":"رسالة جديدة","read":"مقروءة","unreadOnly":"غير المقروء فقط","received":"تم الاستلام","inbound":"وارد","outbound":"صادر","patient":"فتح ملف المريض","empty":"لم يتم استلام رسائل","settings":"إعداد بريد العيادة","notConfigured":"بريد العيادة غير مضبوط. استقبال البريد المُدار غير متاح بعد.","pending":"تم إنشاء العنوان. التفعيل بانتظار التحقق.","active":"بريد العيادة نشط","unavailable":"حالة البريد غير متاحة. حاول لاحقًا.","guidance":"استخدم من 3 إلى 63 حرفًا لاتينيًا أو رقمًا أو شرطة داخلية. أسماء النظام محجوزة.","invalid":"العنوان غير صالح أو محجوز. اختر اسمًا آخر.","conflict":"العنوان غير متاح. اختر اسمًا آخر.","reserved":"هذا الاسم محجوز. اختر اسمًا آخر.","saved":"تم حجز العنوان. التفعيل بانتظار التحقق.","saveError":"تعذر حجز العنوان. حاول لاحقًا.","domainMissing":"يتطلب إعداد البريد مساعدة المسؤول.","createDraft":"حفظ مسودة الرد","draftSaved":"تم حفظ المسودة. لم يتم الإرسال بعد.","replyHelp":"تتطلب الردود مسودة وموافقة قبل الإرسال.","replyUnavailable":"يلزم ربط المرسل بمريض عبر إجراء مصرح به قبل الرد.","review":"مراجعة الرد والموافقة عليه","sendApproved":"الموافقة والإرسال","sent":"تم الإرسال · قبله المزود","failed":"فشل الإرسال. راجع الاتصالات.","notConfiguredStatus":"غير مضبوط","pendingStatus":"بانتظار التفعيل","activeStatus":"نشط","related":"سياق التواصل"} });

Object.assign(it, { revenueProof: {"history":"Storico","outcomes":"Risultati verificati","recoveredValue":"Valore recuperato verificato","recovered":"Appuntamenti recuperati","reactivated":"Pazienti tornati in visita","slots":"Slot cancellati riempiti","open":"Opportunità aperte","proof":"Tutto il periodo · Prenotazioni confermate o completate collegate. Pazienti tornati: visita completata.","priority":"Azioni prioritarie","manage":"Gestisci","setup":"Completa la configurazione","variables":"Variabili del modello (JSON)","variablesHelp":"Usa solo variabili consentite dal modello approvato. Lascia {} se non previste.","unavailable":"Dati non disponibili","timezoneUnknown":"Fuso della clinica non disponibile: orari in UTC"} });
Object.assign(en, { revenueProof: {"history":"History","outcomes":"Verified outcomes","recoveredValue":"Verified recovered value","recovered":"Appointments recovered","reactivated":"Patients returned for a visit","slots":"Cancelled slots filled","open":"Open opportunities","proof":"All time · Linked confirmed or completed bookings. Returned patients: completed visit.","priority":"Priority actions","manage":"Manage","setup":"Complete setup","variables":"Template variables (JSON)","variablesHelp":"Use only variables allowed by the approved template. Leave {} if none.","unavailable":"Data unavailable","timezoneUnknown":"Clinic timezone unavailable: times shown in UTC"} });
Object.assign(ar, { revenueProof: {"history":"السجل","outcomes":"نتائج موثقة","recoveredValue":"القيمة المستردة الموثقة","recovered":"مواعيد تم استعادتها","reactivated":"مرضى عادوا للزيارة","slots":"فترات ملغاة تم حجزها","open":"فرص مفتوحة","proof":"كل الفترات · حجوزات مرتبطة مؤكدة أو مكتملة. عودة المرضى: زيارة مكتملة.","priority":"إجراءات ذات أولوية","manage":"إدارة","setup":"إكمال الإعداد","variables":"متغيرات القالب (JSON)","variablesHelp":"استخدم المتغيرات المسموح بها فقط. اترك {} إذا لم توجد.","unavailable":"البيانات غير متاحة","timezoneUnknown":"المنطقة الزمنية للعيادة غير متاحة: الأوقات بتوقيت UTC"} });
Object.assign(it.revenueProof as Record<string, string>, { noVerifiedValue: 'Nessun valore monetario verificato disponibile.', funnel: 'Percorso di recupero', opportunities: 'Opportunità', contacted: 'Contattate', booked: 'Prenotate', completed: 'Completate' });
Object.assign(en.revenueProof as Record<string, string>, { noVerifiedValue: 'No verified monetary value is available yet.', funnel: 'Recovery funnel', opportunities: 'Opportunities', contacted: 'Contacted', booked: 'Booked', completed: 'Completed' });
Object.assign(ar.revenueProof as Record<string, string>, { noVerifiedValue: 'لا توجد قيمة مالية موثقة بعد.', funnel: 'مسار الاستعادة', opportunities: 'الفرص', contacted: 'تم التواصل', booked: 'تم الحجز', completed: 'مكتملة' });

Object.assign(en.nav as TranslationBundle, { patientInbox: 'Patient Inbox' });
Object.assign(it.nav as TranslationBundle, { patientInbox: 'Inbox pazienti' });
Object.assign(ar.nav as TranslationBundle, { patientInbox: 'صندوق رسائل المرضى' });
Object.assign(en.clinicEmail as TranslationBundle, {
  notConfiguredMeaning: 'No managed address has been reserved. Inbound replies are not operational.',
  reservedMeaning: 'The address is reserved for this clinic. Inbound replies remain unavailable until external activation is complete.',
  activeMeaning: 'The address is active and inbound replies can be routed to Patient Inbox.',
  senderBehavior: 'Outgoing email uses the verified DentAI sender. Once active, this clinic address is used as Reply-To.',
  replyLocation: 'Patient replies appear in Patient Inbox after provider activation and successful patient matching.',
});
Object.assign(it.clinicEmail as TranslationBundle, {
  notConfiguredMeaning: 'Nessun indirizzo gestito è stato riservato. Le risposte in ingresso non sono operative.',
  reservedMeaning: 'L’indirizzo è riservato allo studio. Le risposte in ingresso restano non disponibili fino al completamento dell’attivazione esterna.',
  activeMeaning: 'L’indirizzo è attivo e le risposte in ingresso possono essere instradate a Inbox pazienti.',
  senderBehavior: 'Le email in uscita usano il mittente DentAI verificato. Dopo l’attivazione, questo indirizzo dello studio viene usato come Reply-To.',
  replyLocation: 'Le risposte dei pazienti compaiono in Inbox pazienti dopo l’attivazione del provider e l’associazione corretta del paziente.',
});
Object.assign(ar.clinicEmail as TranslationBundle, {
  notConfiguredMeaning: 'لم يتم حجز عنوان مُدار. الردود الواردة غير متاحة.',
  reservedMeaning: 'العنوان محجوز للعيادة. تبقى الردود الواردة غير متاحة حتى اكتمال التفعيل الخارجي.',
  activeMeaning: 'العنوان نشط ويمكن توجيه الردود الواردة إلى صندوق رسائل المرضى.',
  senderBehavior: 'تستخدم الرسائل الصادرة مرسل DentAI الموثق. بعد التفعيل يُستخدم عنوان العيادة كعنوان Reply-To.',
  replyLocation: 'تظهر ردود المرضى في صندوق الرسائل بعد تفعيل المزود وربط المريض بنجاح.',
});

export const translations: Record<Locale, TranslationBundle> = {
  en,
  it,
  ar,
};
